import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PencilIcon,
  PlusIcon,
  SearchIcon,
  SignatureIcon,
  TrashIcon,
} from '@/components/catalog/icons';
import { isRealDate } from '@/components/catalog/form-utils';
import { ImportedFromXeroBadge } from '@/components/documents/ImportedFromXeroBadge';
// The cell, shared with `/invoices` — the same question about the same column on
// two line tables, so it must not be rendered twice.
import { LineProjectStatusCell } from '@/components/documents/LineProjectStatusCell';
import { QuoteFormModal } from '@/components/quotes/QuoteFormModal';
import { ExpiryBadge, QuoteIcon, StageBadge } from '@/components/quotes/QuoteBits';
// The badge for one row, or an honest absence — shared with the University
// Partnerships dashboard, so the two cannot word the same envelope differently.
import { SignatureCell } from '@/components/docusign/SignatureCell';
/*
 * ⚠️ THE SAME COMPONENT the detail page mounts, not a list-sized variant of it.
 *
 * Everything that makes a send safe lives INSIDE it — the sandbox warning
 * (`signaturesNotBinding()`, which warns on a null connection, and the read is
 * admin-only so null is the ordinary case here), the acceptance consequence, the
 * Xero-fetch-or-upload fork, the anchor refusal, and the ConfirmDialog that
 * restates all of it at the moment it becomes irreversible. A second, "quicker"
 * list version would drop exactly those, and none of the omissions would fail a
 * build.
 *
 * ⚠️ It follows that the row action OPENS this and never sends: recipient name
 * and email are typed here and are matched by DocuSign exactly. A one-click send
 * from a list would email a customer with nobody having checked who.
 */
import { SendForSignatureModal } from '@/components/docusign/SendForSignatureModal';
/*
 * ⚠️ **"Change signer and resend" is VOID-AND-RESEND, and this dialog is the
 * screen that says so.** There is no recipient-edit endpoint —
 * `/docusign/envelopes/:id/recipients` is `GET` only — so re-pointing a live
 * envelope means killing it: the original signer is emailed the reason, their
 * link dies, and any partial signature is discarded. Every one of those
 * sentences lives INSIDE the dialog, in front of the irreversible step. The
 * menu item that opens it must not be worded as an edit, and must not restate
 * any of it — a menu is not the place for prose.
 */
import { ChangeSignerDialog } from '@/components/docusign/ChangeSignerDialog';
/*
 * ⚠️ **The reminder is the OPPOSITE operation and it sits one item away.** It
 * leaves the envelope, the customer's live signing link and any partial
 * signature untouched — it asks DocuSign to email the same person again. It is
 * self-contained: it makes its own call, owns its own in-flight guard, its own
 * abort on unmount and its own cooldown countdown. This page passes it an
 * envelope and a gate, nothing else, and adds no second sentence about what a
 * `200` means.
 */
import { RemindEnvelopeControl } from '@/components/docusign/RemindEnvelopeControl';
/*
 * On `embedded` delivery — the default — DocuSign emails nobody, so a send that
 * ends with the dialog closed leaves an envelope the signer has never been told
 * about. Chaining into this is that missing step, and it is the moment to do it:
 * the link is single use and dies in about five minutes.
 */
import { SigningLinkDialog } from '@/components/docusign/SigningLinkDialog';
/*
 * ⚠️ **`XeroRowPushControl`, `XeroPushReportBanner` and the `XeroRowPushReport`
 * type were imported here until 2026-08-31**, for the Xero column removed that
 * day — see the note where that column's `<TH>` was. Nothing on this page pushes
 * to Xero any more, so there is no outcome to report and no banner to raise.
 *
 * ⚠️ The component still exists and is still exported. It is NOT dead by
 * accident: `XeroPushReportBanner` from the same module is imported by
 * `InvoiceFormModal`, which reports the push a SAVE performs. Do not delete
 * `XeroRowPushControl.tsx` on the strength of this page no longer importing it.
 */
import { InvoiceIcon, InvoiceMoney } from '@/components/invoices/InvoiceBits';
import { DownloadPdfButton } from '@/components/documents/DownloadPdfButton';
/*
 * The in-app PDF viewer (owner 2026-10-25): ONE host per page, opened from the
 * row, the phone card and the kanban card at every stage. Preview needs only
 * `quotes.read`; the viewer itself handles a quote that is not in Xero yet, so
 * a kanban card — which carries no Xero link — still offers it.
 */
import {
  PreviewQuotePdfButton,
  useQuotePdfPreview,
  type QuotePdfPreviewTarget,
} from '@/components/quotes/QuotePdfPreview';
import { quotePreviewHref, quoteSignHref } from '@/lib/quote-pdf-preview';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';
/*
 * ⚠️ Convert OPENS THIS rather than firing the request — an invoice is a
 * real financial record and nothing is created until the prefilled form is
 * saved. The modal owns the conversion from there (`POST /convert`, the
 * follow-up PATCH, both 409 branches, the partial-failure report), which is
 * what keeps this page and the detail page from reporting one refusal two
 * ways.
 */
import { InvoiceFormModal } from '@/components/invoices/InvoiceFormModal';
import { ActivityDrawer, ActivityIcon } from '@/components/tasks/ActivityDrawer';
/*
 * ⚠️ `STAGED_CONVERT_REASON` is the SINGLE copy of a sentence a
 * customer-facing refusal shows — a local copy is a second thing to drift
 * from the server that `tsc` cannot see. Read the block above it before
 * touching Convert; `InvoiceFormModal` also reads it, which is why it lives
 * in `@/lib/quote-utils` rather than a page (a component importing a page
 * would be an import cycle).
 */
import { STAGED_CONVERT_REASON, addDaysIso } from '@/lib/quote-utils';
import { todayIsoDate } from '@/lib/invoice-utils';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { documentCustomerName } from '@/lib/company-names';
import { preferencesStore } from '@/lib/preferences-store';
import { useSavedFilterApply, useSavedFilters } from '@/lib/use-saved-filters';
import {
  ONE_ENVELOPE_PER_QUOTE_REASON,
  SIGNATURE_ACCEPTS_QUOTE_NOTE,
  isEmbeddedDelivery,
  type SignerValue,
} from '@/lib/docusign';
/*
 * The one signature request, and the model around it. Shared rather than
 * page-local since 2026-09-03: the University Partnerships dashboard asks the
 * same question, and a second copy of anything deciding whether a document reads
 * as signed is how two screens end up disagreeing about one envelope. Read the
 * block above `OUTSTANDING_PER_PAGE` before touching any of it.
 */
import {
  useOutstandingEnvelopes,
  hasSignatureToShow,
  recordEnvelope,
  forgetEnvelope,
  type SignatureState,
} from '@/lib/outstanding-envelopes';
import { formatDateOnly, formatDateTime, documentDisplayTitle, formatList } from '@/lib/format';
// The two words, the column key and the option list, shared with `/invoices` —
// see that module's header for what "No" does and does not mean.
import {
  IMPORTED_FROM_XERO_COLUMN,
  IMPORTED_FROM_XERO_FILTER_OPTIONS,
  importedFromXeroFormat,
  importedFromXeroLabel,
  importedFromXeroValue,
} from '@/lib/imported-from-xero';
// The four words and the option list, shared with `/invoices` — see that
// module's header for why this question has four answers and not two.
import {
  LINE_PROJECT_FILTER_OPTIONS,
  lineProjectStatusLabel,
} from '@/lib/line-project-status';
import type {
  DocuSignEnvelope,
  Invoice,
  KanbanCard,
  KanbanQueryParams,
  Paginated,
  Quote,
  QuoteListParams,
  QuoteListSort,
} from '@/lib/types';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { denialReason } from '@/lib/permissions';
import { viewerZone } from '@/lib/task-time';
import { useAuth } from '@/lib/use-auth';
import { fetchAllPages, useShowAll } from '@/lib/use-show-all';
/*
 * The Account lead filter's DEFAULT — the membership test, the arrival states
 * and the URL write, in one module shared with `/deals` and `/invoices`. Read
 * its header before changing anything about how this page arrives; in
 * particular, it explains why a link naming ANY filter suppresses the seed.
 */
import { useDefaultAccountLeadFilter } from '@/lib/use-default-account-lead';
import { currencyOptions, useCurrencyOptions } from '@/lib/use-invoice-options';
import { projectOptions, useProjectOptions } from '@/lib/use-project-options';
import { useQuoteStageOptions } from '@/lib/use-quote-options';
/*
 * ⚠️ **Read ONLY as the evidence for the Account lead default** — this page
 * renders no staff picker and no staff funnel, and must not grow one on the
 * strength of this call. `byGroup.account_lead` answers "may this person be
 * named as an account lead", which is the question the default turns on.
 */
import { useUserOptions } from '@/lib/use-user-options';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ViewToggle as SharedViewToggle } from '@/components/ui/ViewToggle';
import {
  Badge,
  Button,
  ColumnManager,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterSheet,
  FilterableTH,
  Input,
  ListPager,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import type {
  ColumnFilterOption,
  ComboboxOption,
  FilterableTHProps,
  MultiComboboxOption,
} from '@/components/ui';

/** `perPage` on /quotes is 1..100 and defaults to 20. */
const PER_PAGE = 20;
const SKELETON_ROWS = 6;

/** How far ahead the "expiring" preset looks, in days. */
const EXPIRY_WINDOW_DAYS = 30;

/*
 * ✅ **`GET /quotes` SORTS. Corrected 2026-09-02.**
 *
 * *SUPERSEDED, and it was wrong twice over: "Ordering is fixed at `id DESC`
 * server-side — no sort parameter exists, and unknown query keys are stripped
 * silently, so a sortable header would highlight without the rows moving.
 * Newest first, no sort control at all."*
 *
 * `sort`/`order` shipped on 2026-08-31 and `QuoteListSort` is
 * `subject | issueDate | lineProjectStatus | createdAt | updatedAt | id`
 * (`lineProjectStatus` added 2026-09-02, ordering by a four-step COMPLETENESS
 * RANK rather than alphabetically — `no_lines`, `none`, `some`, `all`, least
 * complete first). And the second half was
 * never true of THIS endpoint at all: the query schema is `.strict()`, so an
 * unknown key is a **422 naming it**, not a silent strip — the failure a
 * mistaken `sortKey` produces here is a dead list, not a dead control.
 *
 * ⚠️ **It rotted unnoticed because it was a NEGATIVE claim**, which is this
 * codebase's documented failure mode: nothing contradicts "there is no sort
 * parameter" when one appears — no build breaks, no request 422s, no row moves
 * — so it has to be re-measured on purpose, and the people who would re-measure
 * are the ones the sentence has just told not to bother.
 *
 * ⚠️ **`id` is accepted and is never SENT.** `sort=id&order=desc` is byte for
 * byte what the endpoint already does unasked, so requesting it buys nothing and
 * leaks a redundant pair into every URL that carries it. The way to ask for the
 * default is to ask for nothing — which is why `isQuoteSort` below lists only
 * the members this page can actually put on a header.
 *
 * The sort itself is `useTableFilters`' — see `QUOTE_COLUMNS`. This page holds
 * no second sort state, so a header and a request cannot disagree about the
 * order the rows are in.
 *
 * Id filters live in the URL so a filtered view survives a refresh and can be
 * linked to (`/quotes?companyId=22`). Every write is `replace: true`, since
 * changing a filter isn't a navigation. `search` stays in local state —
 * debounced per keystroke, not worth a URL write while half-typed.
 */
const ID_FILTERS = [
  'stageId',
  'contactId',
  'companyId',
  'dealId',
  /*
   * ⚠️ **`ownerId` was here and went on 2026-08-31**, with the Owner column and
   * the "All owners" control. `/quotes` still accepts it; this app stopped
   * asking. Nothing ever wrote `owner_id` on a quote — the server puts the
   * acting user in `created_by_id` and leaves owner null — so it matched
   * nothing and was indistinguishable from an empty list. Measured that day:
   * **0 of 34 quotes carry an owner.**
   *
   * `accountLeadId` is the replacement and a DIFFERENT person, not a rename. It
   * matches the RESOLVED lead and is a SINGLE id (comma-separated is
   * companies-only; `?accountLeadId=1,2` is a 422 here). Scopes what is SHOWN
   * only — never access control.
   */
  'accountLeadId',
  /*
   * The saleable run a quote's HEADER names — `d_quotes.project_id`. A real
   * `GET /quotes` filter, arrived at by link from `/projects/:id` rather than by
   * a control here.
   *
   * 🔴 **IT MATCHES NOTHING, AND `lineProjectId` BELOW IS THE ONE A PERSON
   * MEANS BY "quotes on this project".** The measurement, why nothing writes the
   * column, and why it is nonetheless kept are all recorded at
   * `QuoteListParams.projectId` in `types.ts` — **deliberately not repeated
   * here, because a count copied into a second place is a count that goes stale
   * in one of them and stays right in the other.**
   *
   * ⚠️ **So it keeps its "Also filtered by" badge and gains NO control** — badge
   * ⇔ no control, the rule stated at the filter bank below.
   */
  'projectId',
  /*
   * Quotes with AT LEAST ONE LINE ITEM on this project —
   * `f_quote_products.project_id`, and the key the Project picker in the filter
   * bank drives. The contract, the counts and the one-prefix-apart warning live
   * at `QuoteListParams.lineProjectId`; what belongs here is what this PAGE does
   * with it.
   *
   * 🔴 **THE ONE MEMBER OF THIS LIST THAT IS A SET — `?lineProjectId=4,9`, since
   * 2026-09-02.** Several ids mean OR. It is parsed by `parseIdList` and NOT by
   * `parseId`, which reads a CSV as absent; `SINGLE_ID_FILTERS` below is what
   * keeps the two loops from meeting. The filter is `number[]` on `filters` and
   * `[]` when nothing is chosen, so **every test on it is `length`, never
   * `!== null`** — an empty array is truthy, and the idiom copied from the line
   * above would report a filter nobody set.
   *
   * ⚠️ **ONE PREFIX APART FROM `projectId`, AND A DIFFERENT QUESTION** — that one
   * asks whether the HEADER names the project, this whether any LINE does. The
   * two sit four lines apart here and one line apart in `QuoteListParams`, which
   * is why each says which question it answers rather than leaving the prefix to
   * carry it.
   *
   * ⚠️ **Independent of the `lineProjectStatus` funnel, and it ANDs with it.**
   * Both read the same column and ask different things: this is WHICH project,
   * that is HOW MANY of the lines name one at all. `?lineProjectId=7` with the
   * funnel on `some` is "work on project 7 that still has lines missing one".
   *
   * ⚠️ **NO KANBAN BOARD ACCEPTS IT, and unlike `projectId` that is STRUCTURAL
   * rather than an omission**: a board filter is matched against a column ON THE
   * RECORD (`kanban.registry.ts`'s `FilterKind` is id / code / dateFrom / dateTo
   * / ids), and this needs a join to the line table. There is no version of the
   * board registry that could take it without a new filter kind. Withheld in
   * `boardParams` and named in `unappliedOnBoard`.
   */
  'lineProjectId',
] as const;
type IdFilter = (typeof ID_FILTERS)[number];

/**
 * Every id filter EXCEPT `lineProjectId`, which is the one member of the list
 * above that is a **comma-separated set** rather than a single id
 * (`?lineProjectId=4,9`, several ids meaning OR — the contract is at
 * `QuoteListParams.lineProjectId`).
 *
 * ⚠️ **The split exists so `parseId` can never be pointed at the multi key.**
 * `parseId` refuses anything that is not one positive integer, so it reads
 * `"4,9"` as absent — the filter would silently vanish from a shared link with
 * nothing failing, which is the quietest way this change could have gone wrong.
 * Two loops that each know which kind they are walking cannot make that
 * mistake; one loop with a special case inside it can be edited into making it.
 *
 * ⚠️ **`ID_FILTERS` itself is UNCHANGED and is still the URL-key list** —
 * `SAVED_FILTER_KEYS` spreads it, so `lineProjectId` is still saved against the
 * account and still deep-linkable. A CSV round-trips through that machinery as
 * the string it already is; nothing there parses an id.
 */
/*
 * `stageId` joined `lineProjectId` as a set on 2026-10-28 (owner: every filter
 * box takes several values; the backend widened `?stageId=` to a CSV on the list
 * and the board alike). Old single-id bookmarks still parse — one id is a CSV of
 * one.
 */
type MultiIdFilter = 'lineProjectId' | 'stageId';
type SingleIdFilter = Exclude<IdFilter, MultiIdFilter>;
const SINGLE_ID_FILTERS = ID_FILTERS.filter(
  (key): key is SingleIdFilter => key !== 'lineProjectId' && key !== 'stageId',
);

/**
 * What this page remembers against the account.
 *
 * The id filters plus the two `validTill` bounds and the two `issueDate` bounds
 * — every URL-backed filter this page has. ⚠️ **`currency` is NOT here because
 * it is not in the URL on this page**; adding it to this list would save a key
 * nothing reads, which stores cleanly, reads back cleanly and does nothing at
 * all.
 *
 * ⚠️ **The SORT is deliberately absent**, and that is not an oversight to even
 * out with the filters. It lives in `useTableFilters`' own state rather than in
 * the URL, so there is no query key to save; putting it here would store a key
 * `readState` never reads — the exact "saves cleanly, does nothing" trap the
 * sentence above describes for `currency`.
 *
 * ⚠️ **`lineProjectStatus` IS ABSENT FOR THE SAME REASON, and it is the entry
 * somebody will reach for**, because unlike the sort it really is a filter and
 * really does narrow the request. It is still hook state, not a URL key: adding
 * it here would store a value nothing ever reads back, and the row would look
 * saved. Making it savable means putting it in the URL first, which means
 * `useTableFilters` becoming a controlled component — a change to the primitive
 * and to five other pages, not a key added to this list.
 */
const SAVED_FILTER_KEYS = [
  ...ID_FILTERS,
  'validTillAfter',
  'validTillBefore',
  'issueDateAfter',
  'issueDateBefore',
] as const;
/** Merged per key rather than all-or-nothing — see `arrivalDecision`. */
const SAVED_VIEW_KEYS = ['view'] as const;
type SavedQuotesKey = (typeof SAVED_FILTER_KEYS)[number] | (typeof SAVED_VIEW_KEYS)[number];

/**
 * Reads a positive integer out of the query string, ignoring anything else.
 * Blank and malformed values are dropped rather than forwarded: `?stageId=`
 * is a 422 on this endpoint, not "no filter".
 */
function parseId(raw: string | null): number | null {
  if (!raw) return null;
  return /^[1-9]\d*$/.test(raw) ? Number(raw) : null;
}

/**
 * The same guard for the ONE key that carries several ids — `?lineProjectId=4,9`.
 *
 * ⚠️ **A BAD MEMBER IS DROPPED, NOT THE WHOLE VALUE, AND THAT IS A CHOICE.** A
 * hand-edited or truncated URL (`?lineProjectId=4,`) would otherwise take the
 * entire filter with it, so a link that names four projects and one typo would
 * return the WHOLE list — which reads as "these quotes are not on that project"
 * rather than as a malformed link. Dropping the member narrows by what was
 * legible, and the picker then shows exactly which ids survived, so the
 * disagreement is visible in the control rather than hidden in the query string.
 *
 * ⚠️ **`[]` is what "no filter" looks like here, never `''`.** An empty value on
 * this `.strict()` schema is a **422**, not "no filter" — so this returns an
 * empty ARRAY and every caller tests `length`, which cannot be spelled as a
 * falsy string by accident.
 *
 * Duplicates are collapsed and the order the person picked them in is kept: the
 * server ORs them, so order changes nothing on the wire, but a chip list that
 * reshuffles itself as ids are added looks like the control losing track.
 */
function parseIdList(raw: string | null): number[] {
  if (!raw) return [];
  const seen = new Set<number>();
  for (const part of raw.split(',')) {
    const id = parseId(part.trim());
    if (id !== null) seen.add(id);
  }
  return [...seen];
}

/**
 * `currency` is validated as exactly `^[A-Z]{3}$` server-side — "usd" is a 422,
 * not folded to uppercase — so anything else in the URL is treated as absent.
 */
function parseCurrency(raw: string | null): string | null {
  if (!raw) return null;
  return /^[A-Z]{3}$/.test(raw) ? raw : null;
}

/**
 * Every ticked value gets a checkbox, whether or not the option list knows it.
 *
 * ⚠️ **A SELECTED VALUE WITH NO OPTION CANNOT BE UNTICKED.** The column reads as
 * filtered, the table is narrowed, and the popover offers nothing to switch it
 * off — so the only way out is editing the URL. Reachable here whenever the
 * `/quote-stages` master could not be read and the Stage funnel falls back to
 * the values accumulated from the loaded page.
 *
 * `#<value>` rather than a guessed name: the label is not in hand, and this is
 * the same idiom the "Also filtered by" badges already use for an id they
 * cannot name.
 *
 * ⚠️ **A COPY OF `CompaniesPage`'s helper** (`/invoices` carries the third). It
 * is a pure four-line function over `ColumnFilterOption[]` and plainly belongs
 * beside `ColumnFilter` or in `use-table-filters`. **Not moved: both are shared
 * files under edit by other sessions**, and moving a primitive under somebody
 * mid-change is a merge conflict in a file six pages import. Recorded so the
 * next person to touch either does it deliberately rather than discovering the
 * duplication.
 */
function withSelectedValues(
  options: ColumnFilterOption[],
  selected: readonly string[],
): ColumnFilterOption[] {
  const known = new Set(options.map((option) => option.value));
  const missing = selected.filter((value) => !known.has(value));
  return missing.length === 0
    ? options
    : [...options, ...missing.map((value) => ({ value, label: `#${value}` }))];
}

/**
 * The same guard for the FOUR date bounds — two on `validTill`, two on
 * `issueDate` — which are 'YYYY-MM-DD' and must be REAL calendar dates:
 * `?validTillBefore=` is a 422 exactly like the empty id, and `2026-02-31` is a
 * 422 that a shape check alone would wave through. `isRealDate` does both —
 * pattern and calendar — in one call.
 *
 * ⚠️ One function for all four, deliberately: a second copy for the issue-date
 * pair would agree with this one the day it was written and drift on the first
 * correction, and what would drift is whether a pasted URL sends a value the
 * endpoint refuses.
 */
function parseIsoDate(raw: string | null): string | null {
  if (!raw) return null;
  return isRealDate(raw) ? raw : null;
}

/**
 * The sort keys THIS PAGE can put on a header, which is narrower than the
 * contract on purpose.
 *
 * `QuoteListSort` also has `company`, `createdAt`, `updatedAt` and `id`; none of
 * them has a `sortKey` on a column below, so none can reach a request. The guard
 * exists because `useTableFilters` hands back `sort.key` as a plain `string` —
 * without it a mistyped `sortKey` would typecheck and 422 the whole list, since
 * the quotes query schema is `.strict()`.
 *
 * ➕ **`subject` ADDED 2026-09-02**, on the Quote header. *SUPERSEDED: it was
 * named in the sentence above as one of the members no column reaches.*
 *
 * ⚠️ **`company` IS A REAL MEMBER AND IS DELIBERATELY LEFT OUT, and the reason
 * is not the enum.** This table renders no company column, so a `sortKey` for it
 * would have no header to sit on — and a sort with no header is a request the
 * reader can neither see nor reverse. It becomes available the day a Company
 * column is added here, and not before. (It IS used on the Accounts dashboard,
 * which bands by company and does render one.)
 *
 * ⚠️ **`id` is excluded on purpose** — see the block at the top of this file.
 *
 * ➕ **`stage` and `importedFromXero` added 2026-09-02**, with the two header
 * controls below. Both are real `QuoteListSort` members, measured in the backend
 * schema rather than inferred from the frontend type that declares them.
 *
 * 🔴 **THE STAGE KEY IS `stage` HERE AND `status` ON `/invoices`** — two columns
 * on two tables (`d_quotes.stage_id` into `f_quote_stages`,
 * `d_invoices.status_id` into `f_invoice_statuses`), exactly the shape of the
 * existing `issueDate` / `billDate` pair. Both query schemas are `.strict()`, so
 * `?sort=status` here is a **422 naming the unrecognised key** and the list
 * renders as *"you have no quotes"*, not as a bad sort. **Do not "harmonise"
 * it**; the resemblance of two names is the weakest evidence that two things are
 * the same thing.
 *
 * ⚠️ **`importedFromXero` IS the same word on both**, and equally deliberately —
 * one nullable timestamp added to both tables by migration 130, asking one
 * question. Same idea + same column ⇒ same key; same idea + different columns ⇒
 * different keys. The two rules sit one line apart in this array.
 *
 * ⚠️ **Neither new sort is alphabetical, and no header may say it is.** `stage`
 * orders by the reference row's own `sortOrder` — a pipeline order somebody
 * arranged — and `importedFromXero` orders on the BOOLEAN `IS NOT NULL`, not on
 * the import date, so it agrees with the Yes/No the cell renders rather than
 * ranking rows the reader sees as identical.
 *
 * ⚠️ **`stage_id` is nullable and the server coalesces NO sentinel**, so
 * `?sort=stage&order=asc` opens on the quotes with NO stage — MariaDB's
 * NULLs-first, the same contract as `?sort=startDate` on `/projects`. That is
 * the ordering, not a broken sort. `importedFromXero` is the one member here
 * with no NULL question to answer at all: `x IS NOT NULL` yields 0 or 1 and
 * never NULL.
 */
const QUOTE_SORT_KEYS: readonly QuoteListSort[] = [
  'subject',
  'stage',
  'issueDate',
  'lineProjectStatus',
  'importedFromXero',
];
function isQuoteSort(key: string): key is QuoteListSort {
  return (QUOTE_SORT_KEYS as readonly string[]).includes(key);
}


/*
 * The view lives in the URL, so `/quotes?view=table` and `?view=board` are
 * both real, sendable links.
 *
 * TABLE IS THE DEFAULT: most per-record actions (tasks/notes, Edit, Delete)
 * are row actions the board has no slot for — `cardAction` holds only ONE
 * control (Convert). ⚠️ The two views must not disagree about what a record
 * can do — a missing affordance reads as a removed feature (the companies
 * "+" menu was once reported DELETED when it had only gone unreachable
 * behind the board) — so anything added to a row should be asked of the
 * card too. See `cardAction` on `KanbanBoard` below.
 *
 * An absent `view` key means the table; only the exact value `board` asks
 * for the board — a typo or stale value falls back to the table rather than
 * 404ing, and `?view=table` still works for old links.
 *
 * `GET /quotes/kanban` takes every filter this page offers, narrowing COLUMN
 * COUNTS AND TOTALS, not just the cards. ⚠️ The schema is `.strict()` per
 * board — an unknown key (or one from another board) 422s the whole board,
 * so `boardParams` below is built one key at a time rather than spread in
 * wholesale.
 *
 * One mismatch remains: the board's `search` matches only `subject`, while
 * `GET /quotes?search=` also matches `quoteNumber` — so "QU-0527" finds its
 * row but no card. The search field hints at this while the board shows.
 *
 * `cardsPerColumn` is left unset (defaults to 20, capped at 100, board-wide)
 * — there's no per-column paging, so a truncated column links to the
 * filtered table instead of pretending to load more.
 */
type QuotesView = 'table' | 'board';

/**
 * What the quote form was opened WITH. `quote` set means edit; both absent
 * means a plain create. `lockedCompany` carries the NAME as well as the id
 * since the form must never show a blank company field — see `?new=1` below.
 */
type QuoteFormRequest = { quote?: Quote; lockedCompany?: { id: number; name: string } };

/*
 * `?new=1` is CREATE, not FILTER — `?companyId=<id>` filters the list;
 * `?new=1&companyId=<id>` opens the create form with that company locked in.
 *
 * ⚠️ Three things this has to get right: (1) the name is NOT in the URL — a
 * caption anyone could edit into a lie — it's fetched via `GET
 * /companies/:id` and the form opens once it's in hand; (2) a URL is not
 * authorisation — `?new=1` from someone who can't create quotes is stripped,
 * not obeyed; (3) the key is cleared on close (`replace: true`), or a
 * refresh would reopen the form. `?new=1` alone is a valid plain create link.
 */
const NEW_PARAM = 'new';

/**
 * Table / Board, as two buttons in a group rather than a `<select>`.
 * `aria-pressed`, not `role="tab"` — these switch what the panel below is,
 * not label panels sharing one tabstop.
 */
function ViewToggle({
  view,
  onChange,
}: {
  view: QuotesView;
  onChange: (next: QuotesView) => void;
}) {
  return (
    <SharedViewToggle<QuotesView>
      label="Quote view"
      value={view}
      options={[
        { value: 'table', label: 'Table' },
        { value: 'board', label: 'Board' },
      ]}
      onChange={onChange}
    />
  );
}


/*
 * `validTillBefore`/`validTillAfter` are both INCLUSIVE, so the presets below
 * are plain bounds on one nullable column (expired -> validTill <= yesterday;
 * soon -> today <= validTill <= today+30).
 *
 * ⚠️ A quote with `validTill: null` matches NEITHER bound (comparison against
 * NULL is never true), so an open-ended, often high-value quote vanishes
 * under either preset. "Expired" + "Expiring in 30 days" is NOT the whole of
 * "All quotes" — the active preset carries a visible hint saying so; don't
 * relabel these as a tidy segmented control, the labels would lie.
 *
 * Every date is built from LOCAL calendar parts (`todayIsoDate` + `addDaysIso`)
 * — a bound derived from `toISOString()` is off by one east of Greenwich
 * after the local evening.
 */
type ExpiryPreset = 'all' | 'expired' | 'soon' | 'custom';

/** The bounds a preset writes into the URL. `custom` is read-only — see below. */
function boundsFor(preset: Exclude<ExpiryPreset, 'custom'>): {
  validTillBefore: string | null;
  validTillAfter: string | null;
} {
  if (preset === 'expired') {
    // Yesterday, not today: `before` is inclusive, so `today` would call a
    // quote that is valid for the rest of today expired.
    return { validTillBefore: addDaysIso(todayIsoDate(), -1), validTillAfter: null };
  }
  if (preset === 'soon') {
    const today = todayIsoDate();
    return { validTillBefore: addDaysIso(today, EXPIRY_WINDOW_DAYS), validTillAfter: today };
  }
  return { validTillBefore: null, validTillAfter: null };
}

/**
 * Which preset the URL currently describes.
 *
 * Anything else carrying a bound — a hand-edited URL, or a link built yesterday
 * whose "yesterday" has since moved — reads as `custom` rather than being
 * silently rewritten. The filter is still applied and still shown; the control
 * just says so instead of claiming a preset it no longer matches.
 *
 * ⚠️ *SUPERSEDED, the noun: "the Select".* That control became a `Combobox` on
 * 2026-09-13 and `custom` is a pinned option there rather than a conditional
 * `<option>` — see `expiryOptions`. **The reason is unchanged and is the point
 * of this function**: a `Combobox` handed a value no option carries shows its
 * PLACEHOLDER, so without that pin the "still shown" half of the sentence above
 * would have quietly stopped being true.
 */
function presetFor(before: string | null, after: string | null): ExpiryPreset {
  if (before === null && after === null) return 'all';
  const expired = boundsFor('expired');
  if (before === expired.validTillBefore && after === null) return 'expired';
  const soon = boundsFor('soon');
  if (before === soon.validTillBefore && after === soon.validTillAfter) return 'soon';
  return 'custom';
}

/**
 * The issue-date range — `issueDateAfter` / `issueDateBefore`, both INCLUSIVE.
 *
 * A `<fieldset>`/`<legend>` rather than two loose boxes, the same shape as
 * `PeriodRangeFilter` on `/projects` and the closing-date range on `/deals`: it
 * reads as one control answering one question, and a screen reader announces
 * "Issued between, from". The per-input labels stay VISIBLE — a date box with
 * nothing on it cannot say which end of the range it is.
 *
 * ⚠️ NOT presets, unlike the expiry control beside it. "Expired" and "expiring
 * in 30 days" are two questions somebody actually asks of a validity date; there
 * is no equivalent shortlist for "when was this issued", and inventing three
 * would be three labels to keep true rather than one range that is.
 *
 * Both values arrive already validated by `parseIsoDate`, so each is either ''
 * or a real `YYYY-MM-DD` — the inverted test below is a plain string comparison
 * with no `Date` anywhere near it, which is the only kind this codebase allows
 * on a date-only column.
 *
 * ⚠️ The inverted-range message is an ERROR, not advice, and is therefore not
 * the helper text this app forbids. The server accepts a back-to-front pair and
 * simply returns nothing — indistinguishable here from "no quote was issued in
 * that period" — so the bound stays applied, stays in the URL, and the sentence
 * only explains the empty result.
 */
function IssueDateRangeFilter({
  from,
  to,
  onFrom,
  onTo,
}: {
  from: string;
  to: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
}) {
  const inverted = from !== '' && to !== '' && from > to;

  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="mb-1.5 p-0 text-xs font-medium text-fg-secondary">Issued between</legend>
      {/* Wraps onto two lines at 320px, side by side from `sm`. */}
      <div className="flex min-w-0 flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1 basis-36">
          <Field label="From">
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                aria-describedby={describedBy}
                value={from}
                onChange={(e) => onFrom(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="min-w-0 flex-1 basis-36">
          <Field label="To" {...(inverted ? { error: 'Before the “from” date.' } : {})}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                aria-describedby={describedBy}
                invalid={inverted}
                /*
                 * No `min`/`max`: a quote is issued in the past far more often
                 * than the future, and a native bound would make the inverted
                 * pair unreachable rather than explained — a control that
                 * silently refuses a keystroke reads as broken.
                 */
                value={to}
                onChange={(e) => onTo(e.target.value)}
              />
            )}
          </Field>
        </div>
      </div>
    </fieldset>
  );
}

/**
 * SEND FOR SIGNATURE, for one row.
 *
 * ONE component in two layouts rather than two renderings of the idea. ⚠️ *This
 * cited `XeroRowPushControl` as "the same arrangement … on this page" until
 * 2026-08-31, when that control was removed; the rule is unchanged and is now
 * stated from itself.* `variant` changes the alignment and the label's placement
 * and NOTHING else, so
 * the gate, the disabled sentence and the dialog it opens are identical at 320px
 * and at 1440. A second copy is how a card and a cell end up disagreeing about
 * what a row can do.
 *
 * `denial` is computed by the page (`sendSignatureDenial`) and passed in rather
 * than derived here: it needs the ability model and the page-wide signature
 * state, and a component that reached for either would be a second decision
 * point. `null` means the control is live.
 *
 * ⚠️ On the CELL the reason REPLACES the label on the `title` rather than
 * sitting beside it — an icon-only button has one `title`, and where a control
 * is unavailable the reason is the more useful of the two. `aria-label` still
 * names the action AND the quote, so the button is never anonymous to a screen
 * reader reading down a column of them. Same treatment as Convert, one button
 * along.
 *
 * ⚠️ On the CARD the label is spelled out instead: a card has the width, and a
 * hover `title` is not reachable on the touch device that layout exists for — so
 * the reason has to survive somewhere the label already is. It is still set as a
 * `title` for the pointer case, but the button is never anonymous without it.
 */
function SendSignatureControl({
  quote,
  denial,
  variant,
  onSend,
}: {
  quote: Quote;
  /** Why it is unavailable, or `null` when it is live. */
  denial: string | null;
  variant: 'cell' | 'card';
  onSend: (quote: Quote) => void;
}) {
  const label = `Send ${quote.subject.trim() || `quote #${quote.id}`} for signature`;
  if (variant === 'card') {
    return (
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<SignatureIcon />}
        disabled={denial !== null}
        {...(denial ? { title: denial } : {})}
        onClick={() => onSend(quote)}
      >
        Send for signature
      </Button>
    );
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      leadingIcon={<SignatureIcon />}
      aria-label={label}
      title={denial ?? label}
      disabled={denial !== null}
      onClick={() => onSend(quote)}
    />
  );
}

/* -------------------------------------------------------------------------
 * THE SIGNATURE MENU — three actions on one row, in the shape of the "+" menu
 * ---------------------------------------------------------------------- */

/** Roughly a 272px menu; clamped into the viewport before it is drawn. */
const SIGNATURE_MENU_WIDTH = 272;
/**
 * Header plus three items, each able to carry a disabled sentence. Only used to
 * decide whether to flip up, and deliberately sized for the TALL case:
 * over-estimating flips the menu up slightly early, which is harmless;
 * under-estimating opens it off the bottom of a phone screen.
 */
const SIGNATURE_MENU_EST_HEIGHT = 260;

const MENU_ITEMS = '[role="menuitem"]';

/**
 * THE ROW'S SIGNATURE ACTIONS, as a menu.
 *
 * Owner's instruction: *"the sign button in lists view can be made like the +
 * button to implement this."* So this is modelled on `CreateFromDealMenu` on
 * `/deals` rather than being a second menu pattern — same portal, same
 * placement, same keyboard contract — because a list that grows two kinds of row
 * menu has grown a bug nobody will file.
 *
 * ⚠️ **Portalled to the body and positioned `fixed`, and that is not a style
 * choice.** This lives inside `TableWrap`, whose `overflow-x-auto` computes
 * `overflow-y: auto` as well, so an absolutely-positioned menu would be clipped
 * to the table's box and the lower rows' menus would be unreachable. Same reason
 * `ColumnFilter` and the deals list's "+" menu are portalled.
 *
 * ── ⚠️ THE THREE ITEMS ARE NOT VARIATIONS ON ONE ACTION ──────────────────
 *
 * The middle two are opposites, and they are worded so that is legible:
 *
 *  - **Send for signature** raises a NEW envelope. Refused while one is
 *    outstanding, which is the state this menu renders in;
 *  - **Resend for signature** asks DocuSign to email the SAME person again. The
 *    envelope, the customer's live signing link and any partial signature are
 *    all untouched;
 *  - **Change signer and resend** VOIDS the envelope and starts again. The
 *    original signer is emailed the reason, their link dies, and any partial
 *    signature is discarded.
 *
 * Somebody who wants the same person chased must be able to tell at a glance
 * which of the last two they want, or they will void a live envelope to achieve
 * what a reminder would have done. ⚠️ **The cost of the third is stated inside
 * `ChangeSignerDialog`, in front of the irreversible step, and is NOT restated
 * here** — a menu is not the place for prose, and a second copy of a sentence
 * about a destructive action is a second thing to drift.
 */
function SignatureMenu({
  quote,
  envelope,
  sendDenial,
  canManage,
  manageDenial,
  variant,
  onSend,
  onReminded,
  onChangeSigner,
}: {
  quote: Quote;
  /** The outstanding envelope this menu acts on. Never `null` here. */
  envelope: DocuSignEnvelope;
  /** Why a fresh Send is unavailable, or `null`. */
  sendDenial: string | null;
  canManage: boolean;
  manageDenial: string | null;
  variant: 'cell' | 'card';
  onSend: (quote: Quote) => void;
  onReminded: (envelope: DocuSignEnvelope) => void;
  onChangeSigner: (quote: Quote, envelope: DocuSignEnvelope) => void;
}) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState({
    left: 0,
    top: 0,
    width: SIGNATURE_MENU_WIDTH,
    flip: false,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const quoteName = quote.subject.trim() || `quote #${quote.id}`;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const width = Math.min(SIGNATURE_MENU_WIDTH, window.innerWidth - 16);
    const below = window.innerHeight - box.bottom;
    const flip = below < SIGNATURE_MENU_EST_HEIGHT && box.top > below;
    // Right-aligned to the trigger, then clamped: this sits in the last column
    // of a table that is already wider than the viewport, so a left-aligned menu
    // would hang off a 320px screen.
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(Math.max(box.right - width, 8), maxLeft);
    setRect({ left, top: flip ? box.top : box.bottom, width, flip });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>(MENU_ITEMS)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function close(returnFocus = true) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      // A row menu can be opened from inside other dismissable surfaces; don't
      // let one Escape close two things.
      event.stopPropagation();
      close();
      return;
    }

    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? []);
    if (items.length === 0) return;

    if (event.key === 'Tab') {
      // Portalled, this is nowhere near its trigger in the tab order, so an
      // uncaught Tab would strand focus at the end of the document.
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowDown'
            ? (current + 1) % items.length
            : (current - 1 + items.length) % items.length;
    items[next]?.focus();
  }

  /*
   * ⚠️ **Why "Change signer and resend" is DISABLED on a terminal envelope
   * rather than hidden.** The reader has deliberately opened a menu to find this
   * item, and an item simply absent from a menu somebody went looking in is
   * indistinguishable from a broken page. The action is real and the refusal is
   * about THIS envelope, so it is stated — the same call the Send control on
   * this page already makes. (Contrast the Xero `sync` rule, where the affordance
   * must be ABSENT: there the only outcome is an error for everybody, which is a
   * different question.)
   *
   * ⚠️ Answered from `terminal`, the server's own flag, and never from the
   * status NAME. This codebase has been bitten three times by matching on an
   * admin-editable string.
   *
   * ⚠️ **The cooldown is deliberately absent from this list, and that is not an
   * omission.** No envelope read carries a last-reminded stamp into this app, so
   * a just-reminded envelope LOOKS remindable and the `429` is where anybody
   * finds out — the same accepted contract as the company-approval resend. A
   * countdown synthesised here would be a clock we cannot set.
   */
  const changeSignerDenial = !canManage
    ? (manageDenial ?? 'You do not have permission to change the signer on this quote.')
    : envelope.terminal
      ? `This envelope is ${envelope.status}, so there is no live request to re-point. Send a new one instead.`
      : null;

  const triggerLabel = `Signature actions for ${quoteName}`;

  return (
    <>
      {variant === 'card' ? (
        <Button
          ref={triggerRef}
          variant="secondary"
          size="sm"
          leadingIcon={<SignatureIcon />}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-label={triggerLabel}
          title={triggerLabel}
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={onTriggerKeyDown}
        >
          Signature
        </Button>
      ) : (
        <Button
          ref={triggerRef}
          variant="ghost"
          size="sm"
          iconOnly
          leadingIcon={<SignatureIcon />}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          /*
           * Never a bare glyph: in a page of twenty rows, twenty buttons called
           * "Signature" are twenty identical entries in a screen reader's list.
           */
          aria-label={triggerLabel}
          title={triggerLabel}
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={onTriggerKeyDown}
        />
      )}

      {open &&
        createPortal(
          <div
            ref={menuRef}
            onKeyDown={onMenuKeyDown}
            style={{
              position: 'fixed',
              left: rect.left,
              width: rect.width,
              ...(rect.flip ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.top + 4 }),
            }}
            className="z-[60] overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-lg"
          >
            {/* The label sits OUTSIDE `role="menu"`: a menu may only contain
                menu items, and prose inside one is read as an item with no
                role. It names which quote the items belong to, which nothing
                else in the portalled popover says. */}
            <p className="truncate px-2 pt-1 pb-1 text-xs font-semibold text-fg-subtle">
              Signature — {quoteName}
            </p>
            <div role="menu" id={menuId} aria-label={triggerLabel}>
              {/*
                First, because it is the item whose absence would be noticed:
                this menu replaced a button that did exactly this and nothing
                else. On a row with an outstanding envelope it is refused, and
                the reason is what points the reader at the two items below it.
              */}
              <Button
                role="menuitem"
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                disabled={sendDenial !== null}
                title={sendDenial ?? 'Send for signature'}
                onClick={() => {
                  setOpen(false);
                  onSend(quote);
                }}
              >
                Send for signature
              </Button>
              {sendDenial && (
                <p className="m-0 px-2.5 pb-1 text-2xs text-fg-subtle [overflow-wrap:anywhere]">
                  {sendDenial}
                </p>
              )}

              {/*
                ⚠️ **Self-contained: it makes its own call.** It owns the
                in-flight guard, the abort on unmount, the cooldown countdown and
                an outcome worded from what was observed. This page passes an
                envelope and a gate and adds nothing — a second sentence here
                about what a `200` means would be a second thing to drift from the
                one that already exists.
              */}
              <RemindEnvelopeControl
                envelope={envelope}
                canManage={canManage}
                manageDenial={manageDenial}
                variant="menu"
                onReminded={onReminded}
              />

              {/*
                ⚠️ **The ellipsis is load-bearing**: this opens a dialog and voids
                nothing on its own. A menu item reading as a bare verb, on an
                operation this destructive, invites a press from somebody who
                thinks they can still back out — and they can, but only because
                the dialog exists.
              */}
              <Button
                role="menuitem"
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                disabled={changeSignerDenial !== null}
                title={changeSignerDenial ?? 'Change signer and resend'}
                onClick={() => {
                  setOpen(false);
                  onChangeSigner(quote, envelope);
                }}
              >
                Change signer and resend…
              </Button>
              {changeSignerDenial && (
                <p className="m-0 px-2.5 pb-1 text-2xs text-fg-subtle [overflow-wrap:anywhere]">
                  {changeSignerDenial}
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * WHICH SIGNATURE CONTROL THIS ROW GETS — the one decision point, for both
 * layouts.
 *
 * ⚠️ **A menu appears only where there is something to put in it.** Two of the
 * three items act ON an outstanding envelope; with none in hand they would be
 * two permanently-dead rows under a trigger, and a menu whose only live item is
 * the one the button used to do directly is a click bought for nothing. So a row
 * with no known envelope keeps the plain Send control it has always had.
 *
 * ⚠️ **"No known envelope" is read ONLY from a `ready` state, which matches the
 * enable-when-in-doubt rule `sendSignatureDenial` already follows.** `loading`,
 * `error` and `unavailable` all mean *we do not know*, and in every one of them
 * the plain Send button is the right control: it is the action somebody can
 * still take, and its own refusal is handled at the server. A menu built on a
 * guess would offer a reminder for an envelope that may not exist.
 *
 * ⚠️ `unfetched` is deliberately not consulted, for the same reason it is not
 * consulted in `sendSignatureDenial`: it can only ever make a MISS unreliable,
 * and a miss lands on the plain button, which is the permissive answer.
 *
 * ONE component in two layouts rather than two renderings of the idea — the same
 * arrangement `SendSignatureControl` uses on this page. ⚠️ *`XeroRowPushControl`
 * was named here too until 2026-08-31, when it was removed from this list.*
 * A second copy is how a card and a cell end up disagreeing about what a row can
 * do.
 */
function RowSignatureActions({
  quote,
  signatures,
  sendDenial,
  canManage,
  manageDenial,
  variant,
  onSend,
  onReminded,
  onChangeSigner,
}: {
  quote: Quote;
  signatures: SignatureState;
  sendDenial: string | null;
  canManage: boolean;
  manageDenial: string | null;
  variant: 'cell' | 'card';
  onSend: (quote: Quote) => void;
  onReminded: (envelope: DocuSignEnvelope) => void;
  onChangeSigner: (quote: Quote, envelope: DocuSignEnvelope) => void;
}) {
  const envelope =
    signatures.kind === 'ready' ? (signatures.byQuoteId.get(quote.id) ?? null) : null;

  if (envelope === null) {
    return (
      <SendSignatureControl quote={quote} denial={sendDenial} variant={variant} onSend={onSend} />
    );
  }

  return (
    <SignatureMenu
      quote={quote}
      envelope={envelope}
      sendDenial={sendDenial}
      canManage={canManage}
      manageDenial={manageDenial}
      variant={variant}
      onSend={onSend}
      onReminded={onReminded}
      onChangeSigner={onChangeSigner}
    />
  );
}

/**
 * What to call a quote that is only known as a kanban card.
 *
 * `title` is `string | null` on every card and a quote's subject can be empty,
 * so a blank is reachable — and a dialog asking somebody to approve a real
 * invoice for an unnamed record is precisely the blank this app must never
 * render. `reference` is the quote number, the string finance reads out on the
 * phone. `#id` is the last resort and still identifies the record, which is
 * more than nothing does.
 */
function cardLabel(card: KanbanCard): string {
  if (card.title !== null && card.title !== '') return card.title;
  if (card.reference !== null && card.reference !== '') return card.reference;
  return `Quote #${card.id}`;
}

function ValidTillCell({ quote }: { quote: Quote }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {/* `null` is open-ended, which is a real and ordinary state — not a
          missing value, and emphatically not "expired". */}
      <span className="whitespace-nowrap tabular">
        {quote.validTill === null ? 'Open-ended' : formatDateOnly(quote.validTill)}
      </span>
      {/* A word, not a colour: the badge says "Expired" so the row still reads
          correctly in greyscale and to a screen reader. It renders nothing at
          all for a quote with months left. */}
      <ExpiryBadge validTill={quote.validTill} />
    </span>
  );
}

function SubjectCell({ quote }: { quote: Quote }) {
  return (
    <div className="min-w-0">
      <TextLink
        to={`/quotes/${quote.id}`}
        className="rounded-sm text-sm font-medium [overflow-wrap:anywhere]"
      >
        {/* Xero's number is composed in rather than stored — see
            `documentDisplayTitle`. This list has no Xero sub-line of its own
            (the one below is `quoteNumber`, the CRM's own reference), so this
            is the only place a reader scanning quotes can see it. */}
        {documentDisplayTitle(quote.subject, quote.xeroQuoteNumber)}
      </TextLink>
      {/* `quoteNumber` is the CRM's own unique reference — the string someone
          reads out on the phone — and is neither of the two Xero fields, which
          nothing populates yet. It is nullable, so many rows have no sub-line. */}
      {quote.quoteNumber && (
        <p className="mt-0.5 truncate text-xs text-fg-muted tabular">
          <span className="sr-only">Quote number </span>
          {quote.quoteNumber}
        </p>
      )}
    </div>
  );
}

/**
 * The columns this page hands to `useTableFilters` — the only ones that may
 * grow a header control.
 *
 * ⚠️ *SUPERSEDED 2026-09-02: "The ONE column this page hands to
 * `useTableFilters`, and the only one that may grow a header control."* True
 * when the Issue date funnel was the only header control; there are five now.
 * **A count written into a heading is the sentence nobody re-reads while
 * appending to the array underneath it**, and nothing fails when it goes wrong.
 *
 * ⚠️ **A `sortKey` is a claim about the SERVER, not about this table.** The
 * hook never sorts rows itself — reordering the twenty rows on screen while
 * implying the whole list is ordered is worse than no sort at all — so a
 * `sortKey` on a column the API cannot order by would highlight a header,
 * send a key, and take the list down with a `.strict()` 422. Every `sortKey`
 * below is a real `QuoteListSort` member pinned with `satisfies`, so a
 * misspelling is a `tsc` error rather than a 422 that renders as *"you have no
 * quotes"*; `isQuoteSort` is the runtime half of the same guard.
 *
 * Named ones that are NOT sortable and must not become so:
 *
 *  - **Awaiting signature** is matched client-side from a *separate* endpoint,
 *    so it is not a column of `d_quotes` at all and could never be a `?sort=`
 *    key;
 *  - **Grand total** and **Valid till** are orderable in principle and are not
 *    members of `QuoteListSort` today. Asking for one is a 422, not a silent
 *    fallback to the default.
 *
 * ⚠️ *SUPERSEDED 2026-09-02, and only the STAGE half of it: "**Grand total**,
 * **Stage** and **Valid till** are orderable in principle and are not members of
 * `QuoteListSort` today."* **`stage` shipped as a real member** and Stage now
 * carries both controls. The sentence was a claim about somebody else's enum,
 * true when written; the other two names in it are untouched and still true.
 *
 * ⚠️ *SUPERSEDED 2026-09-09, and it had become a claim about the whole array:
 * "The funnel that comes with the header is page-scoped — the hook's default —
 * and says so in its own popover. That is deliberately a DIFFERENT mechanism
 * from the 'Issued between' range in the filter bank…"* It was written when
 * **every** column here was page-scoped, and it now describes **two of five** —
 * `stageId`, `lineProjectStatus` and `importedFromXero` all narrow the whole
 * list. The funnel/range pairing it describes is real and is a property of the
 * **Issue date** column alone, so it now lives on that entry rather than in a
 * heading. **A paragraph that generalises over an array is falsified by an
 * append, and appending is the one thing this array is for.**
 *
 * ⚠️ **Which columns are page-scoped is written NOWHERE but on the entries
 * themselves**, deliberately — a list of them up here is the second copy that
 * goes wrong on the next append, and the popover already tells the reader which
 * one they are looking at without being asked.
 */
const QUOTE_COLUMNS: ColumnSpec<Quote>[] = [
  /**
   * The quote's own name — A–Z on the Quote header, which is the column somebody
   * most often wants ordered and was the last visible one without a control.
   *
   * ⚠️ **FIRST IN THE ARRAY BECAUSE THIS LIST IS TABLE ORDER.**
   * `QUOTE_COLUMN_KEYS` is derived from it and handed to `FilterSheet`, which
   * renders the keys in the order given — so an entry appended here would put
   * the Quote section at the BOTTOM of the mobile sheet while its header sits at
   * the left of the table. Nothing fails; the two just stop agreeing.
   *
   * ⚠️ **`get` RETURNS THE BARE `subject`, NOT THE COMPOSED TITLE THE CELL
   * SHOWS.** `SubjectCell` renders `documentDisplayTitle(subject,
   * xeroQuoteNumber)`, which appends Xero's number — and `subject` is what the
   * server orders by, so the funnel and the sort have to speak the same field or
   * the header's two controls would answer about two different values. The cost
   * is visible and small: on a pushed quote the funnel option reads
   * `Arizona State University` where the cell reads
   * `Arizona State University QU-0042`. The alternative is worse — a funnel
   * whose values no `?sort=` could ever reproduce.
   *
   * ⚠️ **`scope` IS THE DEFAULT `'page'`, DELIBERATELY.** There is no
   * `?subject=` filter — `search` is the server-side way to narrow by subject and
   * it already sits in the filter bank, matching `subject` OR `quoteNumber`. So
   * this funnel narrows the loaded page only, which the popover's own footnote
   * says without being asked. Giving it a `param` would invent a query key the
   * `.strict()` schema answers 422 to.
   *
   * ⚠️ **THE FUNNEL LISTS ~20 NEAR-UNIQUE SUBJECTS, AND THAT IS THE ACCEPTED
   * COST OF THE SORT, NOT A DEFECT.** Options are accumulated from the loaded
   * rows, and a page of quotes is twenty mostly-distinct names — so it reads as
   * a long list rather than as a set of categories, exactly as the Company
   * funnel on `/companies` does (`CompaniesPage`'s `name` column) and the
   * Subject funnel on the Accounts dashboard. It is kept because
   * `FilterableTH` renders a sort control ONLY through a `filter`, so the
   * funnel is what the A–Z arrives attached to. Its one genuine use is the Is
   * empty / Is not empty pair — `subject` is `NOT NULL` on the wire, so both
   * answer for every row and neither hides anything.
   *
   * ⛔ **Do NOT "fix" this by adding a sort-only mode to `ColumnFilter`.** That
   * is a shared primitive behind six pages, and `CompaniesPage` already settled
   * the question the other way: its `SORT_ONLY_COLUMN_KEYS` hides a column with
   * NO header, which is not this case — this one has a header, a cell and a
   * reader looking at it.
   */
  {
    key: 'subject',
    label: 'Quote',
    get: (quote) => quote.subject,
    /*
     * ⚠️ **`satisfies` for the same reason the Stage column carries it.** The
     * quotes query schema is `.strict()`, so a misspelt key is a 422 naming it
     * and the list renders as *"you have no quotes"* rather than as a bad sort.
     * `'name'` is the `/companies` spelling of this idea and `'quoteNumber'` is
     * a real member on the Accounts dashboard's own enum — neither travels here.
     */
    sortKey: 'subject' satisfies QuoteListSort,
  },
  /**
   * The quote's STAGE — and the one column on this page whose selection does
   * NOT live in `useTableFilters`.
   *
   * 🔴 **THE URL IS THE SINGLE SOURCE OF TRUTH FOR THE SELECTED STAGE, AND THIS
   * SPEC IS ONLY HALF OF THE COLUMN.** `stageId` is an `ID_FILTERS` key: it is
   * in the query string, it is deep-linkable (`/quotes?stageId=3`), it is saved
   * against the account, it is handed to the BOARD in `boardParams`, and there
   * is a "All stages" `Combobox` in the filter bank bound to it. A funnel
   * keeping its own copy in hook state would be a second answer to one question
   * — the Combobox reading "All stages" while the table showed one stage — so
   * `headerFor` below intercepts this key entirely and routes the selection
   * through the URL. **Read `stageFilterProps` before changing anything here.**
   *
   * ⚠️ **`param` is declared and is deliberately NEVER CONSUMED.** `listFilters`
   * picks two keys by name and `stageId` is not one of them; `load` reads
   * `stageId` out of the query string with the other id filters. The param name
   * is recorded because it is the right one — the transport is the URL, not
   * `serverParams`. `scope: 'server'` is what makes the popover's footnote say
   * this narrows the whole table, which it does.
   *
   * ⚠️ **`get` returns the stage ID as a string, not its NAME**, so it lines up
   * with the option values, which are ids. Matching a stage by name is the trap
   * this codebase has fallen into three times (`"Not Accepted"` contains
   * `accept`); `/quote-stages` is admin-writable, so any rule keyed on the label
   * is a rule an ordinary rename breaks. `null` for a quote with no stage is a
   * real state and makes the popover's Is empty / Is not empty pair meaningful
   * here, unlike on the two columns below.
   *
   * ⚠️ **`options` is NOT declared here** and is supplied by `headerFor` from
   * the loaded `/quote-stages` master — the stages are runtime data and this is
   * a module constant. Without them the popover would offer only the stages
   * present on the loaded page, which is the defect `ColumnSpec.options` exists
   * to fix and is worst for exactly the rare stage somebody is hunting.
   */
  {
    key: 'stageId',
    label: 'Stage',
    get: (quote) => (quote.stage ? String(quote.stage.id) : null),
    scope: 'server',
    param: 'stageId',
    /*
     * 2026-10-28: every ticked stage goes to the URL as one CSV (`headerFor`), so
     * the hook never holds a stage selection; `multi` keeps the popover's scope
     * footnote honest.
     */
    multi: true,
    /*
     * ⚠️ **`satisfies` because `ColumnSpec.sortKey` is a plain `string`, and the
     * one-word difference between the two lists is exactly what it catches.**
     * `'status'` is the INVOICES spelling of this sort; sending it here is a 422
     * naming the unrecognised key on a `.strict()` schema, which renders as *"you
     * have no quotes"* rather than as a bad sort. `isQuoteSort` is the runtime
     * half of the same guard.
     */
    sortKey: 'stage' satisfies QuoteListSort,
  },
  /**
   * The quote's ISSUE DATE — page-scoped funnel, server-scoped sort, in one
   * control, exactly as the Quote column above pairs them.
   *
   * ⛔ **`scope` IS THE DEFAULT `'page'` AND MUST STAY THAT WAY. DO NOT GIVE
   * THIS COLUMN A `param`.** Two independent reasons, and each alone settles it:
   *
   *  1. **There is no exact-match `issueDate` filter to send.**
   *     `QuoteListParams` carries `issueDateBefore` and `issueDateAfter` — a
   *     RANGE — and nothing else on this field. The quotes query schema is
   *     `.strict()`, so `?issueDate=2026-04-01` is a 422 naming the key, which
   *     empties the table and reads as *"no quotes were issued that day"*.
   *  2. **The range pair ALREADY HAS A WRITER, and it is not this control.**
   *     `issueDateAfter` / `issueDateBefore` are `DATE_FILTERS` keys owned by
   *     the "Issued between" range in the filter bank: they live in the query
   *     string, they are deep-linkable, they are saved against the account, and
   *     `load` reads them back out of the URL. A funnel emitting either would be
   *     a second answer to one question — the range showing one window while the
   *     table showed another — which is the failure the Stage column's URL
   *     interception exists to prevent, arriving on a control that has no such
   *     interception to route through.
   *
   * ⚠️ **So this is the funnel/range pairing the array's header used to describe
   * as though it were the rule for every column here**: one client-side pass over
   * the loaded rows on the same field the server bounds from the filter bank —
   * exactly as `/deals` pairs its `closing` funnel with its closing-date range.
   * The popover's own footnote says which of the two the reader is using.
   *
   * ⚠️ **Bridging it to the bank's two params — the Stage column's shape — is
   * NOT the fix, and would be worse than the gap.** A funnel is a set of ticked
   * exact values; a range is two bounds. Ticking three dates cannot be expressed
   * as one window, and collapsing them to min–max would silently return every
   * quote issued BETWEEN them, including the ones nobody ticked. **A control that
   * returns rows its own checkboxes exclude is the reassuring direction.**
   *
   * ⚠️ **`options` is neither declared nor wanted.** A date is an OPEN set, not a
   * closed vocabulary — the accumulated list is the honest one, and it carries
   * counts because a page-scoped column's count is about the rows on screen and
   * is therefore true.
   *
   * Plain 'YYYY-MM-DD' kept as a string and never put through `new Date()`.
   * `null` is a real state — a quote with no issue date — and `normalise` reads
   * it as EMPTY rather than as a value, so the Is empty / Is not empty pair is
   * meaningful here. ⚠️ **That pair is page-scoped whatever the column's scope**:
   * this endpoint has no is-null filter, and a quote with no issue date matches
   * NEITHER bound of the range, so the two mechanisms cannot cover for each other.
   */
  {
    key: 'issueDate',
    label: 'Issue date',
    get: (quote) => quote.issueDate,
    format: formatDateOnly,
    /*
     * ⚠️ **`satisfies` for the reason the header of this array states about every
     * key in it** — and this entry was one of the two that did not carry it, so
     * that sentence was a claim the compiler was not checking. `'billDate'` is the
     * INVOICES spelling of this idea and is a real member of that enum; sent here
     * it is a 422 on a `.strict()` schema, which renders as *"you have no
     * quotes"* rather than as a bad sort.
     */
    sortKey: 'issueDate' satisfies QuoteListSort,
  },
  /**
   * "Projects added / not added", asked of the quote's LINE ITEMS.
   *
   * ⚠️ **`scope: 'server'`, and that is the whole point of the column.** The
   * list is paginated, so a page-scoped funnel would answer "which of these
   * twenty" while the header implied "which quotes" — and the person using it is
   * looking for the ones they have NOT seen. `param` puts it on the request;
   * `serverParams` is re-parsed into `listFilters` below and merged into `load`.
   *
   * ⚠️ **`options` is DECLARED, not accumulated.** Without it the popover offers
   * only the states already present on the loaded page, so "Partly added" would
   * be missing precisely when it is rare. This is the first call site of that
   * prop; see `ColumnSpec.options` for why the other closed-enum columns in this
   * app were deliberately left alone.
   *
   * ⚠️ **`sortKey` is a claim about the SERVER.** `lineProjectStatus` is a real
   * `QuoteListSort` member (2026-09-02) that orders by the four-state rank,
   * least complete first — not an A–Z over a flag. `isQuoteSort` is the second
   * half of that guard.
   *
   * ⚠️ **`get` never returns null**, so the popover's Empty / Not empty pair
   * matches everything and nothing here. That is a property of the primitive
   * shared with `role` on `/users`, not something this column introduced, and it
   * is left alone rather than special-cased into a shape only this page knows.
   */
  {
    key: 'lineProjectStatus',
    label: 'Projects',
    get: (quote) => quote.lineProjectStatus,
    format: lineProjectStatusLabel,
    options: LINE_PROJECT_FILTER_OPTIONS,
    scope: 'server',
    param: 'lineProjectStatus',
    // Several ticked = one CSV, ORed by the server (2026-10-28).
    multi: true,
    /*
     * ⚠️ **`satisfies`, added 2026-09-09 — the second of the two entries whose
     * `sortKey` was an unchecked string** while this array's header asserted that
     * every one of them was pinned. The key and the param are spelled alike here,
     * which is what made the guard look redundant: they are different contracts on
     * the same schema (`sort` enum vs filter key) and only one of them was typed.
     */
    sortKey: 'lineProjectStatus' satisfies QuoteListSort,
  },
  /**
   * "From Xero" — Yes / No, immediately after Projects, matching `/invoices`.
   *
   * ⚠️ **"No" MEANS *CREATED IN EMERGE*, NEVER *NOT IN XERO*.** A quote raised
   * here and pushed is in Xero and still reads "No". The column answers where a
   * document came FROM; the `xero` link envelope answers whether it is THERE,
   * and the envelope cannot answer the first question because a push writes a
   * link row exactly as an import does. That is the whole reason migration 130
   * stored the marker rather than deriving it. `@/lib/imported-from-xero` is the
   * one copy of both words — do not reword them here, and ⛔ **no helper text
   * says any of this on screen**; the distinction lives in source.
   *
   * 🔴 **`scope: 'server'`. AN EARLIER DRAFT OF THE SHARED MODULE SAID THIS
   * COLUMN MUST BE `'page'` BECAUSE NO `?importedFromXero=` PARAM EXISTED. THAT
   * MEASUREMENT WAS OVERTAKEN BY THE BACKEND SHIPPING IT.** Re-measured
   * independently in `emerge-backend-app` on 2026-09-02, from this session and
   * from the coordinator: `quotes.schema.ts:706` declares
   * `importedFromXero: queryBoolean().optional()`, `:830` adds
   * `importedFromXero` to the `sort` enum and `:827` adds `stage`. **A negative
   * claim about another repo is the species that rots with nothing failing** —
   * it was measured honestly, stated emphatically, and false within the day.
   *
   * ⚠️ **`get` SPEAKS `'true'` / `'false'`; `format` SUPPLIES `'Yes'` / `'No'`,
   * AND THE ORDER MATTERS.** `useTableFilters` puts the ticked option's VALUE on
   * the wire verbatim (`rawServerParams[column.param] = chosen`) and renders a
   * label through `format`. A `get` emitting `'Yes'` would send
   * `?importedFromXero=Yes`, which `queryBoolean()` refuses — a 422 that empties
   * the table and reads as *"nothing was imported"*, which is false for 1,127
   * documents. The shared module owns both spellings so the CELL's word and the
   * FILTER's value cannot drift apart across the two pages.
   *
   * ⚠️ **BOTH DIRECTIONS GENUINELY NARROW, which is unusual on that schema.**
   * `imported_from_xero_at` is NULL or a timestamp with no third state, so
   * `IS NOT NULL` and `IS NULL` PARTITION the list and the two counts sum to the
   * unfiltered total. The neighbouring `awaitingSignature` and `activityFirst`
   * booleans both treat `false` as "no filter", so a reader who has just read
   * either will carry that caveat here and it does not apply: omitting the key
   * and sending `false` are DIFFERENT requests.
   *
   * ⛔ **NO `multi`.** A comma-separated `importedFromXero` is a 422 exactly as
   * `?stageId=1,2` is — CSV filters are companies-only across this API. With
   * both options ticked the hook sends nothing and narrows the loaded page,
   * comparing the ticked value against `normalise(get(row))`, which is the same
   * `'true'`/`'false'` vocabulary — the second reason `get` must speak the
   * option values rather than the words.
   */
  {
    key: IMPORTED_FROM_XERO_COLUMN,
    label: 'From Xero',
    get: (quote) => importedFromXeroValue(quote.importedFromXeroAt),
    format: importedFromXeroFormat,
    options: IMPORTED_FROM_XERO_FILTER_OPTIONS,
    scope: 'server',
    param: IMPORTED_FROM_XERO_COLUMN,
    sortKey: 'importedFromXero' satisfies QuoteListSort,
  },
];

/**
 * The same key the header renders, derived rather than retyped so the mobile
 * `FilterSheet` cannot drift out of step with the table.
 */
const QUOTE_COLUMN_KEYS: readonly string[] = QUOTE_COLUMNS.map((column) => column.key);

/**
 * ── THE TABLE'S OWN COLUMN LIST ────────────────────────────────────────────
 *
 * 🔴 **THIS IS NOT `QUOTE_COLUMNS`, AND CONFLATING THE TWO IS THE MISTAKE THIS
 * COMMENT EXISTS TO PREVENT.** `QUOTE_COLUMNS` is the FILTER/SORT model and
 * covers FIVE of the nine columns — it never described table order, and four
 * columns (Awaiting signature, Valid till, Grand total, Actions) have no entry
 * in it at all because they carry no funnel. This array is the NINE columns the
 * table actually renders, in their declared order, and it is what the header,
 * the body and the skeleton are all generated from.
 *
 * ⚠️ **THERE WERE THREE HARDCODED NINE-CELL LISTS BEFORE THIS, and they could
 * only be kept in step by hand.** The file's own `min-w-` note already recorded
 * the failure — "a cell missing here is how the layout jumps a column over as
 * the rows arrive" — which is a warning about a hazard the structure created.
 * One list removes it: a column added here appears in all three or in none.
 *
 * ⚠️ **`width` IS THE PIXEL VALUE OF `className`, AND THE PAIR IS LOAD-BEARING.**
 * The class is what the browser lays out with; the number is what the table's
 * `minWidth` is summed from. They are written side by side so the arithmetic
 * cannot drift from the rendering the way the superseded prose below did.
 *
 * ⚠️ **`width: null` MEANS FLUID — exactly one column may carry it.** `subject`
 * has never had a `w-` class on purpose (see `QuotesTableHead`): it takes
 * whatever the fixed columns leave. See `tableMinWidth` for what happens when a
 * reader hides it.
 *
 * 🔴 **THE SUPERSEDED ARITHMETIC WAS WRONG IN TWO HALVES WHOSE ERRORS CANCELLED.**
 * The `min-w-[1616px]` note listed "Projects 144" and derived a Quote floor of
 * 304. Projects became `w-40` (160) on 2026-09-02 and that note was not
 * re-read — so the real fixed sum is **1328** and the real Quote floor is
 * **288**. 1328 + 288 is the same 1616, which is why nothing ever looked wrong
 * and why the stale decomposition survived: **the total was right and both
 * terms under it were not.** The numbers below are measured off the header's
 * own `className`s, and the full-visibility min-width is still exactly 1616.
 */
interface QuoteTableColumn {
  key: string;
  /** The manager's label, and the header's where the header is a plain `TH`. */
  label: string;
  /** Rendered width in px, or `null` for the one fluid column. */
  width: number | null;
  /** The Tailwind width class the header cell carries, if any. */
  className?: string;
}

/** The actions lane — locked, always last, and `pinned` rather than `frozen`. */
const ACTIONS_COLUMN = 'actions';

const QUOTE_TABLE_COLUMNS: readonly QuoteTableColumn[] = [
  { key: 'subject', label: 'Quote', width: null },
  { key: 'stageId', label: 'Stage', width: 160, className: 'w-40' },
  { key: 'awaitingSignature', label: 'Awaiting signature', width: 176, className: 'w-44' },
  { key: 'issueDate', label: 'Issue date', width: 128, className: 'w-32' },
  { key: 'lineProjectStatus', label: 'Projects', width: 160, className: 'w-40' },
  { key: IMPORTED_FROM_XERO_COLUMN, label: 'From Xero', width: 128, className: 'w-32' },
  { key: 'validTill', label: 'Valid till', width: 192, className: 'w-48' },
  { key: 'grandTotal', label: 'Grand total', width: 160, className: 'w-40' },
  { key: ACTIONS_COLUMN, label: 'Actions', width: 284, className: 'w-[284px]' },
];

const QUOTE_TABLE_COLUMN_KEYS: readonly string[] = QUOTE_TABLE_COLUMNS.map(
  (column) => column.key,
);

/**
 * ⛔ **ACTIONS IS LOCKED, AND IT IS THE ONLY ONE.** Every per-record control on
 * this page lives in that one cell — tasks and notes, the PDF, the signature
 * menu, Convert, Edit and Delete — and five of the six have no second home on
 * this page at all. Hiding it would read exactly like the `/companies` incident
 * this repo already paid for, where a board that hid the row actions was
 * reported as the feature having been DELETED.
 */
const QUOTE_LOCKED_COLUMNS: readonly string[] = [ACTIONS_COLUMN];

/**
 * The columns this table renders with `pinned` rather than `frozen`.
 *
 * ⚠️ **NOT the same question as `QUOTE_LOCKED_COLUMNS`, though today they hold
 * the same one key.** Locked is about hiding and reordering; pinned is about
 * which edge a cell already occupies, and a locked column must stay freezable —
 * the day `subject` is locked for being the only thing that names the row, it is
 * also the column a reader most wants frozen to the left.
 *
 * Handed to three places for three different reasons: `useColumnLayout`, so a
 * freeze cannot write a second position rule onto a `sticky right-0` cell;
 * `ColumnManager`, so those buttons are absent rather than inert; and
 * `useFrozenOffsets`, so this lane's 224px is counted into the offset of
 * anything else frozen right.
 */
const QUOTE_PINNED_COLUMNS: readonly string[] = [ACTIONS_COLUMN];

/**
 * Why a locked column's switch is disabled, in the reader's own terms.
 *
 * A bare `disabled` makes a locked column and a broken switch identical, which
 * is why this sentence is one of the four the no-helper-text rule keeps. Per
 * KEY rather than one string, because the reasons genuinely differ between
 * pages and between columns — today only the actions lane is locked here, and
 * the reason is the six controls that have no second home on this page.
 */
function quoteLockReasonOf(key: string): string | undefined {
  return key === ACTIONS_COLUMN
    ? 'Every per-record control lives in this column, and none has another home on this page'
    : undefined;
}

const QUOTE_COLUMN_WIDTHS = new Map(
  QUOTE_TABLE_COLUMNS.map((column) => [column.key, column.width] as const),
);

const QUOTE_COLUMN_LABELS = new Map(
  QUOTE_TABLE_COLUMNS.map((column) => [column.key, column.label] as const),
);

function quoteColumnLabel(key: string): string {
  return QUOTE_COLUMN_LABELS.get(key) ?? key;
}

/**
 * The fluid Quote column's floor — see the arithmetic note on
 * `QUOTE_TABLE_COLUMNS`.
 *
 * ⚠️ **288, NOT THE 304 THE SUPERSEDED COMMENT DERIVED.** Both numbers produce
 * the same 1616 total; this one is the remainder of the widths the headers
 * actually carry. The owner's 2026-09-10 "double the Quote column" is intact —
 * it moved the total from 1464 to 1616 and that total is unchanged here.
 */
const SUBJECT_COLUMN_FLOOR = 288;

/**
 * The table's floor, in px, for the columns currently visible.
 *
 * 🔴 **AN INLINE STYLE, NEVER A CLASS.** Tailwind v4 scans SOURCE TEXT, so
 * `min-w-[${n}px]` emits no rule whatsoever — the class lands in the DOM and
 * does nothing, which looks identical to a table that simply chose not to have a
 * floor. `Table.tsx`'s freezing note and `UniversityPartnershipsPage` both
 * record this trap; a number goes through `style`.
 *
 * ⚠️ **HIDING A COLUMN HAS TO SHRINK THIS.** A floor left at 1616 over six
 * columns is a table that scrolls sideways with empty space in it — the reader
 * hides three columns to stop the horizontal scroll and the scroll stays.
 *
 * 🔴 **WHAT HAPPENS WHEN THE FLUID COLUMN IS HIDDEN, which is the case the
 * `min-w-` note warned about.** `subject` is the only column with no `w-`, so
 * hiding it leaves the table with none — and the old note said that is "what
 * stops the layout from filling a wide screen". It is not a broken state: the
 * floor is then **the visible fixed widths alone**, and because `Table` is
 * `w-full` under AUTO layout the browser distributes any surplus across the
 * remaining columns proportionally rather than starving one. So the table still
 * fills the panel; what changes is that the surplus is shared instead of all
 * landing in one column. That was chosen over re-labelling some other column as
 * fluid, which would silently re-cut a column the reader did not touch.
 */
function tableMinWidth(visibleKeys: readonly string[]): number {
  return visibleKeys.reduce((total, key) => {
    const width = QUOTE_COLUMN_WIDTHS.get(key);
    // `null` is the fluid column and contributes its FLOOR; an unknown key
    // contributes nothing rather than `NaN`, which would blank the style.
    if (width === null) return total + SUBJECT_COLUMN_FLOOR;
    return total + (width ?? 0);
  }, 0);
}

/**
 * What the header, body and skeleton need to place a cell on either frozen edge.
 *
 * ⚠️ **THE ACTIONS LANE IS STRIPPED HERE AND COUNTED IN THE MEASUREMENT, and
 * the asymmetry is the point.** The CELLS get `pinned` and no `frozen` — the
 * two write the same `box-shadow` property and cannot be composed — while
 * `useFrozenOffsets` is told about the lane through `QUOTE_PINNED_COLUMNS`, so
 * its 224px is counted into the offset of anything else frozen right. Lose
 * either half and a second right-frozen column is placed at `right: 0`,
 * underneath the row buttons, with nothing erroring.
 *
 * ⚠️ *SUPERSEDED: "**`frozenSideOf` HERE IS NOT THE CONTROLLER'S**".* It is now
 * — the hook takes the pinned set itself, so there is no second function to
 * keep in step with the first. This one line is still the only place the lane
 * is stripped for the cells.
 */
interface FrozenProps {
  frozen?: 'left' | 'right';
  frozenOffset?: number;
}

function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => 'left' | 'right' | undefined,
  offsets: Record<string, number>,
): FrozenProps {
  if (key === ACTIONS_COLUMN) return {};
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

interface QuotesTableHeadProps {
  headerFor: (key: string) => FilterableTHProps;
  visibleKeys: readonly string[];
  frozenSideOf: (key: string) => 'left' | 'right' | undefined;
  frozenOffsets: Record<string, number>;
  /**
   * The header row, handed to `useFrozenOffsets`, which measures the RENDERED
   * width of each cell. There is no `table-fixed` and no `<colgroup>` here, so a
   * `w-*` on a `<th>` is a request the content may outvote — the only honest
   * source for a frozen column's offset is the DOM.
   */
  headRowRef: React.RefObject<HTMLTableRowElement | null>;
}

function QuotesTableHead({
  headerFor,
  visibleKeys,
  frozenSideOf,
  frozenOffsets,
  headRowRef,
}: QuotesTableHeadProps) {
  /*
   * Quote, Stage, Issue date, Projects and From Xero carry controls — see
   * `QUOTE_COLUMNS` for why the others do not, and note that `FilterableTH`
   * claims `aria-sort` only where the API can really sort, so a plain `TH`
   * promises nothing.
   * (*SUPERSEDED: "Issue date and Projects are the ONLY headers carrying a
   * control" — `stage` and `importedFromXero` became real `QuoteListSort`
   * members on 2026-09-02, and `subject` reached this header later the same
   * day.*)
   *
   * ⚠️ **THE CELL FOR EACH KEY IS A SWITCH, NOT A TABLE OF JSX.** Every arm
   * below keeps the comment that was attached to the header it replaces —
   * those comments are the record of why each width and each control is what it
   * is, and a lookup object would have stranded them from the markup they
   * describe.
   */
  function headCell(key: string) {
    const frozen = frozenPropsFor(key, frozenSideOf, frozenOffsets);
    const column = QUOTE_TABLE_COLUMNS.find((entry) => entry.key === key);
    const widthClass = column?.className;

    /*
     * ⚠️ **`{...frozen}` AND `className` COME AFTER `{...headerFor(…)}`, and the
     * order is the point.** `headerFor` returns a whole `FilterableTHProps`
     * object; spreading it last would let any key it happens to carry overwrite
     * the freeze this page just computed. A frozen column that silently stopped
     * being frozen is invisible — the cell is present, at the right position,
     * with no error.
     */
    switch (key) {
      case 'subject':
        return <FilterableTH key={key} {...headerFor('subject')} {...frozen} />;
      case 'stageId':
        return (
          <FilterableTH key={key} {...headerFor('stageId')} className={widthClass} {...frozen} />
        );
      case 'awaitingSignature':
        return (
          <TH key={key} className={widthClass} {...frozen}>
            Awaiting signature
          </TH>
        );
      case 'issueDate':
        return (
          <FilterableTH key={key} {...headerFor('issueDate')} className={widthClass} {...frozen} />
        );
      case 'lineProjectStatus':
        return (
          <FilterableTH
            key={key}
            {...headerFor('lineProjectStatus')}
            className={widthClass}
            {...frozen}
          />
        );
      case IMPORTED_FROM_XERO_COLUMN:
        return (
          <FilterableTH
            key={key}
            {...headerFor(IMPORTED_FROM_XERO_COLUMN)}
            className={widthClass}
            {...frozen}
          />
        );
      case 'validTill':
        return (
          <TH key={key} className={widthClass} {...frozen}>
            Valid till
          </TH>
        );
      case 'grandTotal':
        return (
          <TH key={key} numeric className={widthClass} {...frozen}>
            Grand total
          </TH>
        );
      case ACTIONS_COLUMN:
        return (
          <TH key={key} numeric pinned className={widthClass}>
            Actions
          </TH>
        );
      default:
        return null;
    }
  }

  return (
    <THead>
      <TR ref={headRowRef}>
        {visibleKeys.map((key) => headCell(key))}
      </TR>
    </THead>
  );
}

/*
 * ── THE HEADER COMMENTS, KEPT ──────────────────────────────────────────────
 *
 * What follows is the reasoning that used to sit inline against each `<th>`
 * above. It is kept verbatim rather than deleted: every paragraph records a
 * decision somebody would otherwise re-litigate, and several of them are
 * explicit instructions NOT to change something.
 *
 * ⚠️ **Quote (`subject`)**
 *
          * A–Z on the quote's own name. ⚠️ **The funnel beside it is PAGE-scoped
          * and the sort is SERVER-scoped, in one control** — the popover says so
          * itself. That pairing is not a mistake to tidy: `FilterableTH` renders
          * a sort only through a `filter`, so the funnel is what the A–Z is
          * attached to. See the `subject` entry in `QUOTE_COLUMNS`.
          *
          * ⚠️ **No `className`, deliberately** — this header carried none as a
          * plain `TH`, so the Quote column has always taken whatever the other
          * columns' fixed widths leave it. Pinning a width here now would re-cut
          * the whole table to buy a funnel icon room it does not need:
          * `FilterableTH` puts the icon inside the same cell and truncates the
          * label before the column grows. (No count of those columns is written
          * here on purpose — that is the number nobody re-reads when one is
          * added, and this file has already been wrong that way twice.)
 *
 * ⚠️ **Stage (`stageId`)**
 *
          * ⚠️ **THIS FUNNEL AND THE "All stages" COMBOBOX IN THE FILTER BANK ARE
          * ONE FILTER OVER ONE URL PARAM.** The Combobox is NOT redundant and
          * must not be removed: funnels exist on table headers only, and this
          * page has a `?view=board` with no header at all — so on the board the
          * Combobox is the only way to set a stage. `headerFor` routes this
          * control's selection through the same `stageId` query param the
          * Combobox writes; read the block there before changing either.
          *
          * `w-40` is unchanged from the plain `TH` it replaces. The header now
          * carries a funnel and a sort control, but `FilterableTH` renders those
          * inside the same cell — the widest stage name is still what sets the
          * floor, and the badge below truncates to its own `title` rather than
          * wrapping the row taller.
 *
 * ⚠️ **Awaiting signature**
 *
          * Next to Stage because they are the two states of a quote and get
          * read together. The header says "Awaiting" rather than "Signature"
          * because the request behind it asks only for outstanding envelopes —
          * a signed, declined or voided quote is blank here, and a column
          * called "Signature" would make that blank read as "never sent".
 *
 * ⚠️ **Projects (`lineProjectStatus`)**
 *
          * "Projects added / not added", asked of the LINE ITEMS. Sits after the
          * dates rather than beside the quote, because it is a completeness
          * check somebody scans a column of — not part of identifying the row.
          *
          * ⚠️ **`w-40` HOLDS THE LONGEST LABEL, WHICH IS NOW "No projects apply"
          * — AND THERE ARE FIVE OF THEM, NOT FOUR.** *SUPERSEDED: `w-36`, "the
          * longest of the four labels ('No line items')".* That comment carried a
          * COUNT and a WIDTH sized to one particular label, so the fifth state
          * added on 2026-09-02 falsified both halves at once. The badge would have
          * truncated to its own `title` rather than breaking the row, so nothing
          * would have failed — it would just have read as clipped.
          *
          * **Do not write the count back in.** Size this from the longest label in
          * `LABELS`, and re-read it when one is added.
 *
 * ⚠️ **From Xero (`importedFromXero`)**
 *
          * "From Xero" — Yes / No. IMMEDIATELY AFTER Projects, and `/invoices`
          * puts it in the same place: the two lists are deliberately read alike,
          * so a column that drifts a position between them is a real cost.
          *
          * ⚠️ The badge that used to sit inside the STAGE cell moved here. Two
          * facts were sharing one cell — what stage a quote is at, and where the
          * document came from — which is why neither could be filtered or sorted
          * on until they were separated.
          *
          * ⚠️ **"No" is not "not in Xero".** A quote raised here and pushed
          * reads "No". ⛔ No helper text may say so on screen — see
          * `@/lib/imported-from-xero`, which is where that distinction lives.
          *
          * `w-32` matches "Issue date", the closest comparable header: the
          * HEADER is what sets the floor here, not the cell, whose widest
          * content is the three characters of "Yes".
 *
 * ⚠️ **The Xero column that is NOT here**
 *
          * ⚠️ **THE `w-44` XERO COLUMN WAS HERE AND WENT ON 2026-08-31, ON THE
          * OWNER'S INSTRUCTION** — the same removal was made on `/invoices` in the
          * same change. It carried `XeroRowPushControl`: an In Xero / Not in Xero
          * badge over a Push/Sync button, under the note "Goes to Xero when you
          * save it."
          *
          * *SUPERSEDED, what this comment used to say: "Its own column rather
          * than a third icon in Actions. The control is three states — Push /
          * Sync / disabled-with-a-reason — and two of the three are only honest
          * with a label and a sentence attached; an icon-only button cannot say
          * 'no changes recorded since the last send'."*
          *
          * His reasoning, in his words: **"The people who will be using this know
          * that it is going to Xero directly."** Saving a quote pushes it, so the
          * column was restating a rule its readers already hold.
          *
          * 🔴 **THE COST, RECORDED RATHER THAN SOLVED — it is a real loss and it
          * was accepted.** That badge was the ONLY place in this list where you
          * could see a quote had NOT reached Xero. A push is skipped outright when
          * the quote has no line items or its company is not linked, and it can
          * fail; after this removal **all three look identical to a row that
          * synced cleanly**, and the only way to tell is to open the record, where
          * `XeroPanel` still says so in full.
          *
          * ⚠️ **Do NOT compensate by putting an indicator somewhere else on this
          * page** — a smaller badge, a tint on the row, a warning count in the
          * header. That is re-adding what was asked to be removed, in a form
          * nobody reviewed. If the blind spot turns out to matter, it goes back to
          * the owner as a decision, not in as a workaround.
 *
 * ⛔ **Actions — LOCKED, `pinned`, and always the last column**
 *
          * 246px, sized for the WIDEST case rather than for this reader's.
          *
          * 🔴 **THE SIXTH CONTROL HAD ALREADY LANDED AND THIS COMMENT NEVER SAW
          * IT — corrected 2026-09-19, owner's decision.** *SUPERSEDED: "`w-56`
          * … tasks-and-notes, Send for signature, Convert, Edit and Delete is
          * FIVE icon buttons", and with it "Add a sixth control and this changes
          * again".* **The sixth is `DownloadPdfButton`**, mounted in this lane
          * and absent from that list, so the prediction had already fired when
          * it was read: the lane is activity · PDF · signature · Convert · Edit ·
          * Delete.
          *
          * ⚠️ THE ARITHMETIC, because it is the thing that silently drifts. An
          * icon-only `size="sm"` Button is `h-8 w-8` (32px) and the row is
          * `gap-1.5` (6px), inside a `TD`'s `px-3` (24px both sides):
          *
          *     4 buttons  4×32 + 3×6 + 24 = 170
          *     5 buttons  5×32 + 4×6 + 24 = 208
          *     6 buttons  6×32 + 5×6 + 24 = 246
          *     7 buttons  7×32 + 6×6 + 24 = 284   <- the lane today
          *
          * ⚠️ **2026-10-25: 246 -> 284 for the SEVENTH control, Preview PDF**
          * (`PreviewQuotePdfButton`, beside `DownloadPdfButton`). The lane is
          * activity · preview · PDF · signature · Convert · Edit · Delete. The
          * 246-era paragraphs below describe the same rule at six controls.
          *
          * ⚠️ **246 is an EXACT fit and lands on no Tailwind step** (`w-56` is
          * 224, the next is 256), which is why the class is an arbitrary value
          * rather than a scale token. *SUPERSEDED, the slack convention: "`w-52`
          * would be a zero-slack fit, so this takes `w-56` and keeps the ~16px
          * the four-button case had."* Slack bought nothing — the lane was 22px
          * SHORT of its worst case throughout, so what the ~16px actually did
          * was disguise how far under it had drifted.
          *
          * ⛔ **NOTHING CLIPPED WHILE IT WAS SHORT, and the reason is not this
          * table's layout mode.** `Button`'s base class carries `shrink-0`, so
          * the icon buttons are incompressible flex items and the cell's
          * min-content is exactly the sum above; the column could never settle
          * below it whatever the declaration said. **The safety is structural,
          * in the primitive.**
          *
          * ✅ **THE FLOOR FOLLOWS ON ITS OWN NOW.** *SUPERSEDED: "together with
          * BOTH `min-w-` literals below", and "the `min-w-` on both `<Table>`s
          * below is one literal that has to agree with this one".* There are no
          * live `min-w-` class literals left in this file — `tableMinWidth` sums
          * `width` over the visible keys and reaches the tables through `style`.
          * **So this is one number in one place.**
          *
          * It is a fixed width on purpose: a width that changed with the
          * signed-in role would make one page two widths for two people. A
          * viewer gets a column wider than its two buttons, which costs nothing;
          * a column narrower than its buttons squeezes every other column
          * instead.
          *
          * `pinned` holds it against the right edge of the scroller. This is the
          * widest table in the app at 1544px — against a content area of the
          * viewport minus the ~256px sidebar it overflows from 1800 down, so
          * unpinned this column was off-screen on every real monitor with
          * nothing signalling that the table scrolled sideways. Pinned in three
          * places: here, `TableSkeleton` and the body cell.
 *
 * ⚠️ **IT KEEPS `pinned` AND MUST NEVER ALSO TAKE `frozen`.** The two write
 * `box-shadow`, which is ONE property, so two `shadow-[…]` utilities on one
 * element do not merge — one wins, and which one is whatever order Tailwind
 * emitted them in. `frozenPropsFor` returns `{}` for this key for exactly that
 * reason, and the manager's freeze buttons on this row therefore change the
 * OFFSET ARITHMETIC of anything frozen to the right of it and nothing else.
 *
 * ⚠️ **AND IT IS CLAMPED TO THE END OF THE VISIBLE LIST.** `pinned` is
 * `sticky right-0` — it cannot take an offset — so it is only honest while it
 * really is the last column. The controller locks this key against being MOVED
 * but does not stop another column being moved PAST it, which would leave the
 * actions cell floating over a live column. `tableColumnKeys` in the page below
 * appends it rather than trusting its position.
 */

/**
 * `actionCount` rather than a boolean.
 *
 * This used to take `showActions` and draw one square plus a conditional second,
 * which was already a guess and is now wrong five ways: the cell holds between
 * two and five buttons depending on what this account may do. Draw exactly what
 * will arrive, or the row resizes under the reader as the data lands.
 */
function TableSkeleton({
  actionCount,
  visibleKeys,
  frozenSideOf,
  frozenOffsets,
}: {
  actionCount: number;
  visibleKeys: readonly string[];
  frozenSideOf: (key: string) => 'left' | 'right' | undefined;
  frozenOffsets: Record<string, number>;
}) {
  /*
   * ⚠️ **DRIVEN BY THE SAME KEY LIST AS THE HEADER AND THE BODY, which is the
   * whole reason this is a switch rather than a hardcoded row.** The skeleton
   * used to be nine literal `<TD>`s that had to be counted against the header by
   * hand, and its own comments recorded the failure that produces — "a cell
   * missing here is how the layout jumps a column over as the rows arrive". A
   * hidden column now drops out of all three lists or out of none.
   *
   * Each arm keeps the SHAPE of the real cell — a badge is `h-4`, a line of text
   * is `h-3` — because a skeleton of the wrong shape resizes the row under the
   * reader at exactly the moment the data lands.
   */
  function skeletonCell(key: string) {
    const frozen = frozenPropsFor(key, frozenSideOf, frozenOffsets);

    switch (key) {
      case 'subject':
        return (
          <TD key={key} {...frozen}>
            <div className="grid gap-1.5">
              <Skeleton className="h-3 w-48" />
              <Skeleton className="h-2.5 w-24" />
            </div>
          </TD>
        );
      case 'stageId':
        return (
          <TD key={key} {...frozen}>
            <Skeleton className="h-4 w-24" />
          </TD>
        );
      // The signature column, in the real shape — a badge, not a line of text.
      // Its own fetch is separate from the quote list's, but during the initial
      // load both are outstanding, so this stands in for both.
      case 'awaitingSignature':
        return (
          <TD key={key} {...frozen}>
            <Skeleton className="h-4 w-24" />
          </TD>
        );
      case 'issueDate':
        return (
          <TD key={key} {...frozen}>
            <Skeleton className="h-3 w-20" />
          </TD>
        );
      // Projects — a BADGE, like the two above it and unlike the dates either
      // side, so the skeleton stands in for the real shape rather than for a
      // line of text.
      case 'lineProjectStatus':
        return (
          <TD key={key} {...frozen}>
            <Skeleton className="h-4 w-24" />
          </TD>
        );
      // From Xero — a WORD, not a badge, so this is a short text line rather
      // than the `h-4` badge shape above it. The real cell renders "Yes" or
      // "No" and nothing else.
      case IMPORTED_FROM_XERO_COLUMN:
        return (
          <TD key={key} {...frozen}>
            <Skeleton className="h-3 w-8" />
          </TD>
        );
      case 'validTill':
        return (
          <TD key={key} {...frozen}>
            <Skeleton className="h-3 w-20" />
          </TD>
        );
      case 'grandTotal':
        return (
          <TD key={key} numeric {...frozen}>
            <Skeleton className="ml-auto h-3 w-24" />
          </TD>
        );
      /*
       * The Xero placeholder — a status badge over a button — stood before this
       * one until 2026-08-31 and went with the column it stood in for. A
       * skeleton cell outliving its column is how the layout jumps as the rows
       * arrive; there is now no way for one to, because there is no cell that is
       * not generated from a live key.
       */
      case ACTIONS_COLUMN:
        return (
          <TD key={key} numeric pinned>
            <div className="flex justify-end gap-1.5">
              {Array.from({ length: actionCount }, (_, n) => (
                <Skeleton key={n} className="h-6 w-6" />
              ))}
            </div>
          </TD>
        );
      default:
        return null;
    }
  }

  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>{visibleKeys.map((key) => skeletonCell(key))}</TR>
      ))}
    </TBody>
  );
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li
          key={i}
          className="flex items-start justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
        >
          <div className="grid flex-1 gap-1.5">
            <Skeleton className="h-3 w-44" />
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-2.5 w-36" />
          </div>
          <Skeleton className="h-4 w-20" />
        </li>
      ))}
    </ul>
  );
}

export function QuotesPage() {
  const { ability, user } = useAuth();

  /*
   * The read gate. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, the command palette offers the page, and a
   * permission set can change under an access token that lasts days — so the
   * page answers for itself.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. See `can()` in
   * `@/lib/permissions`, which carries the reasoning.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /quotes` answers the
   * same to everybody, so nothing here may be worded as though the data were
   * protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. The write and convert gates below are untouched.
   */
  const canReadModule = ability.canRead('quotes');

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * Saved filters, against the EMERGE account rather than this browser.
   *
   * ⚠️ **The return value is READ now, and that is not decoration.** It is the
   * sequencing signal for the Account lead default below: two writers put
   * filters into this URL on arrival, and the default has to wait for this one
   * and stand down when it applied anything — including when it deliberately
   * applied no lead. See `useDefaultAccountLeadFilter`.
   */
  const applySavedFilters = useSavedFilterApply<SavedQuotesKey>();
  const savedFilters = useSavedFilters({
    scope: 'quotes',
    keys: SAVED_FILTER_KEYS,
    independentKeys: SAVED_VIEW_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  const [result, setResult] = useState<Paginated<Quote> | null>(null);
  const [search, setSearch] = useRememberedState('search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /** "Show all" — remembered against the account. See `load`. */
  const [showAll, setShowAll] = useShowAll('quotes');
  /** The show-all read stopped at its cap; the pager says so. */
  const [showAllTruncated, setShowAllTruncated] = useState(false);

  const [formState, setFormState] = useState<QuoteFormRequest | null>(null);
  /**
   * Bumped on every successful save so the board refetches; see `handleSaved`.
   * The board compares it with the value it last saw, so the number itself is
   * never read and never has to mean anything.
   */
  const [boardRefresh, setBoardRefresh] = useState(0);
  /**
   * Bumped after a successful send so the ONE signature request re-runs and the
   * new envelope arrives from the server rather than only from our own optimism.
   * The number is never read — same shape as `listRefresh` and `boardRefresh`.
   *
   * ⚠️ It is a re-read, not a reset: the effect deliberately does not drop back
   * to `loading`, so the badges already on screen stay put while it is in flight.
   */
  const [signatureRefresh, setSignatureRefresh] = useState(0);
  /**
   * The row whose Send-for-signature dialog is open, held as the RECORD.
   *
   * `SendForSignatureModal` takes a whole `Quote` — it reads the company, the
   * contact, the grand total, `quoteNumber`, and the Xero link envelope through
   * `linkOf()` — all of which a list row already carries, so an id would mean
   * re-reading a quote this page has loaded.
   */
  const [sendingSignature, setSendingSignature] = useState<{
    quote: Quote;
    /**
     * 🔴 **THE WHOLE POINT OF "Change signer and resend", and it is discarded the
     * moment it is not carried.** That flow voids the live envelope and then
     * opens the send dialog, and this is the person it was voided for.
     *
     * ⚠️ **Absent is NOT the same as empty.** `undefined` lets `SignerField`
     * seed the company's primary contact, which is what an ordinary Send wants;
     * an empty `SignerValue` would suppress that seed. And on the change-signer
     * path the primary contact is very often the exact person somebody has just
     * decided not to send to — so a send opened without this looks like it
     * worked and silently undoes the operation.
     */
    initialSigner?: SignerValue;
  } | null>(null);
  /**
   * The row whose "Change signer and resend" dialog is open.
   *
   * The QUOTE as well as the envelope, because the dialog needs the quote's
   * company to scope the signer picker and the send that follows needs the whole
   * record. Held as one object so the two cannot come apart.
   */
  const [changingSignerFor, setChangingSignerFor] = useState<{
    quote: Quote;
    envelope: DocuSignEnvelope;
  } | null>(null);
  /**
   * The envelope a send just produced, while its single-use signing link is on
   * screen. Mounted only while this is non-null, so closing UNMOUNTS the dialog
   * and the minted URL dies with it — the link is never lifted into page state
   * and never put in the URL.
   */
  const [signingLinkFor, setSigningLinkFor] = useState<DocuSignEnvelope | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Quote | null>(null);
  /*
   * ⚠️ **`xeroReport` was declared here and went with the Xero column on
   * 2026-08-31.** It held the outcome of the last push raised from this page,
   * which the one-per-list banner rendered. With no push raised here there is
   * nothing to hold.
   *
   * ⚠️ **`listRefresh` below SURVIVES**, unlike its twin on `/invoices`, which was
   * removed in the same change. The difference is the number of writers: there
   * the push handler was the only one, here the signature path still bumps it.
   */
  /**
   * Bumped to make the list effect re-run, so a post-push refetch is carried by
   * the effect that already owns an `AbortController`. The number is never read.
   */
  const [listRefresh, setListRefresh] = useState(0);
  const [pendingLineCount, setPendingLineCount] = useState<number | null>(null);
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /*
   * Which row's tasks-and-notes sheet is open, held as the quote itself so the
   * sheet keeps its heading while it closes rather than flashing blank.
   *
   * Deliberately NOT `ActivityButton` per row, which is what the record pages
   * use: that control fetches its OWN counts, so a page of twenty rows would be
   * forty requests for badges nobody asked for — against a per-IP limit shared
   * across the office. `/tasks/summary` takes up to 200 ids in ONE
   * call precisely to avoid that, and if counts are ever wanted here that is the
   * way in: one request for the page, never one per row. Same decision, and the
   * same reasoning, as `CompaniesPage`.
   *
   * ⚠️ *SUPERSEDED: "a 120-per-60s per-IP limit."* It is **12,000/60s** (raised
   * 2026-09-04, corrected 2026-09-17), and every sentence below that cites a
   * 120 budget is wrong by the same factor. ⛔ THE ARGUMENT IS THE SHAPE AND
   * SURVIVES INTACT: a per-row lookup on a paginated list is still forbidden,
   * `/tasks/summary`'s batch is still the way in, and `CompaniesPage` has the
   * incident — a viewport loop's `429`s read as a dead session, redirecting
   * everybody to `/login`. Re-derive these refusals from the shape, never from
   * the arithmetic.
   */
  const [activityFor, setActivityFor] = useState<Quote | null>(null);

  /*
   * The quote the Convert form is open over. Held as the RECORD rather than an
   * id, and it has to be: `InvoiceFormModal` seeds nineteen header values off it
   * through `invoicePrefillFromQuote`, so an id alone would mean a second read
   * of a quote this page has already loaded.
   *
   * ⚠️ **The busy and error state that sat beside it is gone.** The request is
   * made inside the modal now, so that is where it reports — both 409s, the 404,
   * the 403 and the case where the conversion lands and the follow-up edit does
   * not. A copy here would be a banner over a page that made no call.
   */
  const [pendingConvert, setPendingConvert] = useState<Quote | null>(null);

  /*
   * ── CONVERT FROM A BOARD CARD ────────────────────────────────────────────
   *
   * The card being read into `pendingConvert`, and why the board needs a read
   * the table does not.
   *
   * The block above says holding an id would mean "a second read of a quote this
   * page has already loaded" — true of a TABLE row, which IS a `Quote`. A board
   * card is not: `KanbanCard` carries ten fields, and `convertFrom` seeds the
   * invoice form off nineteen. So the board path fetches the record once, on
   * click, and then joins the table's path exactly — same `pendingConvert`, same
   * modal, same `afterConvert`. ⚠️ **One state, not two**: a second convert path
   * is how one refusal ends up reported two ways, which is the hazard the
   * comment above `afterConvert` exists to prevent.
   *
   * It is ONE request, made only when somebody presses the button — not a
   * pre-check per card. Pre-resolving every card would be a request per card
   * against a 12,000-per-60s (⚠️ *not the 120 this once said*) limit shared
   * across the office — the shape, not the total — which is the same
   * reason the already-billed refusal is left to the 409.
   *
   * `cardConvertId` doubles as the busy flag and as WHICH card is busy, so the
   * spinner lands on the button that was pressed rather than on all of them.
   */
  const [cardConvertId, setCardConvertId] = useState<number | null>(null);
  /*
   * A failed read leaves NOTHING on screen without this. The modal is what
   * reports a refused conversion, and this failure happens before the modal
   * exists — so the button would simply stop spinning and nothing would open,
   * which reads as a dead control rather than as an error.
   */
  const [cardConvertError, setCardConvertError] = useState<string | null>(null);

  /*
   * The serialised query is the memo/effect dependency, never the parsed object:
   * the memo below returns a fresh object on every render, so keying the fetch
   * on it would refetch forever. A string compares by value.
   */
  const queryString = searchParams.toString();

  const filters = useMemo(() => {
    const current = new URLSearchParams(queryString);
    const ids = {} as Record<SingleIdFilter, number | null>;
    for (const key of SINGLE_ID_FILTERS) ids[key] = parseId(current.get(key));
    return {
      ...ids,
      /*
       * ⚠️ **A `number[]`, where every id beside it is a `number | null`** — the
       * one multi-valued filter on this page. It is `[]` and never `null` when
       * nothing is chosen, so the test is `length`: a `!== null` copied from a
       * neighbouring line would be true for an empty array and would report a
       * filter nobody set. Every reader below says `length` for that reason.
       */
      lineProjectId: parseIdList(current.get('lineProjectId')),
      // `[]` when nothing is chosen — test `length`, as for `lineProjectId`.
      stageId: parseIdList(current.get('stageId')),
      currency: parseCurrency(current.get('currency')),
      validTillBefore: parseIsoDate(current.get('validTillBefore')),
      validTillAfter: parseIsoDate(current.get('validTillAfter')),
      /*
       * ⚠️ A quote with no `issueDate` matches NEITHER of these, so the pair
       * does not partition the list — the same property `validTill` has two
       * lines up, and the reason the expiry control carries a hint about it.
       *
       * No hint here, and that is a measurement rather than an oversight:
       * **0 of 339 live quotes are undated** (2026-09-02), so the sentence would
       * describe a row nobody can produce. Copy for an unreachable state is
       * trusted without ever being tested — and it would sit beside the expiry
       * hint, where a reader would take two warnings for two different problems.
       */
      issueDateBefore: parseIsoDate(current.get('issueDateBefore')),
      issueDateAfter: parseIsoDate(current.get('issueDateAfter')),
    };
  }, [queryString]);

  const expiryPreset = presetFor(filters.validTillBefore, filters.validTillAfter);
  const expiryFiltered = filters.validTillBefore !== null || filters.validTillAfter !== null;

  /**
   * The expiry control's vocabulary: three fixed presets, plus `custom` when —
   * and only when — the URL's bounds match none of them.
   *
   * 🔴 **`custom` IS A PINNED OPTION, NOT A CONDITIONAL ROW, AND WITHOUT IT THE
   * CONTROL MISREPORTS ITS OWN STATE.** A native `<select>` handed a value no
   * `<option>` carries shows it anyway; `Combobox` falls back to its
   * PLACEHOLDER. So a hand-written or stale link — one built yesterday, whose
   * "yesterday" has since moved — would leave this reading "All quotes" over a
   * list genuinely narrowed by two `validTill` bounds: applied, invisible, and
   * with no row to move off. The same pin-back the stage, currency and project
   * controls on this bank already do for an id their master does not carry.
   *
   * ⚠️ **It is pinned only while it is the live state, which is the one
   * difference from those.** `custom` is not a filter somebody can CHOOSE — it
   * is the name for bounds no preset describes, and `setExpiry` returns early on
   * it rather than writing anything. Offering it unconditionally would be a row
   * whose only outcome is nothing, which is the affordance this codebase refuses
   * everywhere else. Picking any other row replaces it and it disappears.
   *
   * ⚠️ **LAST, not first.** The pin-back convention puts an injected value at
   * the TOP because there it cannot be mistaken for a row the master returned.
   * Here it is the opposite: these three are a fixed, ordered vocabulary that
   * reads "all, then narrower", and `custom` is the state you leave rather than
   * the one you reach for. It is already selected whenever it exists, so it
   * needs no discoverability at all.
   */
  const expiryOptions = useMemo<ComboboxOption[]>(() => {
    const options: ComboboxOption[] = [
      { value: 'all', label: 'All quotes' },
      { value: 'expired', label: 'Expired' },
      { value: 'soon', label: `Expiring in ${EXPIRY_WINDOW_DAYS} days` },
    ];
    if (expiryPreset === 'custom') options.push({ value: 'custom', label: 'Custom date range' });
    return options;
  }, [expiryPreset]);

  // Anything that is not exactly 'board' is the table — see the note above.
  const view: QuotesView = searchParams.get('view') === 'board' ? 'board' : 'table';
  const showBoard = view === 'board';

  /**
   * Who is waiting on a signature, for every quote at once. See the block above
   * `OUTSTANDING_PER_PAGE` — this is ONE request for the page, not one per row,
   * and it does not depend on the filters, the search term or the page number.
   *
   * ⚠️ Declared HERE rather than beside the other `useState`s above, because it
   * reads `showBoard` and a hook cannot be called before the value it depends
   * on. It is still unconditional and still above every early return, which is
   * what the rules of hooks actually require.
   */
  const [signatures, setSignatures] = useOutstandingEnvelopes(!showBoard, signatureRefresh);

  /*
   * Declared HERE, above `load`, rather than beside `total` and `totalPages`
   * further down: `load` reads the sort out of this hook, and a hook cannot be
   * called after the callback that depends on it.
   *
   * `table` owns two things — the page-scoped column funnel and the sort — and
   * it is the ONLY sort state on this page. A second `useState` pair would let a
   * header and a request disagree about the order the rows are in, which renders
   * perfectly and is wrong.
   */
  const rows = result?.data ?? [];
  const table = useTableFilters(rows, QUOTE_COLUMNS);
  const visibleRows = table.visibleRows;
  const hiddenByColumns = rows.length - visibleRows.length;
  /*
   * `sort` is state inside the hook, so its identity is stable and listing it in
   * `load`'s deps cannot loop.
   */
  const sort = table.sort;

  /*
   * The SERVER-scoped columns' params, typed against the list contract.
   *
   * ⚠️ **RE-PARSED RATHER THAN TRUSTED**, like every other value this page puts
   * on the wire. `serverParams` is `Record<string, string>`, so without the
   * membership check a value from stale state would reach a `.strict()` enum and
   * 422 the whole list — which renders as an empty table, not as a bad filter.
   *
   * ⚠️ *SUPERSEDED 2026-10-28: `lineProjectStatus` is `multi` and arrives as a
   * CSV, validated member by member below. `importedFromXero` (a boolean)
   * stays single.* **A single selection only.** `useTableFilters` sends nothing for a
   * non-`multi` column with several ticked and falls back to narrowing the
   * loaded page, so each key here is either absent or one value; this returns
   * `{}` for the former and the page-scoped fallback does the rest. That is the
   * honest behaviour — `?lineProjectStatus=none,some` is a 422 on a strict enum,
   * not an OR, and comma-separated multi-value filters are companies-only across
   * this whole API.
   *
   * ⚠️ **`stageId` IS NOT READ HERE, THOUGH ITS COLUMN IS `scope: 'server'` AND
   * DECLARES THAT PARAM.** Its selection lives in the URL with the other id
   * filters (see the Stage entry in `QUOTE_COLUMNS` and `stageFilterProps`), and
   * `load` picks it up from the query string. Adding it to this `Pick` would put
   * the same param on the request twice from two places, which is the second
   * source of truth the URL interception exists to prevent. The `Pick` is by
   * NAME rather than a spread of `serverParams` precisely so that cannot happen
   * by accident.
   *
   * ⚠️ **`importedFromXero` CROSSES A TYPE BOUNDARY, so it is the one key here
   * that is not forwarded as it stands.** `serverParams` is all strings and
   * `QuoteListParams.importedFromXero` is a `boolean`, so `'true'` becomes
   * `true` and `'false'` becomes `false` at this one point. Anything else is
   * dropped rather than coerced: `Boolean('anything')` is `true`, so a coercion
   * would turn a stale value into a confident wrong filter.
   *
   * ⚠️ **`false` IS SENT, NOT TREATED AS "no filter".** `listQuery` drops only
   * `undefined`, `null` and `''`, so `?importedFromXero=false` goes out and
   * genuinely narrows to the quotes created in EMERGE. Omitting the key and
   * sending `false` are different requests, which is NOT true of the
   * `awaitingSignature` boolean beside it on that schema.
   *
   * ⚠️ **Matched against the module's own option values, never against the
   * literals `'true'` and `'false'` spelled here.** A third copy of the
   * vocabulary is what that module exists to prevent, and a value changed there
   * would silently stop matching while both sides still read correctly.
   *
   * `serverParams` keeps a stable identity while unchanged, so this memo and the
   * fetch depending on it settle rather than loop.
   */
  const { serverParams } = table;
  const listFilters = useMemo<
    Pick<QuoteListParams, 'lineProjectStatus' | 'importedFromXero'>
  >(() => {
    const next: Pick<QuoteListParams, 'lineProjectStatus' | 'importedFromXero'> = {};
    /*
     * A CSV since 2026-10-28 (the column is `multi`). Each member is checked
     * against the declared options and an unknown one is dropped, so a stale
     * value can never reach the strict enum.
     */
    const rawProjects = serverParams.lineProjectStatus;
    if (rawProjects !== undefined) {
      const members = [
        ...new Set(
          rawProjects
            .split(',')
            .filter((member) =>
              LINE_PROJECT_FILTER_OPTIONS.some((option) => option.value === member),
            ),
        ),
      ];
      if (members.length > 0) next.lineProjectStatus = members.join(',');
    }
    const rawImported = serverParams[IMPORTED_FROM_XERO_COLUMN];
    const [importedYes, importedNo] = IMPORTED_FROM_XERO_FILTER_OPTIONS;
    if (rawImported !== undefined && rawImported === importedYes?.value) {
      next.importedFromXero = true;
    } else if (rawImported !== undefined && rawImported === importedNo?.value) {
      next.importedFromXero = false;
    }
    return next;
  }, [serverParams]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const params: QuoteListParams = { page, perPage: PER_PAGE };
      // Every key is written only when it has a value. An empty string is a 422
      // on this endpoint rather than "no filter", so `?stageId=` and
      // `?validTillBefore=` must never be sent.
      if (search) params.search = search;
      const current = new URLSearchParams(queryString);
      for (const key of SINGLE_ID_FILTERS) {
        const value = parseId(current.get(key));
        if (value !== null) params[key] = value;
      }
      /*
       * The one multi-valued id filter, re-parsed out of the query string like
       * everything else here rather than read off `filters` — a hand-pasted URL
       * never came through the picker.
       *
       * ⚠️ **AN EMPTY SELECTION OMITS THE KEY ENTIRELY.** `[].join(',')` is
       * `''`, and `?lineProjectId=` is a **422** on this `.strict()` schema, not
       * "no filter" — so the guard is on `length` and there is no path that puts
       * an empty string on `params`. `listQuery` drops `''` as a second line of
       * defence, but a request that never carries the key cannot depend on that.
       *
       * ⚠️ **Sent as a STRING even for one id**, which is what `IdListFilter`
       * (`number | string`) is for. `"7"` and `7` are the same value to the
       * server, so a link naming a single project answers exactly as it did
       * before this control became multi — the property that let it change
       * without a flag day.
       */
      const lineProjectIds = parseIdList(current.get('lineProjectId'));
      if (lineProjectIds.length > 0) params.lineProjectId = lineProjectIds.join(',');
      const stageIds = parseIdList(current.get('stageId'));
      if (stageIds.length > 0) params.stageId = stageIds.join(',');
      const currency = parseCurrency(current.get('currency'));
      if (currency !== null) params.currency = currency;
      const before = parseIsoDate(current.get('validTillBefore'));
      if (before !== null) params.validTillBefore = before;
      const after = parseIsoDate(current.get('validTillAfter'));
      if (after !== null) params.validTillAfter = after;
      const issuedBefore = parseIsoDate(current.get('issueDateBefore'));
      if (issuedBefore !== null) params.issueDateBefore = issuedBefore;
      const issuedAfter = parseIsoDate(current.get('issueDateAfter'));
      if (issuedAfter !== null) params.issueDateAfter = issuedAfter;
      /*
       * The header funnel's server-scoped selection. Merged as an OBJECT that is
       * either empty or holds exactly one validated key, so there is no path
       * that sends `lineProjectStatus=''` — an empty string is a 422 on a strict
       * enum, not "no filter".
       *
       * ⚠️ It lives in the hook's state rather than in the URL, unlike the id
       * filters above. So it is NOT shareable in a link. It IS remembered on the
       * account with the header sort (`useTableFilters` →
       * `useRememberedTableState`, owner 2026-10-16/10-23); *SUPERSEDED: "NOT
       * saved against the account, and it resets on reload"* — the same trade every server-scoped
       * funnel in this app already makes (`stage` on `/companies`, `accountLead`
       * on `/deals`, `role` on `/users`). Moving it into the URL means making
       * `useTableFilters` a controlled component, which is a change to the
       * primitive and to five other pages, not a key added here.
       */
      Object.assign(params, listFilters);

      /*
       * ⚠️ **Both keys, or neither.** `order` alone is meaningless and `sort`
       * alone would take the endpoint's own direction, so they are written in
       * one branch and there is no path that emits half a pair.
       *
       * Sorting NOTHING is what asks for the default (`id DESC`) — never
       * `sort=id&order=desc`, which is the same answer spelled out at the cost
       * of a redundant pair in the request. `isQuoteSort` is what keeps a
       * `sortKey` that is not a real member from reaching a `.strict()` schema.
       */
      if (sort && isQuoteSort(sort.key)) {
        params.sort = sort.key;
        params.order = sort.direction;
      }

      /*
       * Show all: the same params, every page of them, 100 at a time. The page
       * number is ignored (and left in state untouched) while it is on, and
       * `fetchAllPages` throws on any failed page, so a short list is never
       * rendered as the whole one. A bare `load()` (after a save) has no signal
       * of its own, so it gets one nothing aborts — same as the paged path.
       */
      const request: Promise<Paginated<Quote>> = showAll
        ? fetchAllPages<Quote>(
            (pageNumber, perPage, pageSignal) =>
              api.listQuotes({ ...params, page: pageNumber, perPage }, pageSignal),
            signal ?? new AbortController().signal,
          ).then(({ rows: all, total: allTotal, truncated }) => {
            setShowAllTruncated(truncated);
            return {
              data: all,
              meta: { page: 1, perPage: all.length, total: allTotal, totalPages: 1 },
            };
          })
        : api.listQuotes(params, signal).then((data) => {
            setShowAllTruncated(false);
            return data;
          });

      return request
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load quotes');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, queryString, sort, listFilters, showAll],
  );

  // Debounced so typing in the search box doesn't fire a request per keystroke;
  // the controller aborts the in-flight one when the term moves on.
  //
  // Skipped entirely while the board is showing. The rate limit is **per IP**,
  // shared by everyone behind one office NAT — 12,000 requests per 60 seconds
  // (⚠️ not the 120 this once said) — so a
  // list request for a table nobody can see is not a harmless extra — it is
  // spent out of a budget colleagues are also drawing on. `KanbanBoard` is held
  // to the same rule from the other side via `enabled`.
  useEffect(() => {
    if (showBoard) return;
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `listRefresh` is a re-run token, never read — see where it is declared.
  }, [load, showBoard, listRefresh]);

  const stages = useQuoteStageOptions();
  const currencies = useCurrencyOptions();
  /*
   * The active projects, for the Project picker in the filter bank.
   *
   * ⚠️ **NOT gated on the table view.** `useProjectOptions` fetches once per
   * mount, so leaving it ungated costs one request against the 12,000/60s
   * (⚠️ *not the 120/60s this once said*) per-IP
   * bucket; gating it on `!showBoard` would REFETCH on every toggle back, which
   * is the more expensive of the two. The control renders in both views — the
   * board simply says it is not narrowed by it.
   */
  const projects = useProjectOptions();
  /*
   * ── The Account lead filter starts on the SIGNED-IN PERSON ────────────────
   *
   * Owner's instruction, 2026-09-12: a bare `/quotes` shows the quotes attached
   * to whoever is looking. Same mechanism, same module and same refusals as
   * `/deals` — `useDefaultAccountLeadFilter` carries all of the reasoning.
   *
   * ⚠️ *SUPERSEDED: "**`useUserOptions()` was called here for the "All owners"
   * filter and the call went with it on 2026-08-31.** Nothing on this page reads
   * the staff directory now, so a page load makes one fewer request against a
   * 120/60s per-IP bucket shared across the office."* ⚠️ *That quotation is
   * left verbatim and its figure was wrong twice over: the bucket is
   * **12,000/60s** (corrected 2026-09-17).* **The saving is spent
   * again, and on purpose** — two requests, `/users` and `/user-groups`, are the
   * whole cost of the default, and they are the ONLY evidence that can say
   * whether this person may be named as an account lead at all. Seeding without
   * them would be guessing. It is gated on `canReadModule`, so a reader who
   * cannot see the page pays nothing.
   *
   * ⛔ **The directory is evidence, NOT options.** This page still renders no
   * staff control of any kind; `accountLeadId` is arrived at by link and shown
   * as a badge in the "Also filtered by" bank, cleared by the Clear button
   * beside it. **That badge is the whole affordance**, which is thinner than
   * `/deals`' funnel, and is the thing to revisit if this ever needs a control
   * of its own — not this call.
   *
   * ⚠️ **`setPage(1)` is this page's half of the bargain.** `page` is component
   * state rather than a URL key here, so the hook cannot reset it; a seed IS a
   * filter change, and the hook waits on a preferences read that can take
   * seconds, so a reader may already have paged onto a slice of a different
   * result set.
   *
   * ⚠️ **oxlint says NOTHING about this one, and that is an artefact of where
   * the call sits rather than a verdict on it.** `react(set-state-in-effect)`
   * fires on a `setPage` written straight into an effect — it does on several of
   * this page's other resets — and does not fire on one passed as a callback.
   * Measured 2026-09-12. **A reader who deletes this because nothing complains
   * removes the guard, not the noise.**
   */
  const staff = useUserOptions(canReadModule, true);
  useDefaultAccountLeadFilter({
    user,
    staff,
    savedFilters,
    enabled: canReadModule,
    onSeeded: () => setPage(1),
  });

  /**
   * The stage vocabulary BOTH stage controls render — the "All stages" Combobox
   * in the filter bank and the Stage funnel on the header. One derivation, so
   * two controls over one URL param can never offer different stages.
   *
   * 🔴 **RETIRED STAGES ARE OFFERED, AND LABELLED.** `useQuoteStageOptions`
   * already fetches without `isActive` (its own note says why), so the rows were
   * always here — what was missing is the LABEL, so a retired stage read exactly
   * like a live one. A filter is not a create picker: reads never narrow on
   * `isActive`, so a live quote can hold a retired stage and this is the only
   * control that can find it. `selectableStages` is deliberately NOT used —
   * it FILTERS to active rows, which is right for a form and wrong here, and the
   * `(retired)` wording is repeated rather than borrowed from it for that reason.
   *
   * ⚠️ **The id in the URL is injected when the master does not carry it**, so a
   * deep link into a stage the master no longer returns still renders — as a
   * ticked checkbox that can be UNticked in the funnel, and as a chip rather
   * than a raw value in the Combobox. `#N` is the honest label: the name is not
   * in hand, and inventing one would be a claim about a row we did not read.
   *
   * ⚠️ **A FAILED master read leaves this EMPTY, and the funnel must not present
   * that as a closed set** — `headerFor` degrades to the hook's accumulated
   * values instead. See the block there; the Combobox has no such fallback and
   * reports the failure with `invalid` and its own words.
   *
   * Master order is the curated picker order (`sortOrder`, then `name`) and is
   * used as received — `use-quote-options` says not to re-sort it.
   */
  const stageOptions = useMemo<ComboboxOption[]>(() => {
    const options = stages.stages.map((stage) => ({
      value: String(stage.id),
      label: stage.isActive ? stage.name : `${stage.name} (retired)`,
    }));
    const injected: ComboboxOption[] = [];
    for (const id of filters.stageId) {
      const chosen = String(id);
      if (!options.some((option) => option.value === chosen)) {
        injected.push({ value: chosen, label: `#${chosen}` });
      }
    }
    return [...injected, ...options];
  }, [stages.stages, filters.stageId]);
  /*
   * The value is the three-letter CODE, not an id — a quote stores `currency`
   * as CHAR(3) with no foreign key to the currency table.
   *
   * 🔴 **ACTIVE-ONLY IS A DELIBERATE DEPARTURE FROM THE RULE THE STAGE CONTROLS
   * FOLLOW, AND THE SECOND ARGUMENT IS WHAT PAYS FOR IT.** Stage offers retired
   * rows because reads never narrow on `isActive`. `useCurrencyOptions` sends
   * `isActive: true` and cannot be told otherwise from here — and that is the
   * right list anyway: **161 of the 164 currency rows were retired on
   * 2026-08-31**, so the unfiltered master is a checkbox list of 164 codes no
   * live document uses, which is its own way of burying the three that matter.
   *
   * The hole that leaves is one code: a quote genuinely billed in a retired
   * currency. *SUPERSEDED: `null` as the second argument.* Passing the URL's own
   * code pins it back — `currencyOptions` prepends it labelled "No longer
   * offered" — so a deep link into a retired currency renders as itself and can
   * be cleared, rather than leaving the control showing an unmatched raw value.
   * It does NOT make such a currency discoverable from the list; that is the
   * accepted cost, stated rather than hidden.
   */
  const currencyPickerOptions = useMemo(
    () => currencyOptions(currencies.currencies, filters.currency),
    [currencies.currencies, filters.currency],
  );
  /*
   * The Project picker's options: the active projects, plus the id in the URL
   * when it is not among them.
   *
   * ⚠️ **THE INJECTED VALUE CARRIES NO NAME, AND `#N` IS THE HONEST LABEL.**
   * Unlike `projectName` further down — which reads the HEADER project off a
   * loaded row — there is nothing here to read a LINE's project name from: a
   * list response carries no line items. So a project outside the active list
   * renders as `#12`, the same idiom the "Also filtered by" badge already uses
   * for an id it cannot name.
   *
   * ⚠️ **Injected rather than left unmatched, deliberately.** A `MultiCombobox`
   * handed a value no option carries renders the raw VALUE as the chip — so an
   * id outside the active list would read as a bare `12` beside real project
   * names, and with `maxVisibleChips` reached it would vanish into a count
   * entirely. Either way the trigger describes a narrower list than it claims,
   * which is exactly the silent misreport the retired-value convention exists to
   * prevent.
   *
   * ⚠️ **SEVERAL ids are injected now, and each goes through `projectOptions()`
   * so the labelling rule stays in ONE place.** The helper takes a single
   * stored value, so this folds over the chosen ids and keeps the row it
   * prepends. Writing the `#N` / "(no longer offered)" label out again here
   * would be a second copy of a rule four other forms already share, and the two
   * would drift on the first correction to either.
   *
   * 🔴 **`projectOptions()` LABELS AN INJECTED VALUE "(no longer offered)", AND
   * THAT CLAIM IS FALSE TODAY — THE FORECAST BELOW FIRED.** Absent from the
   * active list means retired OR merely past the first page, and the helper
   * cannot tell the two apart.
   *
   * ⚠️ *SUPERSEDED, and it was written in the future tense for a state that has
   * since arrived: "It is correct today — 31 projects against a `perPage` of
   * 100, so `hasMore` is false and absent really does mean retired — and it
   * becomes a false statement at the 101st project, with nothing failing."*
   * **Measured 2026-09-12: 113 ACTIVE projects.** So `hasMore` IS true, the
   * hundred-and-first project exists, and a project sitting on page 2 is being
   * labelled "(no longer offered)" right now — a live mislabelling, not a
   * hazard in waiting. Nothing failed when it crossed over, which is exactly
   * what the superseded sentence predicted about itself.
   *
   * **Pre-existing in the shared helper and true of every consumer of it, not
   * introduced here**; the footnote on the control below is what keeps the
   * truncation visible. ⚠️ Going multi makes it reachable more often — a person
   * can now hold several ids at once, so several chips can carry the wrong
   * reason — but it is the SAME wrong reason, in the same shared helper, and
   * fixing it there is a change to five forms rather than to this one.
   * **Deliberately left as it is and reported rather than patched here.**
   */
  const projectPickerOptions = useMemo<MultiComboboxOption[]>(() => {
    const base = projectOptions(projects.projects, null);
    const seen = new Set(base.map((option) => option.value));
    const injected: MultiComboboxOption[] = [];
    for (const id of filters.lineProjectId) {
      const value = String(id);
      if (seen.has(value)) continue;
      /*
       * `projectOptions` PREPENDS the stored value when it is missing and
       * returns the list untouched when it is not — and the `seen` check above
       * has already established it is missing, so index 0 is the injected row.
       * Called per id rather than once because the helper's signature takes one
       * stored value; the alternative is a label rule written twice.
       */
      const [row] = projectOptions(projects.projects, { id, name: '' });
      if (row) {
        injected.push({ value: row.value, label: row.label });
        seen.add(row.value);
      }
    }
    return [...injected, ...base.map(({ value, label }) => ({ value, label }))];
  }, [projects.projects, filters.lineProjectId]);
  /*
   * WHAT THIS PAGE MAY OFFER.
   *
   * This used to be one `isAdminTier(user)` covering New, Edit and Delete
   * alike, which was wrong for two of the three: `POST /quotes` and
   * `PATCH /quotes/:id` are both `requireRole(...WRITE_ROLES)`, so a
   * `basic_user` and an `advanced_user` may create and edit quotes and were
   * being shown a read-only screen for work they are employed to do. DELETE is
   * genuinely admin-tier — every DELETE in the API is.
   *
   * `PATCH /quotes/:id/stage`, which is what a drag on the board calls, is also
   * WRITE_ROLES — so `canMove` is `canUpdate`, not `isAdmin`.
   *
   * ⚠️ CONVERT NOW SHIPS ON THIS PAGE. This block used to say it deliberately
   * did not; that is no longer true, and the reasoning it gave was sound but
   * incomplete, so it is recorded here rather than deleted.
   *
   * It is `ability.canConvertQuote()` and NOT an admin check —
   * `POST /quotes/:id/convert` takes WRITE_ROLES (quotes.routes.ts:66). The
   * handoff note claiming an `advanced_user` gets a 403 there is wrong; do not
   * re-tighten this to `isAdmin` on the strength of it.
   *
   * THREE things decide whether a convert can work, and this page can answer
   * only two of them from a list row:
   *
   *  1. the ROLE — `canConvertQuote()`, in hand, gates whether the button
   *     renders at all;
   *  2. `billingMode === 'staged'` — a required field on `Quote`, so it is in
   *     hand on every row. A staged quote is billed one line item at a time
   *     through `POST /quotes/{id}/invoices` and convert is refused outright
   *     (`409 QUOTE_IS_STAGED`), for ever. The button renders DISABLED with
   *     `STAGED_CONVERT_REASON` as its title, exactly as on the detail page and
   *     from the same string;
   *  3. ⚠️ HAS THIS QUOTE ALREADY BEEN BILLED — which this page CANNOT answer.
   *     `/quotes` returns no invoice count, so knowing costs one
   *     `GET /invoices?quoteId=` PER ROW: twenty requests to grey out a button,
   *     against a 12,000-per-60s (⚠️ *not the 120 this once said*) per-IP limit
   *     shared across the office — the per-row SHAPE is the objection — on every
   *     page of a list most readers are only scrolling.
   *
   * So (3) is NOT pre-checked here, and the 409 is the guarantee instead. That
   * is a deliberate difference from `QuoteDetailPage`, which loads the invoices
   * it needs anyway and can therefore disable the button up front. The cost is
   * that someone can press Convert on an already-billed quote and be refused;
   * the confirmation says so before they do, so the refusal is a stated
   * possibility rather than a surprise. Nothing is created by a refused
   * convert — the 409 happens before any invoice is raised.
   *
   * ⚠️ The 409 branches are matched on `error.code`, never on the message, with
   * `QUOTE_IS_STAGED` checked FIRST so the generic branch cannot swallow it: the
   * two mean opposite things, and rendering them alike would tell someone their
   * staged quote had produced an invoice it never produced. An UNRECOGNISED code
   * falls through to the server's own message, deliberately — see
   * `convertRefusal` in `InvoiceFormModal`, which is where the request is made
   * from now and therefore where every one of those branches lives.
   *
   * Never `user.role` directly: `permissions.ts` reconciles the role with
   * `/permissions/me`, and the two disagree in both directions.
   */
  const canCreate = ability.canCreate('quotes');
  const canUpdate = ability.canUpdate('quotes');
  const canDelete = ability.canDelete('quotes');
  const canConvert = ability.canConvertQuote();
  const showRowActions = canUpdate || canDelete;

  /*
   * How many controls the Actions cell will actually hold, for the skeleton to
   * draw. The `2 +` is tasks-and-notes and Send for signature, BOTH of which
   * always render — tasks-and-notes because the task and note routers carry no
   * role guard, Send because it states its own reason when it is unavailable
   * rather than vanishing. Convert counts whenever the ROLE allows it, disabled
   * or not: a disabled button occupies a square exactly like an enabled one.
   *
   * Unlike `CompaniesPage`, this does NOT size the column — that is a fixed
   * 246px on the header, because a width varying with the signed-in role would
   * make one page two widths for two people.
   *
   * ⚠️ *SUPERSEDED: "a fixed `w-56`", and "because the `min-w-` on both
   * `<Table>`s is one literal that would otherwise have to vary with the signed-
   * in role".* The width moved on 2026-09-19, and **there are no `min-w-` class
   * literals left in this file at all** — `tableMinWidth` sums the declared
   * widths and reaches the tables through `style`, so the floor follows the
   * declaration on its own. The reason the width is fixed survives; the
   * mechanism it cited does not.
   *
   * 🔴 **THIS COUNT IS ONE SHORT AND IS DELIBERATELY NOT FIXED HERE.** It maxes
   * at FIVE and **omits `DownloadPdfButton`**, which renders in the same lane on
   * any Xero-linked quote. It is contained — the count drives the SKELETON and
   * nothing else, and the column is declared at the six-control worst case, so
   * an under-drawn skeleton can no longer jump the column when the rows arrive.
   * What is left is a cosmetic mismatch in the placeholder count. **Widening it
   * means teaching the skeleton the PDF button's own link-row condition**, which
   * is a read the skeleton does not have.
   */
  // `3 +` since 2026-10-25: Preview PDF renders on every row this page shows,
  // because its only gate is `quotes.read` — the page's own read gate.
  const actionCount = 3 + (canConvert ? 1 : 0) + (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);

  /*
   * ── WHY SEND FOR SIGNATURE IS UNAVAILABLE ON THIS ROW, or `null` ─────────
   *
   * THE GATE IS `ability.canUpdate('quotes')`. Both send endpoints are
   * `requireRole(...WRITE_ROLES)`, which is the same question `canUpdate`
   * answers, and never `user.role` at a call site — `permissions.ts` reconciles
   * the role with `/permissions/me`, and the two disagree in both directions.
   *
   * ⚠️ **DISABLED WITH THE REASON, NOT HIDDEN**, which is the opposite of what
   * Edit and Delete do two lines below in the same cell — and deliberately so.
   * They are ordinary CRUD a reader can infer the absence of; this is the only
   * route to a customer signature, and a control that is simply not there is
   * indistinguishable from a broken page. The action is real and the refusal is
   * about THIS READER, so it is stated. (Contrast the Xero `sync` rule, where
   * the affordance must be ABSENT — there the only outcome is an error for
   * everybody, which is a different question with a different answer.)
   *
   * ⚠️ **The gate is the optimisation; the server is the guarantee.** A role can
   * be taken away under a live access token, which lasts about five days, so a
   * 403 is still reachable — and it is handled where the request is made, in
   * `SendForSignatureModal`, as a form-level banner (a 403 carries no `details`,
   * so it can never be a field error). Nothing is duplicated here.
   *
   * ⚠️ **The outstanding-envelope refusal is read ONLY from a `ready` state.**
   * `loading`, `error`, `unavailable` and a `ready` state with `unfetched > 0`
   * all mean *we do not know*, and this page's rule is to ENABLE when in doubt:
   * a wrongly-enabled Send costs one 409 that the modal renders in the server's
   * own words, while a wrongly-disabled one blocks a real send behind a sentence
   * asserting an envelope that may not exist. `unfetched` is not consulted
   * because it only ever makes a `has()` MISS unreliable, and a miss enables.
   *
   * The sentence itself is imported, never retyped: the quote's own
   * `EnvelopePanel` disables its Send on the same fact, and two surfaces wording
   * one server behaviour is two things to drift.
   */
  function sendSignatureDenial(quote: Quote): string | null {
    if (!canSend) {
      return (
        denialReason(ability, 'send', 'quotes') ??
        'You do not have permission to send a quote for signature.'
      );
    }
    if (signatures.kind === 'ready' && signatures.byQuoteId.has(quote.id)) {
      return ONE_ENVELOPE_PER_QUOTE_REASON;
    }
    return null;
  }

  /*
   * What a send has to leave behind, in both places it can happen.
   *
   * TWO steps, and neither replaces the other. The optimistic fold makes the row
   * correct at once — its badge appears and its Send button disables, so nobody
   * presses it twice into a 409 while a request is in flight. The re-read is what
   * makes it TRUE: it is the server's answer, and it also repairs the case the
   * fold cannot touch, where the state was not `ready` and was rightly left alone.
   *
   * ⚠️ The quote LIST is deliberately not refetched. A send changes no field on
   * the quote — the stage move on send is being added server-side and is not
   * this app's to predict, and a signature that completes later moves the stage
   * with nobody here at all. Refetching would spend a request out of a
   * 12,000-per-60s (⚠️ *not the 120 this once said*) office budget to redraw
   * identical rows.
   */
  function afterSignatureSent(envelope: DocuSignEnvelope) {
    setSendingSignature(null);
    setSignatures((current) => recordEnvelope(current, envelope));
    setSignatureRefresh((token) => token + 1);
    /*
     * ⚠️ **On `embedded` delivery DocuSign emails NOBODY** — and `embedded` is
     * the default the dialog opens on. The link is ours to hand over, so a send
     * that ends with the dialog closed and nothing else on screen leaves an
     * envelope outstanding that the signer has never been told about, with the
     * sender having no reason to think a further step exists.
     *
     * `isEmbeddedDelivery` is the shared narrowing rather than an
     * `=== 'embedded'` written here; it is also what the signing-URL call
     * demands, so this cannot route an emailed envelope into a mint.
     */
    if (isEmbeddedDelivery(envelope)) setSigningLinkFor(envelope);
  }

  /**
   * The TRUE reason a signature write is refused, from the ability model.
   *
   * Read once and handed to every control that needs it, rather than
   * "administrators only" — which is wrong for a `viewer` and misleading for a
   * basic user. `QuoteDetailPage` passes the identical expression into
   * `EnvelopePanel`, so the two surfaces cannot word one refusal two ways.
   */
  // 2026-10-02 (owner): every DocuSign action is `quotes.send`, not `update`.
  const canSend = ability.canSend('quotes');
  const signatureManageDenial = denialReason(ability, 'send', 'quotes');

  /*
   * A reminder came back. The envelope is the same request with `lastRemindedAt`
   * moved, so the row is replaced rather than dropped — nothing about what is
   * outstanding has changed.
   *
   * ⚠️ **No re-read is fired, deliberately.** A reminder changes one timestamp
   * this page does not render, so a `signatureRefresh` here would spend a request
   * out of a 12,000-per-60s (⚠️ *not the 120 this once said*) office budget to
   * redraw an identical row. The control reports its own outcome where it was
   * pressed.
   */
  function afterReminded(envelope: DocuSignEnvelope) {
    setSignatures((current) => recordEnvelope(current, envelope));
  }

  /*
   * "Change signer and resend", after the VOID has already happened.
   *
   * ⚠️ **Two steps, and the second is the whole point of the first.** The voided
   * envelope leaves the outstanding set — so the badge clears and Send stops
   * being refused on `ONE_ENVELOPE_PER_QUOTE_REASON`, which has just stopped
   * being true — and then the send dialog opens SEEDED WITH THE CHOSEN SIGNER.
   *
   * 🔴 **Dropping `initialSigner` is the failure this whole path exists to
   * prevent.** `SendForSignatureModal` seeds the company's primary contact into
   * an untouched signer field, and that is very often the exact person somebody
   * has just decided not to send to — so opening it without this silently undoes
   * the operation while looking like it worked.
   *
   * The re-read is fired as well as the optimistic drop: a void is a real state
   * change on the server and the fold only closes the window before its answer
   * lands.
   */
  function afterSignerChanged(quote: Quote, voided: DocuSignEnvelope, signer: SignerValue) {
    setChangingSignerFor(null);
    setSignatures((current) => forgetEnvelope(current, voided.quote.id));
    setSignatureRefresh((token) => token + 1);
    setSendingSignature({ quote, initialSigner: signer });
  }

  /*
   * ── THE PDF PREVIEW HOST — one per page ──────────────────────────────────
   *
   * `onChanged` fires after Mark Sent or Send to Xero succeeded inside the
   * viewer: both move the row (stage, Xero link), so the list AND the board —
   * held mounted behind the table with its own copy — are re-read through the
   * tokens that already own their AbortControllers.
   *
   * Send for signature from the viewer opens THIS page's own modal when the row
   * is loaded and sendable, so the reader stays on the list; otherwise (a kanban
   * card outside the loaded page, or a refused send) it goes to the quote page,
   * which opens the same modal on `?sign=1` only if that reader could.
   */
  const pdfPreview = useQuotePdfPreview({
    onChanged: () => {
      setListRefresh((token) => token + 1);
      setBoardRefresh((token) => token + 1);
    },
    onSendForSignature: (target) => {
      const row = rows.find((quote) => quote.id === target.id);
      if (row && sendSignatureDenial(row) === null) {
        setSendingSignature({ quote: row });
        return;
      }
      navigate(quoteSignHref(target.id));
    },
  });

  function previewTargetOf(quote: Quote): QuotePdfPreviewTarget {
    return {
      id: quote.id,
      quoteNumber: quote.quoteNumber,
      subject: quote.subject,
      companyName: quote.company ? documentCustomerName(quote.company) : null,
    };
  }

  /* ---- `?new=1`: open the create form, don't just filter ----------------- */

  // Derived from the serialised query, like `filters`, so it moves with it.
  const wantsNew = new URLSearchParams(queryString).get(NEW_PARAM) === '1';

  const clearNewParam = useCallback(() => {
    const next = new URLSearchParams(queryString);
    // Guarded: an unconditional `setSearchParams` on every close would rewrite
    // the URL — and re-render every filter consumer — for no change at all.
    if (!next.has(NEW_PARAM)) return;
    next.delete(NEW_PARAM);
    setSearchParams(next, { replace: true });
  }, [queryString, setSearchParams]);

  /*
   * Closing has to do both, and in one place.
   *
   * Dropping the form state without dropping the key leaves `?new=1` in the
   * URL, and the effect below would immediately reopen the form the user just
   * dismissed — a modal that will not close. Both the Cancel path and the
   * saved path go through here.
   */
  const closeForm = useCallback(() => {
    setFormState(null);
    clearNewParam();
  }, [clearNewParam]);

  // A URL is not authorisation. Stripping the key rather than ignoring it also
  // stops this re-evaluating on every render.
  useEffect(() => {
    if (wantsNew && !canCreate) clearNewParam();
  }, [wantsNew, canCreate, clearNewParam]);

  /*
   * Resolve the company NAME, then open.
   *
   * The page only has the id — that is why the badge below reads `Company #22`
   * — so the name comes from `GET /companies/:id`. The form is opened when it
   * arrives rather than immediately with a placeholder: a locked field reading
   * "Company #22", or worse nothing at all, is exactly the blank the lock is
   * supposed to prevent.
   *
   * If that lookup fails — a deleted company, a 502, an offline laptop — the
   * fallback is a PLAIN create form with a working company picker, not a locked
   * field with no name in it. The user loses the prefill and can search for the
   * company themselves; they do not lose the form.
   *
   * `setFormState(current => current ?? …)` throughout: if something is already
   * open (the New quote button, an Edit) it wins. A URL param must never
   * replace a form that has typing in it.
   *
   * Deliberately NOT depending on `clearNewParam` — its identity changes with
   * every query-string write, which would re-run this and re-fetch the company
   * each time a filter moved underneath the open modal.
   */
  useEffect(() => {
    if (!wantsNew || !canCreate) return;

    const companyId = filters.companyId;
    const controller = new AbortController();

    // Both routes end in the same place, so the open happens once and in one
    // branch: no company asked for, and a company that could not be read, are
    // the same outcome — a create form with a working picker.
    const resolveCompany: Promise<{ id: number; name: string } | undefined> =
      companyId === null
        ? Promise.resolve(undefined)
        : api
            .getCompany(companyId, controller.signal)
            .then((envelope) => ({ id: envelope.data.id, name: envelope.data.name }))
            .catch(() => undefined);

    void resolveCompany.then((lockedCompany) => {
      if (controller.signal.aborted) return;
      setFormState((current) => current ?? (lockedCompany ? { lockedCompany } : {}));
    });

    return () => controller.abort();
  }, [wantsNew, canCreate, filters.companyId]);

  // `rows` is declared with the `useTableFilters` call above `load`, which
  // needs it. Everything below reads it from there.
  const total = result?.meta.total ?? 0;
  // `meta.totalPages` is 0 (not 1) for an empty set, which would disable both
  // pager buttons and render "Page 1 of 0".
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  // Show all is one list from the first row, whatever `page` still holds.
  const firstRow = total === 0 ? 0 : showAll ? 1 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  /*
   * The issue-date pair counts as ONE filter however many of its two bounds are
   * set, exactly as the expiry preset does — it is one question ("issued when?")
   * with two ends, and counting the ends would make "2 filters" appear from a
   * single control.
   *
   * ⚠️ Column funnels are NOT counted here. This number describes the request;
   * `table.activeCount` describes what was hidden after it came back, and the
   * two are announced separately because merging them would put a client-side
   * pass and a server-side narrow behind one figure.
   */
  const issueDateFiltered = filters.issueDateAfter !== null || filters.issueDateBefore !== null;
  /*
   * ⚠️ **The project picker counts as ONE filter however many projects are
   * ticked**, exactly as the two date pairs above count as one each. It is one
   * question ("on which projects?") whose answer happens to be a set, and
   * counting the members would make "3 filters" appear from a single control —
   * which then disagrees with the number of controls a reader can see to clear.
   */
  const activeFilterCount =
    SINGLE_ID_FILTERS.filter((key) => filters[key] !== null).length +
    (filters.lineProjectId.length > 0 ? 1 : 0) +
    (filters.stageId.length > 0 ? 1 : 0) +
    (filters.currency ? 1 : 0) +
    (expiryFiltered ? 1 : 0) +
    (issueDateFiltered ? 1 : 0);
  const anyFilter = search !== '' || activeFilterCount > 0;

  /*
   * What the column-filter notice may claim, now that the funnels are MIXED
   * SCOPE — one server-scoped (Projects) and one page-scoped (Issue date).
   *
   * ⚠️ **DERIVED BY SUBTRACTING THE PARAMS ACTUALLY SENT, never by counting a
   * column by name.** A server column with SEVERAL values ticked sends nothing
   * and falls back to narrowing the loaded page — `useTableFilters` says so at
   * `rawServerParams` — so a name-based count would claim the whole list had
   * been narrowed on exactly the request where it had not. This arithmetic
   * follows the filter down into that fallback. Same shape as `DealsPage`.
   */
  const serverFiltered = Object.keys(listFilters).length > 0;
  const pageScopedColumnCount = table.activeCount - Object.keys(listFilters).length;
  const loadedQuotes = rows.length === 1 ? '1 quote' : `${rows.length} quotes`;
  /*
   * 🔴 **THE NAMES ARE DERIVED FROM WHAT WAS SENT, NOT WRITTEN DOWN.*
   * *SUPERSEDED: the two branches below hard-coded the word "Projects", which
   * was true while `lineProjectStatus` was the only server column on this page
   * and became a wrong sentence the moment "From Xero" joined it on 2026-09-02
   * — it would have credited Projects with a narrowing From Xero had done.*
   *
   * ⚠️ **Nothing fails when a column gains a scope**; the sentence just stops
   * being true, which is the same rot the block above the notice already records
   * for its predecessor. Mapping the param names that were ACTUALLY sent back to
   * their column labels cannot go stale that way: a column added to
   * `QUOTE_COLUMNS` appears here with nothing to remember.
   *
   * ⚠️ **`stageId` is correctly ABSENT from this**, though its column is
   * `scope: 'server'`. Its selection travels in the URL, so it is counted by
   * `activeFilterCount` — which describes the REQUEST — rather than by
   * `table.activeCount`, which describes what the funnels hid after it came
   * back. Naming it here would report one filter twice under two headings.
   */
  const serverColumnNames = QUOTE_COLUMNS.filter(
    (column) => column.param !== undefined && column.param in listFilters,
  ).map((column) => column.label);
  const columnScopeSentence = !serverFiltered
    ? `Applies to the ${loadedQuotes} on this page only.`
    : pageScopedColumnCount > 0
      ? `${formatList(serverColumnNames)} ${
          serverColumnNames.length === 1 ? 'narrows' : 'narrow'
        } the whole list; the ${
          pageScopedColumnCount === 1 ? 'other one applies' : `other ${pageScopedColumnCount} apply`
        } to this page only.`
      : `${formatList(serverColumnNames)} ${
          serverColumnNames.length === 1 ? 'narrows' : 'narrow'
        } the whole list, not just this page.`;

  /*
   * The project behind `?projectId=N`, by NAME, and for free: every quote
   * serialises `project: {id, name}`, so any row that matched already carries
   * it. No second request — and no name at all when the filter came back empty,
   * which is the normal case while nothing has been quoted against a project.
   * The id still identifies it, so the badge falls back to `Project #N`.
   */
  const projectName =
    filters.projectId === null
      ? null
      : (rows.find((row) => row.project?.id === filters.projectId)?.project?.name ?? null);

  /*
   * The account lead behind `?accountLeadId=N`, by name, on the same terms as
   * `projectName` above: every quote serialises `accountLead`, so a matched row
   * already carries the label and it costs no request. Null when nothing
   * matched — the ordinary case, since the filter is usually followed precisely
   * because the person has no quotes.
   *
   * Rendered defensively: a `UserSummary` can carry `name: ''`, and an empty
   * string in a badge reads as a missing record rather than an unnamed one.
   */
  const accountLeadName =
    filters.accountLeadId === null
      ? null
      : (rows
          .find((row) => row.accountLead?.id === filters.accountLeadId)
          ?.accountLead?.name.trim() || null);

  /*
   * How many rows ON SCREEN are out for signature.
   *
   * Drives whether the consequence note below renders at all: a completed
   * signature accepts the quote and raises an invoice with nobody asked to
   * confirm, which is worth saying beside a list that is showing one — and is
   * pure noise on a list that is not. Counted from the rows rather than from the
   * response, because the response covers every quote in the system and this
   * sentence is about what the reader can see.
   */
  const outstandingOnPage =
    signatures.kind === 'ready'
      ? rows.reduce((count, row) => (signatures.byQuoteId.has(row.id) ? count + 1 : count), 0)
      : 0;

  /*
   * Every key the QUOTES board accepts, written one at a time.
   *
   * The kanban schema is `.strict()` and per board, so this must never become a
   * spread of the page's filter state: `statusId` or `dueDateBefore` in here is
   * a 422 for the whole board, not a stripped key. Blank values are omitted for
   * the same reason they are on the list request — `?stageId=` is a 422, not
   * "no filter".
   *
   * `search` is passed raw: `useKanban` debounces by 250ms itself, so
   * double-debouncing here would only make the board feel slower than the table
   * for no fewer requests.
   */
  const boardParams = useMemo<KanbanQueryParams>(() => {
    const params: KanbanQueryParams = {};
    if (search) params.search = search;
    if (filters.companyId !== null) params.companyId = filters.companyId;
    if (filters.contactId !== null) params.contactId = filters.contactId;
    // The board is grouped by stage, so this narrows it to one column rather
    // than reordering it — which is what someone arriving from
    // `/quotes?stageId=3&view=board` is asking for.
    // A CSV since 2026-10-28; the board takes `IdListFilter` for this key.
    if (filters.stageId.length > 0) params.stageId = filters.stageId.join(',');
    if (filters.dealId !== null) params.dealId = filters.dealId;
    if (filters.currency !== null) params.currency = filters.currency;
    // Already validated as real calendar dates by `parseIsoDate`; the board
    // rejects `2026-02-30` exactly as the list endpoint does.
    if (filters.validTillAfter !== null) params.validTillAfter = filters.validTillAfter;
    if (filters.validTillBefore !== null) params.validTillBefore = filters.validTillBefore;
    /*
     * `filters.projectId` is DELIBERATELY NOT SENT. `GET /quotes` takes it; the
     * quotes board does not declare it, and the schema is `.strict()`, so this
     * one key would 422 the whole board rather than being ignored. A board that
     * ignores a filter is bad; a board that 422s is worse. The notice below the
     * badge tells the reader instead of letting the two views disagree in
     * silence.
     *
     * ⚠️ **`filters.lineProjectId` IS NOT SENT EITHER, AND THAT ONE CAN NEVER
     * CHANGE.** `projectId` is merely undeclared by this board and could be
     * added to the registry tomorrow; `lineProjectId` matches through a JOIN to
     * `f_quote_products`, and a board filter is matched against a column ON THE
     * RECORD — `kanban.registry.ts` types every filter as id / code / dateFrom /
     * dateTo / ids. So this is a structural limit, not a gap somebody will close,
     * and the notice is the permanent answer rather than a stopgap. Same footing
     * as `lineProjectStatus` below.
     *
     * ⚠️ **`issueDateAfter` / `issueDateBefore` are NOT SENT either, and for the
     * same reason.** The quotes board declares `stageId`, `dealId`, `currency`
     * and the two `validTill` bounds — not these two — so the pair that narrows
     * the table would 422 the board. **Do not add them on the strength of the
     * `validTill` pair two lines up**: that they are both date bounds on the
     * same table is the weakest possible evidence about a schema that lists its
     * keys one by one. The notice below covers them.
     *
     * ⚠️ **`lineProjectStatus` IS NOT SENT EITHER**, and this one is not even a
     * near miss: the quotes board declares no key about line items at all, and
     * there is no line-item filter on any of the five boards. Same treatment —
     * withheld here, named in the notice.
     */
    return params;
  }, [
    search,
    filters.companyId,
    filters.contactId,
    filters.stageId,
    filters.dealId,
    filters.currency,
    filters.validTillAfter,
    filters.validTillBefore,
  ]);

  /*
   * The filters this page can apply to the TABLE and `boardParams` withholds
   * from the BOARD, named as a reader would say them.
   *
   * ⚠️ **KEEP THIS IN STEP WITH THE BLOCK ABOVE, and nothing enforces that.**
   * Every entry corresponds to a key deliberately absent from `boardParams`; a
   * filter added there and not here goes unmentioned, which is the silent half
   * of the failure the notice exists to prevent. It is a list rather than a
   * chain of ternaries precisely so a name can only be missing by being missing.
   */
  const unappliedOnBoard = useMemo(() => {
    const names: string[] = [];
    /*
     * 🔴 **THREE PROJECT-ISH FILTERS CAN BE SET AT ONCE, SO NOT ONE OF THEM MAY
     * BE CALLED "project".** `projectId`, `lineProjectId` and
     * `lineProjectStatus` are three different questions the board takes none of,
     * and a name that fits two of them reports several withheld filters as one —
     * so a reader clears the one they recognised and is still looking at an
     * unnarrowed board, which is the failure this notice exists to prevent.
     *
     * ⚠️ **`'quoted project'` is the counterpart of `/invoices`' `'billed
     * project'`** — the same shape reached independently, worded for the document
     * each page lists. The two pages need not share the words; neither may be
     * ambiguous about which project filter it means.
     *
     * ⚠️ **IT PLURALISES, and the singular is not merely tidier.** The picker
     * takes several projects now, and a reader who has ticked three of them and
     * is told the board ignores "the quoted project filter" has been given a
     * count as well as a name — one they can check against their own screen and
     * find wrong. `'header project'` beside it stays singular because
     * `projectId` genuinely is one id.
     *
     * ⚠️ **The test is `length`, not `!== null`.** An empty selection is `[]`
     * here, which is truthy, so the neighbouring line's idiom would name this
     * filter on every board with nothing set.
     */
    if (filters.projectId !== null) names.push('header project');
    if (filters.lineProjectId.length > 0) {
      names.push(filters.lineProjectId.length === 1 ? 'quoted project' : 'quoted projects');
    }
    if (issueDateFiltered) names.push('issue-date');
    if (listFilters.lineProjectStatus !== undefined) names.push('projects-on-lines');
    return names;
  }, [filters.projectId, filters.lineProjectId, issueDateFiltered, listFilters]);

  const quoteHref = useCallback((cardId: number) => `/quotes/${cardId}`, []);

  /* ---- Accepting a quote from the board --------------------------------
   *
   * ⚠️ REMOVED 2026-08-29, backend release `20260829011913-5fbab06`: the
   * `renderMoveConfirmation` prop passed to `KanbanBoard` below, the
   * `QuoteAcceptanceDialog` it mounted, and this page's `acceptanceBusy` /
   * `acceptanceInFlight` / `confirmAcceptance` double-send guard.
   *
   * **Moving a card into the accepting stage now moves the stage and nothing
   * else.** `409 QUOTE_ACCEPTANCE_REQUIRES_CONFIRMATION` is gone from the
   * server source and cannot fire on this route, so a board move here is an
   * ordinary move that succeeds or fails: `useKanban` reports a failure as
   * `moveError` and rolls the card back, exactly as on every other board.
   *
   * (Naming the removed code keeps it greppable; this is the only mention of it
   * left on this page.)
   *
   * ⚠️ **The guard those three variables enforced was against a SECOND INVOICE,
   * not against a second request.** Nothing on the board can raise an invoice
   * any more, which is why the guard goes rather than being kept "to be safe" —
   * `useKanban` already refuses a same-column move without a request, and a
   * duplicate stage PATCH is idempotent. Re-adding a double-send guard here
   * would be guarding a consequence that no longer exists.
   */

  const setView = useCallback(
    (next: QuotesView) => {
      const params = new URLSearchParams(queryString);
      // The table's default is the absence of the key, so switching back to it
      // leaves a clean `/quotes` rather than `/quotes?view=table`. This has to
      // flip with the default above, or the page writes `?view=table` for ever.
      if (next === 'board') params.set('view', 'board');
      else params.delete('view');
      setSearchParams(params, { replace: true });
    },
    [queryString, setSearchParams],
  );

  const setFilter = useCallback(
    (key: SingleIdFilter | 'currency', value: string) => {
      setPage(1);
      const next = new URLSearchParams(queryString);
      if (value === '') next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [queryString, setSearchParams],
  );

  /**
   * The Project picker's selection, written as a comma-separated list.
   *
   * ⚠️ **A SECOND SETTER RATHER THAN A WIDENED `setFilter`, deliberately.**
   * `setFilter` takes a `string` and deletes the key on `''`; handing it a
   * joined array would work by coincidence — `[].join(',')` is `''` — and the
   * coincidence is the problem, because the one rule that must hold here (an
   * empty selection DELETES the key, since `?lineProjectId=` is a 422 and not
   * "no filter") would be enforced by a falsy-string check somebody could
   * reasonably rewrite. Testing `length` on the array states it.
   *
   * ⚠️ **Re-parsed on the way in.** `MultiCombobox` hands back the option
   * `value` strings it was given, and one of those can be an id injected from
   * the URL — so the same guard the query string gets is applied here rather
   * than trusting the control. `parseIdList` also collapses duplicates, which
   * the server would OR into the same answer but which would show as two chips.
   *
   * PAGE 1, like every other filter: this genuinely narrows the list, so a stale
   * page 5 reads as an empty book the moment the selection tightens.
   */
  const setIdList = useCallback(
    (key: MultiIdFilter, values: readonly string[]) => {
      setPage(1);
      const next = new URLSearchParams(queryString);
      const ids = parseIdList(values.join(','));
      if (ids.length === 0) next.delete(key);
      else next.set(key, ids.join(','));
      setSearchParams(next, { replace: true });
    },
    [queryString, setSearchParams],
  );
  const setLineProjects = useCallback(
    (values: string[]) => setIdList('lineProjectId', values),
    [setIdList],
  );
  // The Stage picker and the Stage funnel both write here (2026-10-28).
  const setStages = useCallback(
    (values: readonly string[]) => setIdList('stageId', values),
    [setIdList],
  );

  /*
   * Both bounds are rewritten together, always. Leaving a stale `validTillAfter`
   * behind when switching to "Expired" would send `after = today` AND
   * `before = yesterday` — an empty range that returns nothing and reads as a
   * broken list rather than as a contradictory filter.
   */
  const setExpiry = useCallback(
    (preset: ExpiryPreset) => {
      if (preset === 'custom') return;
      setPage(1);
      const next = new URLSearchParams(queryString);
      next.delete('validTillBefore');
      next.delete('validTillAfter');
      const bounds = boundsFor(preset);
      if (bounds.validTillBefore) next.set('validTillBefore', bounds.validTillBefore);
      if (bounds.validTillAfter) next.set('validTillAfter', bounds.validTillAfter);
      setSearchParams(next, { replace: true });
    },
    [queryString, setSearchParams],
  );

  /**
   * One issue-date bound, written on its own.
   *
   * ⚠️ **Deliberately NOT the both-bounds-together rewrite `setExpiry` does**,
   * and the difference is the mechanism rather than the column. The expiry
   * control writes PRESETS, where a stale half from the previous preset produces
   * a contradictory range nobody asked for; these two boxes are typed
   * independently, and rewriting the other one would erase a bound the person
   * had just entered.
   *
   * An empty box DELETES its key. `?issueDateBefore=` is a 422 on this
   * `.strict()` schema, not "no filter", so the empty string must never reach
   * the URL — `load` guards the request end too, because a hand-pasted URL never
   * came through here.
   *
   * The list IS narrowed by these, so a stale page 5 would read as an empty book
   * the moment the range tightens.
   */
  const setIssueDate = useCallback(
    (key: 'issueDateAfter' | 'issueDateBefore', value: string) => {
      setPage(1);
      const next = new URLSearchParams(queryString);
      if (value === '') next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [queryString, setSearchParams],
  );

  /**
   * The hook's header props, with the page reset folded into the two events that
   * change what the SERVER returns — the sort, and a server-scoped funnel.
   *
   * A sort is a new request for a differently-ordered list, so page 5 of the old
   * order describes rows that are no longer there. The PAGE-scoped funnel
   * deliberately does NOT reset — it hides rows already loaded and fires no
   * request, so moving the reader off their page would be a side effect of a
   * purely local filter.
   *
   * ⚠️ **THE `scope === 'server'` BRANCH ARRIVED WITH THE PROJECTS COLUMN
   * (2026-09-02), AND THE OLD COMMENT WOULD HAVE JUSTIFIED LEAVING IT OUT.** It
   * said the funnel "fires no request", which was true while every column here
   * was page-scoped and is false now: a server column re-queries and NARROWS THE
   * WHOLE LIST, so a stale page 5 reads as an empty book rather than as a filter
   * that matched little. Same shape and same branch as `DealsPage`.
   *
   * Not memoised: the hook returns a fresh object each render, so a
   * `useCallback` here would hold nothing. Same shape as `CompaniesPage`.
   */
  function headerFor(key: string): FilterableTHProps {
    const props = table.headerFor(key);
    const filter = props.filter;
    if (!filter) return props;
    /*
     * THE STAGE FUNNEL AND THE "All stages" COMBOBOX ARE ONE FILTER.
     *
     * 🔴 **`stageId` LIVES IN THE URL AND MUST HAVE EXACTLY ONE HOME.** It is an
     * `ID_FILTERS` key — deep-linkable, saved against the account, and handed to
     * the BOARD in `boardParams`, which has no header to hang a funnel on. So
     * the funnel is a PROJECTION of the query string rather than a second copy:
     * `value.selected` is read back from `filters.stageId`, and a single pick is
     * written through `setFilter`, the same call the Combobox in the filter bank
     * makes. Set the stage in either control and the other shows it; switch to
     * the board and it is still applied.
     *
     * ⚠️ **The alternative — letting `useTableFilters` own it — renders
     * perfectly and is wrong**: the Combobox would read "All stages" over a
     * table showing one stage, and the board would silently drop the filter the
     * table had. That is the second-source-of-truth failure, not a styling one.
     *
     * ══ SEVERAL TICKED, AND WHY THAT HALF STAYS IN THE HOOK ═══════════════
     *
     * ⚠️ **`?stageId=1,2` IS A 422 — comma-separated multi-value filters are
     * COMPANIES-ONLY across this API**, on the list and the board alike
     * ("expected number, received NaN"). So one URL param cannot carry two
     * stages, and this must never emit CSV.
     *
     * `useTableFilters` already has the honest answer for exactly this, and the
     * Projects column above inherits it: with several ticked on a non-`multi`
     * server column it sends NOTHING and narrows the LOADED PAGE instead
     * (`rawServerParams` skips `selected.length !== 1`, and `visibleRows` then
     * runs the row test). This routes into that rather than reimplementing it:
     *
     *     exactly one ticked   ->  the URL; hook `selected` emptied
     *     none, or two or more ->  hook `selected`; the URL param dropped
     *
     * The two are mutually exclusive by construction, so the selection has one
     * home at every moment and the hook's own `scopeNote` — "Several values
     * selected — the API filters this column by one value at a time…" — is true
     * when it fires. **It also means hook `selected` NEVER holds exactly one**,
     * which is what keeps `serverParams.stageId` from ever being emitted; it is
     * not consumed anywhere either, so the guarantee is belt and braces.
     *
     * ⚠️ **EMPTINESS ALWAYS GOES TO THE HOOK**, and it is genuinely page-scoped:
     * `stage_id` is nullable but there is no is-null filter on this endpoint, so
     * "Is empty" can only ever answer for the rows already loaded. The
     * primitive's default server footnote says exactly that, unprompted.
     *
     * ⚠️ **`options` come from the `/quote-stages` master, by ID**, and are the
     * SAME array the Combobox renders — one derivation, so the two controls
     * cannot offer different stages. Never matched on the stage NAME:
     * `/quote-stages` is admin-writable and this codebase has been bitten three
     * times by substring matches on admin-editable free text.
     *
     * 🔴 **A MASTER THAT RETURNED NOTHING IS NOT A CLOSED SET OF NOTHING.**
     * `ColumnSpec.options` REPLACES the accumulated list outright, so handing the
     * funnel an empty `stageOptions` after a failed read — or on the first paint
     * — would render "No values on this page" over a table of quotes that every
     * one have a stage: a closed vocabulary asserted from a request that never
     * came back. `stages.stages.length` is what distinguishes the two, NOT
     * `stageOptions.length`, which the injected URL id can make non-empty without
     * anything having been read. Degrading to the hook's own accumulated values
     * is narrower than the master and is at least derived from rows on screen —
     * and `withSelectedValues` keeps the applied stage untickable-from either
     * way, since a filtered response can come back holding no rows at all.
     */
    const base: typeof filter =
      key === 'stageId'
        ? {
            ...filter,
            options: withSelectedValues(
              stages.stages.length > 0 ? stageOptions : filter.options,
              filters.stageId.map(String),
            ),
            value: {
              ...filter.value,
              selected: filters.stageId.map(String),
            },
            /*
             * *SUPERSEDED 2026-10-28: "exactly one ticked -> the URL; two or more
             * -> hook `selected`".* `?stageId=` takes a CSV now, so EVERY ticked
             * stage goes to the URL and the hook keeps emptiness only. An empty
             * selection deletes the param.
             */
            onChange: (next) => {
              filter.onChange({ selected: [], emptiness: next.emptiness });
              setStages(next.selected);
            },
          }
        : filter;
    const withReset: typeof filter = {
      ...base,
      onChange: (next) => {
        base.onChange(next);
        if (filter.scope === 'server') setPage(1);
      },
    };
    const sortControl = filter.sort;
    if (!sortControl) return { ...props, filter: withReset };
    return {
      ...props,
      filter: {
        ...withReset,
        sort: {
          direction: sortControl.direction,
          onSort: (direction) => {
            sortControl.onSort(direction);
            setPage(1);
          },
        },
      },
    };
  }

  /**
   * ── COLUMN LAYOUT: order, hidden set, and the two frozen edges ────────────
   *
   * Stored AGAINST THE ACCOUNT under `preferences.columns.quotes`, a sibling of
   * the saved-filter branch this page already writes. ⚠️ **It is NOT in the URL,
   * and that is the line `useSavedFilters` already draws**: a filter says WHICH
   * ROWS and must travel with a shared link, where a column arrangement is the
   * reader's own chrome — putting it in the query string would impose the
   * sender's hidden columns on whoever they sent it to.
   *
   * ⚠️ **The scope string is `'quotes'`, the same one the saved filters use.**
   * They are different branches of the preferences document, so the two cannot
   * collide; reusing the word means a page is one scope rather than two names
   * somebody has to keep in step.
   */
  const columnLayout = useColumnLayout(
    'quotes',
    QUOTE_TABLE_COLUMN_KEYS,
    QUOTE_LOCKED_COLUMNS,
    QUOTE_PINNED_COLUMNS,
  );

  /**
   * The keys the table renders, in order, with the actions lane forced last.
   *
   * ⚠️ **THE APPEND IS NOT BELT-AND-BRACES.** `actions` is locked, so the
   * controller refuses to HIDE it and refuses to MOVE it — but nothing stops
   * another column being moved PAST it, and `pinned` is `sticky right-0`, which
   * is only honest while this really is the last column. Left to the stored
   * order, a reader who drags Grand total to the end gets an actions cell
   * floating over it. Nothing errors and the buttons still work; they are just
   * drawn on top of a live column.
   */
  const tableColumnKeys = useMemo(() => {
    const rest = columnLayout.visibleKeys.filter((key) => key !== ACTIONS_COLUMN);
    return [...rest, ACTIONS_COLUMN];
  }, [columnLayout.visibleKeys]);

  const headRowRef = useRef<HTMLTableRowElement | null>(null);

  /**
   * ⚠️ **THE MEASURER COUNTS ACTIONS AS A RIGHT EDGE; THE CELLS DO NOT.**
   * `useFrozenOffsets` sums the rendered widths of the frozen cells OUTSIDE each
   * one, so if Actions' 224px were left out of that sum a second right-frozen
   * column would be placed at `right: 0` — directly underneath the actions lane,
   * with no error and nothing in the DOM looking wrong. The cells themselves
   * still get `pinned` and no `frozen`, because the two write the same
   * `box-shadow` property and cannot be composed. See `frozenPropsFor`.
   *
   * ⚠️ *SUPERSEDED: a `frozenSideForMeasurement` callback here answering
   * `'right'` for the actions key.* The hook takes `pinnedKeys` itself now, so
   * the controller's own `frozenSideOf` goes in unwrapped — which also settles
   * the dependency question that wrapper existed to state. That function is
   * memoised on the two freeze lists, where the controller object is a fresh
   * literal every render, and `useFrozenOffsets` tears down and re-attaches a
   * `ResizeObserver` over the header and every cell in it whenever its identity
   * changes — on every keystroke in the search box, had this depended on the
   * controller.
   */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    tableColumnKeys,
    columnLayout.frozenSideOf,
    QUOTE_PINNED_COLUMNS,
  );

  /**
   * The table's floor, in px — see `tableMinWidth`.
   *
   * 🔴 **`style`, NEVER a class.** `min-w-[${n}px]` emits no CSS at all: Tailwind
   * v4 scans source text, so a computed arbitrary value is a class name in the
   * DOM with no rule behind it. The failure is invisible — the table simply has
   * no floor and its columns crush instead.
   */
  const tableStyle = useMemo(
    () => ({ minWidth: `${tableMinWidth(tableColumnKeys)}px` }),
    [tableColumnKeys],
  );

  /**
   * One row's cells, generated from the same key list as the header and the
   * skeleton. Each arm keeps the comment that was attached to the `<td>` it
   * replaces.
   */
  function bodyCell(key: string, quote: Quote) {
    const frozen = frozenPropsFor(key, columnLayout.frozenSideOf, frozenOffsets);

    switch (key) {
      case 'subject':
        return (
          <TD key={key} {...frozen}>
            <SubjectCell quote={quote} />
          </TD>
        );
      /*
       * The stage ALONE. `ImportedFromXeroBadge` shared this cell until
       * 2026-09-02 and now has its own column after Projects — two facts in one
       * cell is why neither could be filtered or sorted on. The wrapper `div`
       * went with it: one badge needs no flex row.
       */
      case 'stageId':
        return (
          <TD key={key} {...frozen}>
            <StageBadge stage={quote.stage} stages={stages.stages} />
          </TD>
        );
      case 'awaitingSignature':
        return (
          <TD key={key} {...frozen}>
            <SignatureCell state={signatures} quoteId={quote.id} />
          </TD>
        );
      /*
       * Dates are plain 'YYYY-MM-DD'; `formatDateOnly` is timezone-safe, and
       * `new Date()` on one of these drifts a day for anyone west of Greenwich.
       */
      case 'issueDate':
        return (
          <TD key={key} className="whitespace-nowrap text-xs text-fg-muted tabular" {...frozen}>
            {formatDateOnly(quote.issueDate)}
          </TD>
        );
      /*
       * Four states, never two — see `LineProjectStatusCell`. The value is the
       * SERVER's, derived from the line items; it is not computed from
       * `quote.project`, which is the header project and answers a different
       * question.
       */
      case 'lineProjectStatus':
        return (
          <TD key={key} {...frozen}>
            <LineProjectStatusCell status={quote.lineProjectStatus} />
          </TD>
        );
      /*
       * From Xero — the WORD, not the badge. The owner asked for a Yes/No
       * column, and a badge that renders for "Yes" and nothing for "No" cannot
       * fill one: a blank cell in a column headed "From Xero" reads as unknown,
       * where "No" is a real, known answer.
       *
       * ⚠️ **The import DATE stays on `title`**, exactly as
       * `ImportedFromXeroBadge` carries it — which run imported a document
       * matters when somebody is investigating one and never when they are
       * scanning a list. A `title` carrying a value the cell does not show is
       * the documented exception to the no-helper-text rule; it is a value, not
       * advice, and there is none on the "No" rows because there is no date to
       * show.
       *
       * ⚠️ **`neutral`, and no tone at all on the cell.** An imported quote is
       * not a problem — it is real history a customer was really billed for —
       * and 1,127 of them carry the mark, so a tone that shouts here teaches
       * people to ignore tone everywhere.
       */
      case IMPORTED_FROM_XERO_COLUMN:
        return (
          <TD
            key={key}
            className="text-xs text-fg-secondary"
            {...frozen}
            {...(quote.importedFromXeroAt !== null
              ? {
                  title: `Imported from Xero on ${formatDateTime(quote.importedFromXeroAt)}`,
                }
              : {})}
          >
            {importedFromXeroLabel(quote.importedFromXeroAt)}
          </TD>
        );
      case 'validTill':
        return (
          <TD key={key} className="text-xs text-fg-muted" {...frozen}>
            <ValidTillCell quote={quote} />
          </TD>
        );
      case 'grandTotal':
        return (
          <TD key={key} numeric className="text-fg-secondary" {...frozen}>
            <InvoiceMoney value={quote.grandTotal} currency={quote.currency} />
          </TD>
        );
      /*
       * The Xero cell stood before this one — `XeroRowPushControl` in `cell`
       * layout — and went with its column on 2026-08-31. `/invoices` lost the
       * same cell in the same change, so the two lists still do not drift.
       *
       * ⚠️ THE EM-DASH PLACEHOLDER IS GONE, and its removal is the point rather
       * than a tidy-up. This cell used to be `showRowActions ? buttons : —`,
       * where the dash stood for "this cell is empty on purpose, and here is
       * why" on a `title`. That shape cannot survive an UNGATED control:
       * tasks-and-notes renders for everybody, so the cell is never empty again,
       * and the dash would have sat BESIDE a working button — where it stops
       * reading as "nothing here" and starts reading as a broken second control.
       *
       * The alternative considered and rejected was to keep the dash only when
       * nothing but the activity button renders. That is the same picture — a
       * dash next to a live button — so it fixes nothing and keeps a branch.
       *
       * What is lost is a hover-only `denialReason` hint that a reader had to
       * find on an em-dash to read at all. That is a thin loss, and
       * `CompaniesPage` — whose Actions cell went ungated first — already drops
       * it for the same reason. Consistency between the two lists is worth more
       * than the hint.
       */
      case ACTIONS_COLUMN:
        return (
          <TD key={key} numeric pinned>
            <div className="flex justify-end gap-1.5">
              {/* Ungated: the task and note routers carry no role guard, so this
                  is offered to everyone the row is offered to — a `viewer`
                  included. */}
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                leadingIcon={<ActivityIcon />}
                aria-label={`Tasks and notes for ${quote.subject}`}
                title={`Tasks and notes for ${quote.subject}`}
                onClick={() => setActivityFor(quote)}
              />
              {/* Ungated for the same reason — the PDF route carries no role
                  guard. It renders nothing unless the quote is in Xero, and the
                  SAME component in `card` layout is on the sub-`md` card, so the
                  two cannot drift. */}
              {/* Preview, at every stage and whether or not the quote is in
                  Xero yet — the viewer answers that itself. */}
              <PreviewQuotePdfButton
                target={previewTargetOf(quote)}
                onPreview={pdfPreview.open}
                variant="row"
              />
              <DownloadPdfButton
                kind="quote"
                document={quote}
                reference={quote.subject}
                variant="row"
              />
              {/* Always rendered, disabled with its reason when it cannot be
                  used — see `sendSignatureDenial`. Second in the row because
                  sending precedes billing: a quote goes out for signature, then
                  converts.

                  On a row with an outstanding envelope this is the three-item
                  menu, in the same square: the trigger is the same icon-only
                  ghost button, so the Actions column does not change width or
                  order from row to row. */}
              <RowSignatureActions
                quote={quote}
                signatures={signatures}
                sendDenial={sendSignatureDenial(quote)}
                canManage={canSend}
                manageDenial={signatureManageDenial}
                variant="cell"
                onSend={(target) => setSendingSignature({ quote: target })}
                onReminded={afterReminded}
                onChangeSigner={(target, envelope) =>
                  setChangingSignerFor({ quote: target, envelope })
                }
              />
              {canConvert && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  leadingIcon={<InvoiceIcon />}
                  aria-label={`Convert ${quote.subject} to an invoice`}
                  /*
                   * The disabled sentence REPLACES the label on a staged quote
                   * rather than sitting beside it: an icon-only button has one
                   * `title`, and on a quote that can never convert the reason is
                   * the more useful of the two. `aria-label` still names the
                   * action, so the control is never anonymous to a screen
                   * reader.
                   */
                  title={
                    quote.billingMode === 'staged'
                      ? STAGED_CONVERT_REASON
                      : `Convert ${quote.subject} to an invoice`
                  }
                  disabled={quote.billingMode === 'staged'}
                  onClick={() => setPendingConvert(quote)}
                />
              )}
              {canUpdate && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  leadingIcon={<PencilIcon />}
                  aria-label={`Edit ${quote.subject}`}
                  title={`Edit ${quote.subject}`}
                  onClick={() => setFormState({ quote })}
                />
              )}
              {canDelete && (
                <Button
                  variant="danger"
                  size="sm"
                  iconOnly
                  leadingIcon={<TrashIcon />}
                  aria-label={`Delete ${quote.subject}`}
                  title={`Delete ${quote.subject}`}
                  onClick={() => void openDelete(quote)}
                />
              )}
            </div>
          </TD>
        );
      default:
        return null;
    }
  }

  /**
   * "Clear column filters" — the hook's own state, PLUS the stage.
   *
   * ⚠️ **`table.clearAll` alone would leave the Stage funnel showing a tick.**
   * That funnel's selection lives in the URL rather than in the hook (see
   * `headerFor`), so a button beside it labelled "clear column filters" that
   * left it standing is the broken-button reading this codebase already records
   * for a "Clear filters" that leaves rows hidden.
   *
   * ⚠️ **The cost, stated rather than hidden: this also clears a stage set from
   * the "All stages" Combobox in the filter bank.** That is coherent because it
   * is ONE filter over one param shown in two controls — clearing it from either
   * clears the same thing — and the bank's own "Clear filters" already drops
   * `stageId` too, so nothing becomes unclearable in either direction.
   *
   * ⚠️ **The SORT still survives, deliberately**, as `clearAll`'s own comment
   * says: an order hides nothing and narrows nothing, so dropping it here would
   * re-order a list as a side effect of clearing a filter.
   */
  const clearColumnFilters = useCallback(() => {
    table.clearAll();
    setStages([]);
  }, [table, setStages]);

  function clearFilters() {
    setSearch('');
    setPage(1);
    /*
     * The column funnel lives in the hook rather than in the URL, so rewriting
     * the query string leaves it standing — and a "Clear filters" button that
     * leaves rows hidden reads as a broken button.
     *
     * ⚠️ `clearAll` clears the FUNNEL ONLY; the sort survives, deliberately. An
     * order is not a filter: it hides nothing and narrows nothing, so dropping
     * it here would re-order a list as a side effect of clearing a search box.
     */
    table.clearAll();
    // `view` is not a filter and survives the clear: dropping it would throw
    // someone out of the board they are looking at as a side effect of tidying
    // up a search box. The TABLE is the default, so it is the BOARD that has to
    // be re-spelled here.
    setSearchParams(showBoard ? { view: 'board' } : {}, { replace: true });
  }

  /*
   * DELETING A QUOTE DESTROYS TWO THINGS, AND THE SERVER WARNS ABOUT NEITHER.
   *
   * 1. Its line items cascade away — no 409, no count in the response, nothing
   *    to undo.
   * 2. Any invoice raised from this quote SURVIVES, with its `quote` link set
   *    to null. The foreign key is ON DELETE SET NULL and nothing else in the
   *    system records the provenance, so the invoice keeps every figure and
   *    quietly loses all trace of where those figures came from. Unrecoverably.
   *
   * The ONE thing the server does refuse over is DocuSign.
   * `f_docusign_envelopes.quote_id` is ON DELETE RESTRICT, so a quote carrying
   * signature envelopes answers 409 instead of 204 — an envelope and any
   * signature on it exist inside DocuSign whatever we do here, and dropping the
   * local row would leave a legal record with nothing saying what it settles.
   * `confirmDelete` surfaces that refusal verbatim and keeps the dialog open.
   *
   * Nothing else is guarded: neither of the two consequences above raises so
   * much as a warning, so a UI that expects the server to protect them will let
   * someone shred an audit trail without a murmur. Both counts are fetched up
   * front because this dialog is the only moment anyone could have been told.
   */
  async function openDelete(quote: Quote) {
    setDeleteError(null);
    setPendingLineCount(null);
    setPendingInvoiceCount(null);
    setPendingDelete(quote);

    // `perPage: 1` — only `meta.total` is wanted here, not the invoices.
    const [lines, invoices] = await Promise.allSettled([
      api.listQuoteProducts(quote.id),
      api.listInvoices({ quoteId: quote.id, perPage: 1 }),
    ]);
    // A failed count must not block the delete — the dialog falls back to the
    // generic warning rather than claiming there are none.
    if (lines.status === 'fulfilled') setPendingLineCount(lines.value.data.length);
    if (invoices.status === 'fulfilled') setPendingInvoiceCount(invoices.value.meta.total);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteQuote(pendingDelete.id);
      setPendingDelete(null);
      // Removing the last row of a page would strand the user on an empty final
      // page; stepping back refetches through the page effect.
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // Already gone is the outcome that was wanted — treat it as success.
        setPendingDelete(null);
        if (rows.length === 1 && page > 1) setPage((p) => p - 1);
        else await load();
      } else if (caught instanceof ApiError && caught.status === 409) {
        /*
         * DocuSign envelopes hold the quote down — see the note above
         * `openDelete`. The message names the quote, the envelope count and
         * whether they are still out for signature ("Void them before deleting
         * it."), so it is shown as-is: there is no envelopes screen to send
         * anyone to, and nothing invented here would say more than the server
         * already does. `pendingDelete` is deliberately left set, so the dialog
         * stays open with the explanation on it.
         */
        setDeleteError(`${caught.message} Nothing was deleted.`);
      } else if (caught instanceof ApiError && caught.status === 403) {
        /*
         * A 403 body carries NO `details`, so this is always a banner and never
         * a per-field error. The button is gated, but a role can change under a
         * live token, so the handler stays — and the copy comes from
         * `denialReason` rather than the old flat "administrators only", which
         * was the wrong sentence for a viewer and for a write-role user alike.
         */
        setDeleteError(
          `${
            denialReason(ability, 'delete', 'quotes') ??
            'Your access changed since this page loaded.'
          } Nothing was deleted.`,
        );
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  /**
   * ── CONVERT, and what this page no longer does ───────────────────────────
   *
   * ⚠️ **The request used to be fired from here, from a ConfirmDialog, and it is
   * not any more.** On the owner's instruction Convert opens a prefilled
   * Create-Invoice modal and nothing is created until Save. `InvoiceFormModal`
   * owns the conversion from that point: the `POST /quotes/{id}/convert`, the
   * follow-up `PATCH /invoices/{newId}` carrying whatever was edited, both 409
   * branches — staged and already-billed, which mean opposite things — and the
   * report for a conversion whose follow-up edit was refused.
   *
   * ⚠️ **Do not reinstate a `convertToInvoice` here.** This handler and
   * `QuoteDetailPage`'s were maintained branch for branch precisely because two
   * implementations of one refusal drift; the modal is now the single copy, and
   * a local one would put that hazard straight back.
   *
   * `POST /quotes/{id}/convert` RESOLVES TO AN INVOICE, never a quote — the two
   * differ in exactly the fields this list renders — so the record handed back is
   * navigated to and never fed into `result`. The list left behind is stale by
   * design, because it is no longer on screen.
   */
  function afterConvert(invoice: Invoice) {
    navigate(`/invoices/${invoice.id}`);
  }

  /**
   * Convert, pressed on a BOARD CARD.
   *
   * Resolves the card to its `Quote` and then hands off to exactly the state the
   * table row sets. Nothing is created here and no conversion is attempted —
   * this is a READ, and the `POST /quotes/{id}/convert` still happens inside
   * `InvoiceFormModal` when somebody presses Save on the prefilled form.
   *
   * ⚠️ The staged check has ALREADY been made by the button, off the card's own
   * `billingMode`, so this is not the place to repeat it — and it must not
   * become the place, because a check here would fire after a request the card
   * could have prevented. The 409 remains the guarantee behind both.
   *
   * A second press while one is in flight is ignored rather than queued: the
   * modal keys on the quote id, so two overlapping reads would race to decide
   * which quote it opens over.
   */
  async function openConvertFromCard(card: KanbanCard) {
    if (cardConvertId !== null) return;
    setCardConvertError(null);
    setCardConvertId(card.id);
    try {
      const { data } = await api.getQuote(card.id);
      setPendingConvert(data);
    } catch (caught) {
      /*
       * `cardLabel`, not `card.title` — the title is `string | null` on EVERY
       * board and an empty string is reachable, so a raw read gives
       * "Couldn't open  to convert", a broken sentence. It is also the same
       * name the acceptance dialog puts on this card, which is the point of
       * reusing it: one card, one label, whatever is being said about it.
       */
      const label = cardLabel(card);
      const detail =
        caught instanceof ApiError
          ? /*
             * 404 gets its own sentence: the card is a snapshot of a fetch that
             * has already happened, so a quote deleted since the board loaded is
             * an ordinary state, and the server's own wording for it says
             * nothing about the board being stale.
             */
            caught.status === 404
            ? 'It may have been deleted since the board was loaded. Refresh to see current stages.'
            : (caught.formMessage ?? caught.message)
          : 'Something went wrong reading it.';
      setCardConvertError(`Couldn’t open ${label} to convert. ${detail}`);
    } finally {
      setCardConvertId(null);
    }
  }

  /*
   * ⚠️ **`handleXeroSettled` was here and went with the Xero column on
   * 2026-08-31.** It was the only producer of `xeroReport`: it recorded the push
   * outcome, re-read the row through `listRefresh` (a refused push still moves
   * `xero.status`, so the report alone would have left the button claiming
   * something from before the attempt), and bumped the board, which holds its own
   * copy of these records.
   *
   * Removed together with the column rather than left behind: a settle handler
   * with nothing that can settle is a live-looking path to a control that is
   * gone. Both tokens it bumped survive and are still bumped elsewhere.
   */
  /*
   * The line grid inside the form saved on its OWN, and the modal is still
   * open.
   *
   * ⚠️ **THIS IS NOT A SMALLER `handleSaved`, AND IT MUST NOT CLOSE THE FORM.**
   * The person has not finished — they pressed "Save N rows" at the top of the
   * line-items band and are still editing. Everything `handleSaved` does about
   * *finishing* (`closeForm`, and the create-lands-on-page-1 jump) would take
   * the form away from underneath them.
   *
   * ══ WHY IT EXISTS AT ALL ══════════════════════════════════════════════════
   *
   * The grid's own Save is a second way to write line items, and it reported
   * NOTHING upward. So: open a quote, fix the project on two lines, press
   * "Save 2 rows", watch each row report success — and then the only way out is
   * a button reading **Cancel**, because the footer Save has correctly greyed
   * out with nothing left to send. Cancel routes to `closeForm`, which does not
   * reload. The row's **Projects** badge then still read its old value while
   * the database said otherwise, and a re-sort by that column disagreed with
   * the badge sitting next to it. Nothing errored, and a manual refresh "fixed"
   * it — which is what made it read as the column being flaky rather than as a
   * missing refetch.
   *
   * ⚠️ `lineProjectStatus` is computed live per request from the line table, so
   * it can never be stale ON THE SERVER. Every stale badge on this column is
   * this page holding an old response, which is why the fix belongs here and
   * not in the derivation.
   *
   * ⚠️ Refetching UNDERNEATH AN OPEN MODAL is deliberate, not premature. The
   * row behind the dialog is wrong the instant those lines land; waiting for
   * the close would mean picking one of four exit paths (Cancel, the X,
   * Escape, the overlay) and getting the other three wrong. `formState` holds
   * its own copy of the quote, so a reload of `rows` cannot disturb the form.
   *
   * The board is bumped for the same reason `handleSaved` bumps it — it is held
   * mounted behind the table with its own copy of these records.
   */
  function handleLinesSaved() {
    setBoardRefresh((token) => token + 1);
    void load();
  }

  function handleSaved() {
    const wasCreate = !formState?.quote;
    // `closeForm`, not `setFormState(null)`: a save has to drop `?new=1` too,
    // or the effect above reopens the form on the very next render.
    closeForm();
    /*
     * The board is held MOUNTED behind the table view here, so it is carrying a
     * copy of the records that a save has just invalidated. Bumped on an edit
     * as well as a create: the card shows the subject, the total and the valid-
     * till date, and a stage change in the form moves it to another column.
     * Harmless while the table is showing — a disabled board fetches nothing.
     */
    setBoardRefresh((token) => token + 1);
    // A new quote gets the highest id, so under `id DESC` it lands on page 1.
    if (wasCreate && page !== 1) setPage(1);
    else void load();
  }

  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'quote' : 'quotes'}${search ? ` matching “${search}”` : ''}${
        activeFilterCount > 0
          ? ` (${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'})`
          : ''
      }`;

  /*
   * The board renders its own per-column counts and its own busy state, so the
   * row count would be a second, different number for the same thing — it gets
   * no description at all.
   */
  const panelDescription = showBoard ? undefined : countLabel;

  const newQuoteButton = canCreate ? (
    <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
      New quote
    </Button>
  ) : null;

  const emptyState = (
    <QuotesEmptyState
      filtered={anyFilter}
      expiryFiltered={expiryFiltered}
      projectFiltered={filters.projectId !== null}
      /*
       * The request brought rows back and a COLUMN FUNNEL hid every one of them.
       * That is a different sentence with a different button: "clear filters"
       * rewrites the URL and would leave the funnel — and the table — exactly as
       * they are, which reads as a dead control.
       */
      columnsHidAll={rows.length > 0}
      onClearColumns={table.clearAll}
      onClear={clearFilters}
      hasError={Boolean(error)}
      action={newQuoteButton}
    />
  );

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Operations" title="Quotes" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Quotes"
        /*
         * PageHeader's actions slot is `flex-wrap`, so the toggle and the New
         * quote button drop onto their own lines at 320px instead of forcing
         * the header wide. This is NOT PanelHeader's actions slot, which is
         * `shrink-0` with no wrap and has blown this app out sideways before —
         * the filter bank below is in a full-width row for exactly that reason,
         * and the toggle must not join it.
         */
        actions={
          <>
            <ViewToggle view={view} onChange={setView} />
            {newQuoteButton}
          </>
        }
      />

      <Panel>
        <PanelHeader title="All quotes" description={panelDescription} />

        {/*
          * The filters get their own full-width row rather than PanelHeader's
          * `actions` slot: that slot is `shrink-0`, so a bank of controls in it
          * can neither shrink nor wrap and blows the page out sideways below
          * 1280px. This was a real bug on DealsPage.
          */}
        <div className="flex flex-wrap items-start gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-56">
            <Field
              label="Search quotes"
              hideLabel
              /*
               * The one filter the board honours that still does not mean the
               * same thing in both views: `/quotes?search=` matches `subject`
               * OR `quoteNumber`, the board's matches the card title — the
               * subject — alone. So "QU-0527" finds a row and finds no card,
               * and the board's result is a strict subset of the table's for
               * the same term. Said in the field itself rather than buried in a
               * banner, because it is a fact about THIS box.
               */
              {...(showBoard
                ? { hint: 'On the board this matches the subject only, not the quote number.' }
                : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="search"
                  // Wider than the invoice list's, and the placeholder says so:
                  // this one matches the subject OR the quote number, so
                  // "QU-0527" genuinely finds its row.
                  placeholder="Search subject or number…"
                  leadingIcon={<SearchIcon />}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-48">
            <Field label="Filter by stage" hideLabel>
              {({ id }) => (
                /*
                 * A multiselect since 2026-10-28 (owner: every filter box takes
                 * several values). *SUPERSEDED: a single `Combobox` writing one
                 * id.* Several stages mean OR; the CSV lands in the same
                 * `stageId` param the funnel writes, so the two stay one filter.
                 */
                <MultiCombobox
                  id={id}
                  values={filters.stageId.map(String)}
                  onChange={setStages}
                  options={stageOptions}
                  placeholder="All stages"
                  searchPlaceholder="Search stages…"
                  summaryNoun={{ singular: 'stage', plural: 'stages' }}
                  maxVisibleChips={2}
                  loading={stages.loading}
                  {...(stages.failed ? { invalid: true } : {})}
                />
              )}
            </Field>
          </div>
          {/*
            * ✅ **A searchable `Combobox` — owner's decision 2026-09-13**, who
            * overruled the argument this comment used to make. *SUPERSEDED: "A
            * native Select, not a Combobox: three fixed presets need no search
            * box, and the platform control brings keyboard type-ahead and the
            * mobile picker with it."* Every filter dropdown in this bank is now
            * one control, and consistency across the bank was chosen over what
            * a three-option list wants on its own. ⚠️ **The superseded reasoning
            * was not WRONG — it was outranked**, so do not read the reversal as
            * a defect being fixed and do not re-argue it from the same premise.
            *
            * ⛔ **NO `emptyOptionLabel`, AND THAT IS NOT AN OMISSION — this is
            * the one control on the bank whose neutral state is a REAL VALUE.**
            * Its four neighbours clear to `''` (the key leaves the URL), so
            * their empty row IS the cleared state. Here `all` is a member of
            * `ExpiryPreset` and writes two deleted bounds; an empty row beside
            * it would be a SECOND row meaning the same thing, and picking it
            * would hand `setExpiry` an `''` that is not a member of the union at
            * all. `placeholder` stays as the honest fallback and is unreachable
            * while a preset always matches.
            */}
          <div className="w-full min-w-0 sm:w-56">
            <Field
              label="Filter by expiry"
              hideLabel
              // Surfaced, not merely commented. Both presets are bounds on
              // `validTill` and NULL matches neither, so open-ended quotes drop
              // out of both views. Silently hiding live quotes from someone
              // reviewing what is about to lapse is the worst thing this screen
              // could do, so it says so on the spot.
              {...(expiryFiltered ? { hint: 'Quotes with no expiry date are not shown.' } : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={expiryPreset}
                  onChange={(value) => setExpiry(value as ExpiryPreset)}
                  options={expiryOptions}
                  placeholder="All quotes"
                  searchPlaceholder="Search expiry filters…"
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-40">
            {/* The invalid border was the only signal here. It needs words
                beside it, and the words also have to say the empty list is a
                failure rather than an answer. */}
            <Field
              label="Filter by currency"
              hideLabel
              {...(currencies.failed
                ? { error: 'Couldn’t load currencies.' }
                : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={filters.currency ?? ''}
                  onChange={(value) => setFilter('currency', value)}
                  options={currencyPickerOptions}
                  emptyOptionLabel="All currencies"
                  placeholder="All currencies"
                  searchPlaceholder="Search currencies…"
                  loading={currencies.loading}
                  aria-describedby={describedBy}
                  {...(currencies.failed ? { invalid: true } : {})}
                />
              )}
            </Field>
          </div>
          {/*
            * "Quotes with work on ANY of these projects" — `lineProjectId`,
            * matched against the LINE items.
            *
            * ✅ **A MULTISELECT SINCE 2026-09-02**, on the owner's report that
            * "multiselect is not working in the projects filter". It was not
            * broken; it had never been multi. *SUPERSEDED: a single `Combobox`
            * writing one id.*
            *
            * ⚠️ **SEVERAL PROJECTS MEAN OR, AND THE ALTERNATIVE READING IS THE
            * ONE TO REFUSE.** A quote matches when at least one of its lines
            * names ANY of them, so ticking a second project can only ever ADD
            * rows — which is what a person doing it expects. An AND would make
            * the second tick shorten the list, and a shorter list after ticking
            * something reads as the control being broken rather than as a
            * narrower question.
            *
            * ⚠️ **AN EMPTY SELECTION OMITS THE KEY.** `?lineProjectId=` is a
            * **422** on this `.strict()` schema, not "no filter" — the guard is
            * in `setLineProjects` (the URL end) and in `load` (the request end),
            * because a hand-pasted URL never came through the control.
            *
            * ⚠️ **`lineProjectStatus` IS UNTOUCHED AND STILL DESCRIBES THE WHOLE
            * DOCUMENT.** It asks how many of a quote's lines name a project at
            * all — every line, including lines on projects nobody selected here.
            * So a row filtered to two projects and reading "Partly added" is
            * counting lines this filter said nothing about. Correct, and the one
            * way to misread the pair.
            *
            * 🔴 **THIS IS NOT THE `projectId` FILTER, AND THE TWO ARE ONE PREFIX
            * APART.** `projectId` asks whether the quote's HEADER names the
            * project, and a control built on it could only ever empty the table;
            * this asks whether any LINE names it, which is what somebody means.
            * The counts behind both claims are at `QuoteListParams` and are not
            * copied here. **Do not repoint this at `projectId`, and do not add a
            * second control for it.**
            *
            * ⚠️ **`projectId` KEEPS ITS "Also filtered by" BADGE AND THIS ONE
            * GETS NONE** — badge ⇔ no control. The badge row exists for filters
            * arrived at by link with nothing on screen to set them; a filter with
            * a picker already shows its value in the picker, and announcing it
            * twice invites somebody to clear one and believe the other went with
            * it. `/invoices` follows the same rule, reached independently.
            *
            * ⚠️ **`w-56`, matching Search and Expiry rather than the `w-40`
            * Currency box beside it.** Project names run long
            * (`BSF India - Summer 2027`), and the trigger truncates rather than
            * wrapping the filter row taller.
            *
            * ⚠️ **`maxVisibleChips={2}` FOR THAT SAME REASON, and neither end of
            * the range is right here.** The default of 8 is sized for a modal
            * field; eight long project names in a 224px box wrap to eight rows
            * and take the whole filter bank with them. Count-only (`0`, what the
            * narrow line-item cells use) would go the other way and drop the
            * NAME from the common case — one project chosen, the trigger reading
            * "1 project", which is strictly less than the single `Combobox` it
            * replaced showed. Two chips keeps the name while one or two are
            * picked and collapses to a count past that, where the names would
            * not have fitted anyway.
            */}
          <div className="w-full min-w-0 sm:w-56">
            <Field
              label="Filter by project"
              hideLabel
              // The invalid border alone says nothing about WHY the list is
              // empty, and an empty list here is a failure rather than an
              // answer — the same pairing the currency control uses.
              {...(projects.failed ? { error: 'Couldn’t load projects.' } : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  values={filters.lineProjectId.map(String)}
                  onChange={setLineProjects}
                  options={projectPickerOptions}
                  placeholder="All projects"
                  searchPlaceholder="Search projects…"
                  summaryNoun={{ singular: 'project', plural: 'projects' }}
                  maxVisibleChips={2}
                  loading={projects.loading}
                  aria-describedby={describedBy}
                  /*
                   * 🔴 **`'even-when-truncated'`, NOT A BARE `true` — owner's
                   * decision 2026-09-13, and the value is spelled out because
                   * the interlock it opts out of is a real guard elsewhere.**
                   * `MultiCombobox` withholds the select-all row beside a
                   * `footnote`, and the truncation footnote below **fires
                   * today** at 113 active projects against a `perPage` ceiling
                   * of 100 — so a bare `true` would draw nothing here, ever,
                   * on the control the owner asked for it on.
                   *
                   * ⚠️ **What makes the carve-out honest is that THIS IS A
                   * FILTER.** Ticking the 100 that loaded narrows the list to
                   * 100 projects and the trigger says so — it reads a COUNT,
                   * never the word "all" — and the truncation footnote stays
                   * on screen beside it. ⛔ **The same value on a line-item
                   * cities or inclusions picker would be a STORED set claiming
                   * completeness it does not have, on a document a customer is
                   * billed against.** Never carry it there.
                   */
                  selectAll="even-when-truncated"
                  /*
                   * ⚠️ **A fact about the DATA, not helper text** — the one
                   * footnote shape the no-helper-text rule names as an exception.
                   * `/projects` caps `perPage` at 100 and there is no "give me
                   * all", so past the hundredth project this list is silently
                   * short — and a picker missing its 101st option is
                   * indistinguishable from that project not existing.
                   *
                   * 🔴 **IT FIRES TODAY.** *SUPERSEDED: "It cannot fire today
                   * (31 projects) and it stays. This is the branch that turns a
                   * silent truncation into a visible one on the day somebody
                   * adds the hundred-and-first, which is a day nobody will
                   * connect to this control."* **Measured 2026-09-12: 113 ACTIVE
                   * projects**, so `hasMore` is true and this footnote is on
                   * screen — and the day it describes came and went with nothing
                   * failing and nobody connecting it to this control, exactly as
                   * the superseded sentence said it would.
                   *
                   * ⚠️ **The footnote is now the ONLY visible sign of a defect it
                   * does not name**: past the hundredth project, `projectOptions`
                   * labels a page-2 project "(no longer offered)" — see the block
                   * at `projectPickerOptions`. Reported, live, and deliberately
                   * not patched here; it is a shared helper with five consumers.
                   */
                  {...(projects.hasMore
                    ? { footnote: 'Showing the first 100 active projects.' }
                    : {})}
                  {...(projects.failed ? { invalid: true } : {})}
                />
              )}
            </Field>
          </div>
          {/*
            * Wider than the single controls beside it because it is two boxes,
            * and last in the bank because it is the one somebody sets after
            * they already know which quotes they are looking at.
            *
            * Its two bounds are INCLUSIVE and are ordinary query params, so the
            * range is shareable, survives a refresh and is saved against the
            * account like every other filter here.
            */}
          <div className="w-full min-w-0 sm:w-80">
            <IssueDateRangeFilter
              from={filters.issueDateAfter ?? ''}
              to={filters.issueDateBefore ?? ''}
              onFrom={(value) => setIssueDate('issueDateAfter', value)}
              onTo={(value) => setIssueDate('issueDateBefore', value)}
            />
          </div>
          {/*
            ⚠️ THE "ALL OWNERS" FILTER STOOD HERE; THE SLOT IS DELIBERATELY
            EMPTY. Owner is gone from quotes (owner's instruction, 2026-08-31)
            and `ownerId` is not a member of `ID_FILTERS`, so a stale
            `?ownerId=` in a URL is read as absent rather than honoured.
          */}
          {/*
            * THE COLUMN MANAGER — show/hide, freeze either edge, reorder.
            *
            * ⚠️ **`hidden md:flex`, because BELOW `md` THERE IS NO TABLE.** The
            * narrow layout is a stacked list of cards with no columns at all, so
            * a Columns control there would be an affordance whose only outcome
            * is nothing — the same rule that keeps Xero's `sync` button off the
            * page and DocuSign's connect button off the integrations card. It
            * matches the `md:hidden` / `hidden md:block` split the two row
            * layouts already use.
            *
            * ⚠️ **AND IT IS HIDDEN ON THE BOARD, for the same reason.**
            * `?view=board` renders kanban cards, not a table; the control would
            * still SAVE (the preference is stored against the account, not the
            * view) but nothing on screen would move, which reads as broken.
            *
            * ⚠️ **`items-end` puts it on the baseline of the controls beside
            * it.** The bank is `items-start` because its `Field`s carry labels
            * this one does not have — without this the button would float to the
            * top of a row whose inputs sit lower.
            *
            * ⚠️ **NO SENTENCE BESIDE IT.** The trigger's own badge says how many
            * columns are hidden, which is the one thing a reader cannot see by
            * looking at the table; anything more would be helper text attached
            * to a control, which is prohibited. The two `title`s inside the
            * panel are the allowed exception — both say why a checkbox is
            * DISABLED.
            */}
          {!showBoard && (
            <div className="hidden w-full min-w-0 items-end sm:w-auto md:flex">
              <ColumnManager
                layout={columnLayout}
                labelOf={quoteColumnLabel}
                lockedKeys={QUOTE_LOCKED_COLUMNS}
                lockReasonOf={quoteLockReasonOf}
                pinnedKeys={QUOTE_PINNED_COLUMNS}
              />
            </div>
          )}
        </div>

        {/*
          * Contact, company and deal are filterable on the API but have no
          * control here: they are arrived at by link from those records. They
          * still have to be visible, or the list looks short for no reason.
          */}
        {(filters.contactId !== null ||
          filters.companyId !== null ||
          filters.dealId !== null ||
          filters.accountLeadId !== null ||
          filters.projectId !== null) && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
            <span className="text-xs text-fg-muted">Also filtered by</span>
            {filters.companyId !== null && <Badge tone="neutral" variant="outline">Company #{filters.companyId}</Badge>}
            {filters.contactId !== null && <Badge tone="neutral" variant="outline">Contact #{filters.contactId}</Badge>}
            {filters.dealId !== null && <Badge tone="neutral" variant="outline">Deal #{filters.dealId}</Badge>}
            {/* Says which relationship it scoped by, not just "mine": account
                lead and owner are different people, and a reader who assumes
                the wrong one reads an empty list as a fault. */}
            {filters.accountLeadId !== null && (
              <Badge tone="neutral" variant="outline">
                {accountLeadName === null
                  ? `Account lead #${filters.accountLeadId}`
                  : `Account lead: ${accountLeadName}`}
              </Badge>
            )}
            {/* Named where a row could name it, and by id where none could —
                the list is usually empty under this filter, and an id is still
                an identifier where a blank would be nothing at all. */}
            {filters.projectId !== null && (
              <Badge tone="neutral" variant="outline">
                {projectName === null ? `Project #${filters.projectId}` : `Project: ${projectName}`}
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        )}

        {/*
          * The filters on this page the board cannot take. Said next to the
          * controls that would otherwise imply they had been applied to both
          * views — a filtered table and an unfiltered board under one heading is
          * exactly the "it looks like it worked" failure this is fixing.
          *
          * ⚠️ **The issue-date range joined the project filter here on
          * 2026-09-02**, and the Projects column later the same day. This
          * sentence GROWS rather than gaining a neighbour: two notices a line
          * apart, each naming one filter, read as two different faults.
          *
          * ⚠️ **THE NESTED TERNARY THAT DID THIS FOR TWO NAMES IS GONE**, and
          * that is not a tidy-up. It was already 2 branches for 2 filters and
          * would have been 7 for 3 — the shape where the next person adds a
          * filter, updates `boardParams`, and quietly does not update every
          * branch here. `unappliedOnBoard` is one list, so a name can only be
          * missing by being missing.
          *
          * The list is derived from what `boardParams` withholds — see the block
          * inside that memo.
          */}
        {showBoard && unappliedOnBoard.length > 0 && (
          <div className="border-b border-border px-4 py-2">
            <p className="m-0 text-xs text-fg-muted">
              The board is <strong className="font-semibold">not</strong> narrowed by the{' '}
              {formatList(unappliedOnBoard)}{' '}
              {unappliedOnBoard.length === 1 ? 'filter' : 'filters'}. Switch to Table for that.
            </p>
          </div>
        )}

        {/*
          * WHAT THE SIGNATURE COLUMN IS AND IS NOT.
          *
          * Three independent notes, rendered as three elements rather than
          * coalesced with `??`. This repo's recurring silent-drop bug is a
          * coalesce between two things that can BOTH be true — the truncation
          * bound and the consequence sentence are exactly such a pair, and
          * showing one of them would drop a fact rather than tidy a layout.
          *
          * Table view only: the board carries no signature state, so a note
          * explaining one would describe a column that is not there.
          */}
        {!showBoard && signatures.kind === 'error' && (
          <div className="border-b border-border px-4 py-2">
            <p className="m-0 text-xs text-fg-muted">
              <strong className="font-semibold text-fg-secondary">
                Signature state couldn&rsquo;t be loaded.
              </strong>{' '}
              {signatures.message} The column reads &ldquo;Unknown&rdquo; rather than blank.
            </p>
          </div>
        )}

        {/*
          * ⚠️ THE COVERAGE BOUND. One request fetches the 100 most recent
          * outstanding envelopes; past that we hold some and not others, and a
          * quote on this page can be out for signature with nothing in its
          * cell. A list quietly missing its 101st envelope is indistinguishable
          * from that envelope not existing, and the person who cannot find it
          * has no way to tell which — so the bound is stated rather than left
          * to look like an answer.
          *
          * In practice this stays hidden: an outstanding envelope is one a
          * person is waiting on, and they drain as people act. It is the
          * unbounded case that must never be silent.
          */}
        {!showBoard && signatures.kind === 'ready' && signatures.unfetched > 0 && (
          <div className="border-b border-border px-4 py-2">
            <Notice tone="warning">
              <p className="m-0">
                <strong className="font-semibold">
                  Signature state is incomplete on this page.
                </strong>{' '}
                {signatures.unfetched === 1
                  ? '1 more quote is out for signature'
                  : `${signatures.unfetched} more quotes are out for signature`}{' '}
                than could be checked here. Open a quote to be certain.
              </p>
            </Notice>
          </div>
        )}

        {/*
          * The consequence, from the ONE copy of it in `@/lib/docusign`. Shown
          * only while a row on screen is actually out for signature.
          *
          * It is not decoration: a completed signature is the one route into the
          * accepting stage with NO confirmation step — the customer signing is
          * the confirmation — so a quote sitting at "Sent" here can change stage
          * with nobody at a keyboard. That is a specific wrong conclusion this
          * list would otherwise invite, which is the test for copy that stays.
          *
          * ⚠️ SUPERSEDED 2026-08-30, kept because the correction is the point:
          * this said a signature was "one signature away from a real invoice and
          * a real Xero draft". Release `20260829011913-5fbab06` removed the
          * acceptance chain — accepting a quote now MOVES THE STAGE AND NOTHING
          * ELSE, and a completed envelope additionally attaches the signed PDF
          * to the Xero quote. No invoice, no Xero draft, from either route.
          *
          * The sentence was accurate when written and was falsified by a change
          * in another repo, with nothing here able to fail — the reason this
          * file keeps the old wording beside the new rather than overwriting it.
          * ⚠️ It also failed in the REASSURING direction's opposite: it promised
          * a money document nobody was going to get, so the person reading it
          * would have gone looking for an invoice that was never raised.
          */}
        {!showBoard && outstandingOnPage > 0 && (
          <div className="border-b border-border px-4 py-2">
            <p className="m-0 text-xs text-fg-muted">{SIGNATURE_ACCEPTS_QUOTE_NOTE}</p>
          </div>
        )}

        {/* The board keeps its own live region; two would talk over each other. */}
        {!showBoard && (
          <p role="status" aria-live="polite" className="sr-only">
            {countLabel}
          </p>
        )}

        {!showBoard && error && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load quotes.</>}>
              <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}

        {/*
          * The board is mounted in BOTH views and merely hidden in the table
          * one, which is exactly what its `enabled` prop is for: `useKanban`
          * fetches nothing while disabled and derives its busy flags from
          * `enabled` during render, so a warm board survives a round trip to
          * the table without spending another request on the shared IP budget.
          * Unmounting it would refetch on every toggle.
          *
          * The table is the other way round — unmounted rather than hidden —
          * because everything it holds (`result`, `page`, `search`) lives in
          * this component and survives regardless. `min-w-0` so the board's own
          * horizontal scroller stays inside the panel at 320px.
          */}
        <div className={cx('min-w-0 px-4 py-3', !showBoard && 'hidden')}>
          {/*
            * A card's Convert could not READ its quote — see
            * `openConvertFromCard`. Distinct from every other failure this page
            * reports: the modal owns the conversion and its refusals, but it
            * does not exist yet at this point, so without a banner the button
            * would simply stop spinning and open nothing, which is
            * indistinguishable from a dead control.
            *
            * Cleared by the next press rather than by a dismiss control. The
            * message names the card, so a second failure on a different card
            * replaces it with one that is true rather than stacking two.
            */}
          {cardConvertError && (
            <Notice tone="danger" role="alert" className="mb-3">
              <span className="min-w-0 [overflow-wrap:anywhere]">{cardConvertError}</span>
            </Notice>
          )}
          {/*
            * ⚠️ NO SIGNATURE STATE ON THE BOARD, DELIBERATELY.
            *
            * `KanbanBoard` renders `KanbanCard` itself and serves FIVE boards
            * off one payload, and the rule that has kept deals, quotes,
            * invoices, deliverables and companies from drifting apart is that
            * nothing inside it branches on the board name. A signature badge on
            * a quote card can only be put there by breaking that rule, or by
            * widening the card for four boards that have no envelopes — and the
            * board is not this page's to change.
            *
            * Not surfaced as a notice in the UI, and the distinction is worth
            * keeping: the project-filter note below the badge exists because a
            * filter LOOKS applied to both views and is not, which is a false
            * impression. An absent decoration makes no claim. The table is where
            * "which quotes are out for signature?" gets answered — one click
            * away on the toggle now that the board is what loads first; the
            * board answers a different question, about stage.
            *
            * The envelope request is skipped entirely while this is showing —
            * see the effect — so the board costs nothing on the shared rate
            * limit for a column it does not render.
            */}
          <KanbanBoard
            board="quotes"
            params={boardParams}
            hrefFor={quoteHref}
            /* PATCH /quotes/:id/stage is WRITE_ROLES, not admin. */
            canMove={canUpdate}
            enabled={showBoard}
            /*
             * The SAME modal the header button and the table rows open —
             * `setFormState({})` is the one way this page opens a create form,
             * so there is no second piece of state to drift. Omitted outright
             * without `canCreate`, which is what hides the control from a
             * viewer rather than showing them a 403 in waiting.
             */
            create={
              canCreate
                ? {
                    label: 'New quote',
                    onCreate: () => setFormState({}),
                    refreshToken: boardRefresh,
                  }
                : undefined
            }
            /*
             * ✅ **CONVERT NOW SHIPS ON THE CARD, and this is the note that used
             * to explain why it could not.**
             *
             * The gate is the table's, unchanged, both halves:
             *
             *  1. `ability.canConvertQuote()` — the ROLE.
             *     `POST /quotes/:id/convert` is `requireRole(...WRITE_ROLES)`,
             *     so this is NOT an admin check and must never be narrowed to
             *     one. It is also not `/permissions/me`: the seeded `basic-user`
             *     set has no `quotes.convert` while the route takes any write
             *     role, so the permission list would hide a working button —
             *     confirmed live, that account's convert returns 422, not 403.
             *     Omitted outright rather than disabled, exactly as `create` is,
             *     so a viewer gets no control instead of a 403 in waiting.
             *  2. `billingMode !== 'staged'` — the half that used to be
             *     unanswerable from a card. The quotes board now ships it
             *     (backend `97cfa55`): `"full" | "staged"`, never null on THIS
             *     board and ABSENT on the other four.
             *
             * ⚠️ `=== 'staged'` and never `?? 'full'`. On a board that does not
             * carry the field the coalesce would read as "safe to convert",
             * inventing a fact; the equality is false for `undefined`, so it can
             * only ever fail towards the VISIBLE wrong answer — an enabled
             * button that collects a 409 — never towards silently converting
             * something that must not be. It is a FIELD, not a filter:
             * `?billingMode=` on the board query is still a 422, which is why
             * nothing was added to `boardParams`.
             *
             * ⚠️ It opens the SAME `pendingConvert` state and the SAME
             * `InvoiceFormModal` as the table row — not a second path. Two
             * implementations of one refusal drift, which is the whole reason
             * the request itself lives in the modal; a board-only convert would
             * put that hazard straight back. The only difference is the read in
             * `openConvertFromCard`, and it exists solely because a card is not
             * a `Quote`.
             *
             * DRAG SUPPRESSION IS ALREADY HANDLED and must not be re-done here.
             * `KanbanCard` wraps whatever this returns in the
             * `data-kanban-no-drag` block, and `KanbanBoard` watches for a
             * pointerdown inside it and takes `draggable` off the `<li>` for the
             * duration of the press — which is the only thing that works, since
             * the drag source is the nearest draggable ANCESTOR and a
             * `draggable={false}` child is still dragged with the card.
             *
             * The ALREADY-BILLED refusal is still not pre-checked, here or on
             * the table: it would cost a `GET /invoices?quoteId=` per card
             * against a 12,000-per-60s (⚠️ not the 120 this once said) limit
             * shared across the office — a per-card lookup is the wrong shape
             * whatever the budget. The 409 is
             * the guarantee, and the modal renders it in the server's own words.
             */
            /*
             * Preview PDF sits BESIDE Convert (owner 2026-10-25) and is always
             * offered — its gate is `quotes.read`, which this page already
             * requires. A card carries no Xero link; the viewer handles a quote
             * that is not in Xero itself.
             */
            cardActionCount={canConvert ? 2 : 1}
            cardAction={(card) => (
              <span className="flex items-center gap-1">
                <PreviewQuotePdfButton
                  target={{
                    id: card.id,
                    quoteNumber: card.reference,
                    subject: card.title,
                    companyName: card.company ? documentCustomerName(card.company) : null,
                  }}
                  onPreview={pdfPreview.open}
                  variant="card"
                />
                {canConvert && (
                    <Button
                      variant="secondary"
                      size="sm"
                      iconOnly
                      leadingIcon={<InvoiceIcon />}
                      /*
                       * `secondary`, not the table's `ghost`. The table row
                       * gives a button a settled background to sit on; this one
                       * is absolutely positioned over a card that carries a
                       * title, a company and a badge beneath it, so a ghost
                       * control reads as part of the content rather than as a
                       * control. The companies board's card action makes the
                       * same call for the same reason.
                       */
                      loading={cardConvertId === card.id}
                      /*
                       * The label NAMES THE CARD, because a board shows many at
                       * once and "Convert to an invoice" repeated down a column
                       * is anonymous to a screen reader.
                       */
                      aria-label={`Convert ${cardLabel(card)} to an invoice`}
                      /*
                       * On a staged card the reason REPLACES the label rather
                       * than sitting beside it — an icon-only button has one
                       * `title`, and on a quote that can never convert the
                       * reason is the more useful of the two. `aria-label` still
                       * names the action, so the control is never anonymous.
                       * The sentence is imported, never retyped: it is the one
                       * copy of a refusal the server also states.
                       */
                      title={
                        card.billingMode === 'staged'
                          ? STAGED_CONVERT_REASON
                          : `Convert ${cardLabel(card)} to an invoice`
                      }
                      disabled={card.billingMode === 'staged'}
                      onClick={() => void openConvertFromCard(card)}
                    />
                )}
              </span>
            )}
          />
        </div>

        {/*
          * ⚠️ **`XeroPushReportBanner` rendered here until 2026-08-31** — the one
          * place a push's outcome, including the server's own refusal text, was
          * shown. It went with the column that produced it: with no push raised
          * from this page there is no outcome to report.
          *
          * ⚠️ **The banner component is NOT gone and must not be deleted.**
          * `InvoiceFormModal` still renders it, for the push a SAVE performs.
          */}

        {/*
          * The column funnel is set from the table header at `md` and up and
          * from the `FilterSheet` below it, so its effect is announced here —
          * a filter set on a wide screen must not go on silently hiding rows on
          * a narrow one, and the pager's "1–20 of 339" describes the REQUEST,
          * never what survived the funnel.
          *
          * 🔴 **THE UNQUALIFIED "this page only" WAS FALSE THE MOMENT A SERVER
          * COLUMN EXISTED, and this comment ARGUED FOR LEAVING IT THAT WAY.**
          * *SUPERSEDED: "It is page-scoped without qualification: `QUOTE_COLUMNS`
          * has no `scope: 'server'` entry, so there is no 'this one narrows the
          * whole table' case to word around."* The Projects funnel is
          * `scope: 'server'` (2026-09-02) and narrows the WHOLE list, so the old
          * sentence told a reader their filter had touched twenty rows when it
          * had touched three hundred — wrong in the direction that makes
          * somebody re-filter something already filtered.
          *
          * ⚠️ **It was a claim about a sibling constant, checked once and then
          * true only until that constant changed.** Nothing fails when a column
          * gains a scope; the sentence just stops being true. `scopeSentence` is
          * derived from what was actually SENT, so it cannot go stale the same
          * way — the same shape `DealsPage` already uses for its one server
          * column.
          *
          * The *clear* button is the wide-screen half only — below `md` the
          * sheet carries its own, and two same-labelled buttons a screen apart
          * read as two different scopes.
          */}
        {!showBoard && table.activeCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
            <p className="m-0 min-w-0 text-xs text-fg-muted">
              {table.activeCount} column {table.activeCount === 1 ? 'filter' : 'filters'} applied.{' '}
              {columnScopeSentence}
              {hiddenByColumns > 0
                ? ` ${hiddenByColumns} loaded ${hiddenByColumns === 1 ? 'quote is' : 'quotes are'} hidden.`
                : ''}
            </p>
            <span className="hidden md:contents">
              {/* `clearColumnFilters`, not `table.clearAll` — the Stage funnel's
                  selection is in the URL and the hook cannot reach it. */}
              <Button variant="ghost" size="sm" onClick={clearColumnFilters}>
                Clear column filters
              </Button>
            </span>
          </div>
        )}

        {!showBoard && (
          <div
            aria-busy={loading || undefined}
            className={cx(
              'transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && result && 'opacity-60',
            )}
          >
            {/* Seven columns will not fit 320px, so rows become a stacked list.
                (This said "Nine" while the Owner and Xero columns were here;
                both went on 2026-08-31.) */}
            <div className="md:hidden">
              {/*
                * A card has no header row, so the table header's funnel — and
                * the sort that travels with it — have nowhere to hang below
                * `md`. `FilterSheet` is their mobile form, handed the SAME
                * `headerFor` and `clearAll` from the one `useTableFilters` call
                * above, so a filter set here is the filter the table shows when
                * the viewport widens.
                *
                * ⚠️ Without it the sort would be desktop-only and a funnel set
                * at 1440 would keep hiding rows at 375 with no way to clear it —
                * an affordance that is unreachable reads as a removed feature,
                * which is the failure the board's `cardAction` exists to avoid.
                */}
              {/* The bar owns the border and padding; FilterSheet's className lands on its
                  trigger Button, so styling a bar through it would double the padding. */}
              <div className="border-b border-border px-4 py-2">
                {/* The same `clearColumnFilters` the wide-screen button uses —
                    two buttons a breakpoint apart must not clear two different
                    sets. */}
                <FilterSheet
                  keys={QUOTE_COLUMN_KEYS}
                  headerFor={headerFor}
                  activeCount={table.activeCount}
                  clearAll={clearColumnFilters}
                />
              </div>
              {initialLoad ? (
                <ListSkeleton />
              ) : visibleRows.length === 0 ? (
                emptyState
              ) : (
                <ul className="m-0 list-none p-0">
                  {visibleRows.map((quote) => (
                    <li key={quote.id} className="border-b border-border px-4 py-3 last:border-b-0">
                      <div className="flex items-start justify-between gap-3">
                        <SubjectCell quote={quote} />
                        {/* `shrink-0` and a column: at 320px two badges side by
                            side would squeeze the subject to nothing. Stacking
                            them keeps both readable and the subject wrapping. */}
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <StageBadge stage={quote.stage} stages={stages.stages} />
                          {/* `ImportedFromXeroBadge` stood here and moved down
                              beside the Projects badge on 2026-09-02, mirroring
                              the column move on the table. Both layouts changed
                              in the one edit: a badge left in the stage stack on
                              the card alone is the card/cell disagreement this
                              page already warns about twice. */}
                          {/* Only the states that carry information — see
                              `hasSignatureToShow`. */}
                          {hasSignatureToShow(signatures, quote.id) && (
                            <SignatureCell state={signatures} quoteId={quote.id} />
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                        {/* Always with its currency code: there is no exchange
                            rate in this system, so a bare number is an invitation
                            to add two currencies together. `currency` is null on
                            every imported quote and `InvoiceMoney` then omits the
                            code rather than guessing one. */}
                        <span className="text-fg-secondary">
                          <InvoiceMoney value={quote.grandTotal} currency={quote.currency} />
                        </span>
                        <span className="text-fg-muted">
                          <ValidTillCell quote={quote} />
                        </span>
                      </div>
                      <p className="mt-1 m-0 text-xs text-fg-muted tabular">
                        Issued {formatDateOnly(quote.issueDate)}
                      </p>
                      {/* The Projects badge, on the card as well as in the cell.
                          The two layouts are the SAME list at two widths, and a
                          column that exists only on one is how a person on a
                          phone concludes a document has no state at all — the
                          card/cell disagreement the shared component exists to
                          prevent. */}
                      {/*
                        * Projects, then From Xero — the same order as the two
                        * columns on the table, so the card and the row read
                        * alike.
                        *
                        * ⚠️ **THE CARD KEEPS THE BADGE WHERE THE TABLE PRINTS THE
                        * WORD, and that is deliberate rather than an oversight.**
                        * A card has no header row, so a bare "No" identifies
                        * nothing — it would be a word floating beside a badge,
                        * naming no column. The badge renders for an imported
                        * quote and nothing for one created here, which is the
                        * same fact in the form a card can carry, and it is
                        * already the single copy of that rendering (four mount
                        * points; see its header).
                        *
                        * ⚠️ **The FILTER and the SORT are not lost below `md`** —
                        * that is the half that would really be a removed
                        * feature. `FilterSheet` above is handed the same
                        * `headerFor` and the same `QUOTE_COLUMN_KEYS`, which is
                        * DERIVED from `QUOTE_COLUMNS`, so this column and Stage
                        * arrived in the sheet with nothing to remember to add.
                        */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <LineProjectStatusCell status={quote.lineProjectStatus} />
                        <ImportedFromXeroBadge importedAt={quote.importedFromXeroAt} />
                      </div>
                      {/* The card's `XeroRowPushControl` — the same component the
                          desktop cell mounted, in `card` layout — stood here and
                          went with the column on 2026-08-31. Removed on BOTH
                          layouts in the one change: a control left on the card
                          alone is exactly the card/cell disagreement its shared
                          `variant` existed to prevent. */}
                      {/*
                        * Tasks-and-notes, Send for signature and Convert, all
                        * OUTSIDE `showRowActions`. ⚠️ *This used to read "the same
                        * precedent the Xero control above sets, and for the same
                        * reason" — that control went on 2026-08-31, so the
                        * precedent is cited from the rule itself now rather than
                        * from a neighbour that is no longer there.* That gate
                        * means "can edit OR can delete", and none of these asks
                        * that question:
                        *
                        *  - tasks and notes is UNGATED outright. The task and
                        *    note routers carry no role guard, so a `viewer` who
                        *    can open this list may write against a quote in it.
                        *    Putting it inside `showRowActions` would hide a
                        *    capability that works;
                        *  - Send for signature is gated on `canUpdate` alone and
                        *    renders DISABLED with its reason rather than
                        *    vanishing — see `sendSignatureDenial`;
                        *  - Convert has its own, NARROWER gate —
                        *    `canConvertQuote()` — and states its own reason when
                        *    it is disabled, which is the whole point: a hidden
                        *    control cannot say why it is not there.
                        *
                        * One row rather than three, so a 320px card does not grow
                        * a button bank per gate.
                        */}
                      <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          leadingIcon={<ActivityIcon />}
                          onClick={() => setActivityFor(quote)}
                        >
                          Tasks and notes
                        </Button>
                        {/* The SAME component as the desktop cell, in `card`
                            layout. It sits in this ungated row rather than the
                            Edit/Delete one below, because the PDF route takes no
                            role. */}
                        <PreviewQuotePdfButton
                          target={previewTargetOf(quote)}
                          onPreview={pdfPreview.open}
                          variant="card"
                        />
                        <DownloadPdfButton
                          kind="quote"
                          document={quote}
                          reference={quote.subject}
                          variant="card"
                        />
                        {/*
                          * The SAME component as the desktop cell in `card`
                          * layout, not a second rendering of the idea. (⚠️ *This
                          * said "exactly as the Xero control above does it" until
                          * 2026-08-31, when that control was removed.*) It
                          * resolves to the
                          * plain Send button or to the three-item menu, and it
                          * makes that choice ONCE for both layouts, so a card and
                          * a cell cannot disagree about what the row can do.
                          */}
                        <RowSignatureActions
                          quote={quote}
                          signatures={signatures}
                          sendDenial={sendSignatureDenial(quote)}
                          canManage={canSend}
                          manageDenial={signatureManageDenial}
                          variant="card"
                          onSend={(target) => setSendingSignature({ quote: target })}
                          onReminded={afterReminded}
                          onChangeSigner={(target, envelope) =>
                            setChangingSignerFor({ quote: target, envelope })
                          }
                        />
                        {canConvert && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<InvoiceIcon />}
                            /*
                             * `billingMode` is a required field on `Quote`, so
                             * this is answerable from the row and the refusal is
                             * preventable rather than merely reportable. The
                             * ALREADY-BILLED refusal is not answerable here and
                             * is left to the 409 — see the gating block above.
                             */
                            disabled={quote.billingMode === 'staged'}
                            {...(quote.billingMode === 'staged'
                              ? { title: STAGED_CONVERT_REASON }
                              : {})}
                            onClick={() => setPendingConvert(quote)}
                          >
                            Convert
                          </Button>
                        )}
                      </div>
                      {/* Edit and Delete are gated separately now: a write-role
                          user edits but cannot delete, so the two are no longer
                          one admin check. */}
                      {showRowActions && (
                        <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
                          {canUpdate && (
                            <Button
                              variant="secondary"
                              size="sm"
                              leadingIcon={<PencilIcon />}
                              onClick={() => setFormState({ quote })}
                            >
                              Edit
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="danger"
                              size="sm"
                              leadingIcon={<TrashIcon />}
                              onClick={() => void openDelete(quote)}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="hidden md:block">
              {/*
                * 🔴 **ONE TABLE MOUNT, NOT TWO — and the swap is INSIDE it.**
                * This used to be `initialLoad ? <TableWrap>…skeleton</TableWrap>
                * : rows.length === 0 ? emptyState : <TableWrap>…rows</TableWrap>`,
                * which rendered the header TWICE from two sibling branches. The
                * three outcomes are unchanged — loading shows the skeleton, no
                * rows shows the empty state, rows show the rows — and what
                * changes is that the `<thead>` is now ONE element that survives
                * the load transition.
                *
                * ⚠️ **THAT IS A REQUIREMENT OF FREEZING, not a tidy-up.**
                * `useFrozenOffsets` MEASURES the header row and observes it for
                * resizes. Across the old two-branch swap the observed row was
                * unmounted and replaced by a different element, and the hook's
                * effect has no reason to re-run — it depends on the visible keys
                * and the frozen sides, neither of which changes when rows
                * arrive. So a frozen column's offset would have been measured
                * against the SKELETON's header and then kept, silently, with the
                * observer watching a detached node. Nothing errors; the frozen
                * column just sits a few px off and never corrects.
                *
                * ⚠️ It also retires the "the two `min-w-` literals must agree"
                * hazard the note below spends a paragraph on — there is one
                * mount, so there is nothing to keep in step.
                */}
              {initialLoad || visibleRows.length > 0 ? (
                <TableWrap frozenHeader>
                  {/* 1080, + the `w-44` signature column (176px) = 1256, + the
                      `w-44` Xero column (176px) = 1432, + the Actions column
                      widening from `w-28` to `w-44` (112px → 176px, so +64px)
                      = 1496, + Actions widening again from `w-44` to `w-56` for
                      the fifth button, Send for signature (176px → 224px, so
                      +48px) = 1544, − the `w-44` Owner column removed on
                      2026-08-31 (176px) = 1368, − the `w-44` Xero column itself,
                      removed later the same day (176px) = 1192, + the `w-36`
                      Projects column (144px) = 1336.
                      Moved with the column rather than left to be re-guessed:
                      the two numbers are the one thing here that can silently
                      drift apart, and the skeleton must match the real table
                      below or the layout jumps as the rows arrive.
                      ⚠️ Actions is a FIXED width sized for its worst case, not
                      one that follows `actionCount` — a width varying with the
                      signed-in role would make one page two widths for two
                      people. The per-button arithmetic is on the `<TH>`.

                      🔴 *SUPERSEDED: "a FIXED `w-56` sized for five buttons …
                      the per-button arithmetic behind 224px".* **It is 246 and
                      it is sized for SIX** — the fifth-button step recorded in
                      this chain was already one behind when it was written,
                      because `DownloadPdfButton` renders in the same lane. ⚠️
                      **Every figure in the delta chain above it is therefore
                      history rather than arithmetic to extend**: the floor is
                      DERIVED by `tableMinWidth` now and this comment cannot move
                      it. **Re-sum the declarations; never carry this chain
                      forward by another delta** — that is how it came to be
                      wrong in the first place.

                      ⚠️ **1336 -> 1464 on 2026-09-02**, the whole of the +128
                      being the new `w-32` From Xero column. Nothing else was
                      re-cut, which is what makes the change auditable: one
                      column added, one number moved by exactly its width, in the
                      two places this literal appears.

                      ⚠️ **THIS IS A FLOOR, NOT A SUM, and this table is not the
                      quote LINE grid.** There is no `table-fixed` and no
                      `<colgroup>` here, so a `<th>` width is a request the auto
                      layout may exceed rather than an authority. The EIGHT fixed
                      columns are

                        Stage 160 · Awaiting 176 · Issue date 128 · Projects 144 ·
                        From Xero 128 · Valid till 192 · Grand total 160 ·
                        Actions 224                                      = 1312

                      leaving the remainder as floor for the FLUID Quote column.
                      Do NOT carry the line grid's "the sum IS the min-w" rule
                      across — measure this file.

                      ⚠️ **1464 -> 1616 on 2026-09-10, owner's request: DOUBLE THE
                      QUOTE COLUMN.** The whole of the +152 is that column and
                      nothing else was re-cut, which is what keeps the change
                      auditable — one column widened, one number moved by exactly
                      its increase, in the two places this literal appears.

                      ⚠️ **IT IS WIDENED HERE AND NOT ON THE HEADER, and that is
                      the only place it CAN be done.** The Quote `<th>` carries no
                      `className` on purpose (see `QuotesTableHead`); it is the
                      one fluid column and takes whatever the eight fixed ones
                      leave. So its width is this literal minus 1312 — 152px
                      before, 304px now — and putting a `w-` on the header
                      instead would make it a ninth FIXED column and leave the
                      table with no fluid one at all, which is what stops the
                      layout from filling a wide screen.

                      🔴 **THE LITERAL IS GONE AND THE ARITHMETIC IS NOT.** The
                      floor is computed by `tableMinWidth` from the columns
                      actually visible, and at full visibility it is still
                      exactly 1616 — every number above is preserved, with one
                      correction the note itself needed: the fixed sum is 1328
                      (Projects is `w-40`, not the `w-36` this text still lists)
                      and the Quote floor is therefore 288, not 304. Same total,
                      both terms corrected.

                      ⚠️ **It is an inline `style`, and it has to be**: a
                      computed `min-w-[…]` class emits no CSS rule at all, so the
                      table would silently lose its floor. See `tableMinWidth`.

                      🔴 **AND IT SITS ON `<Table>` NOW.** ⚠️ *SUPERSEDED: "**AND
                      IT SITS ON A WRAPPER, NOT ON `<Table>`, because `Table`
                      TAKES NO `style` PROP** … ⛔ Do not 'simplify' this by
                      moving the style onto `<Table>`; widening that shared
                      primitive is a fifteen-page change and this page may not
                      make it."* The primitive takes a `style` as of 2026-09-12,
                      and takes it precisely because three adopting pages had each
                      wrapped their table in a spacer `<div>` to carry this one
                      number. The wrapper is gone and the floor sits on the
                      element it describes. **The prohibition was right on the day
                      and is now an instruction to restore a workaround.**

                      ⚠️ **The "two places this literal appears" warning is
                      retired by construction** — the skeleton and the real table
                      now read the same `tableStyle`, so they cannot drift. */}
                  <Table style={tableStyle}>
                    <QuotesTableHead
                      headerFor={headerFor}
                      visibleKeys={tableColumnKeys}
                      frozenSideOf={columnLayout.frozenSideOf}
                      frozenOffsets={frozenOffsets}
                      headRowRef={headRowRef}
                    />
                    {initialLoad ? (
                      <TableSkeleton
                        actionCount={actionCount}
                        visibleKeys={tableColumnKeys}
                        frozenSideOf={columnLayout.frozenSideOf}
                        frozenOffsets={frozenOffsets}
                      />
                    ) : (
                      <TBody>
                        {visibleRows.map((quote) => (
                          <TR key={quote.id} interactive>
                            {tableColumnKeys.map((key) => bodyCell(key, quote))}
                          </TR>
                        ))}
                      </TBody>
                    )}
                  </Table>
                </TableWrap>
              ) : (
                emptyState
              )}
            </div>
          </div>
        )}

        {/*
          * No totals row. Rows on one page can be in several currencies — and
          * `currency` is null outright on the imported quotes — with no
          * exchange rate anywhere in the system, so any sum shown here would
          * either be wrong or need a per-currency breakdown nobody has asked
          * for yet. Deliberately left out.
          *
          * Hidden on the board: `page` and `perPage` are not board keys — the
          * board is bounded by `cardsPerColumn` instead — so a pager here would
          * be a control with nothing behind it.
          */}
        {!showBoard && result && (
          <ListPager
            page={page}
            totalPages={totalPages}
            total={total}
            firstRow={firstRow}
            lastRow={lastRow}
            perPage={PER_PAGE}
            showAll={showAll}
            loading={loading}
            truncated={showAll && showAllTruncated}
            noun="quotes"
            onPage={setPage}
            onToggleShowAll={(next) => {
              // Back to paging starts on page 1, never on whatever `page` held.
              if (!next) setPage(1);
              setShowAll(next);
            }}
          />
        )}
      </Panel>

      <QuoteFormModal
        open={formState !== null}
        onClose={closeForm}
        quote={formState?.quote}
        lockedCompany={formState?.lockedCompany}
        /*
         * ⚠️ `/quotes` returns no line count, and there is no cheap way to get
         * one for a whole page of rows — so an EDIT opened from here says
         * "unknown" rather than guessing, and the form locks the four header
         * totals on the strength of it.
         *
         * The guess this replaces was not neutral. Omitting the prop defaulted
         * it to `false`, which offered the four money boxes on a quote that has
         * lines: a header PATCH writes literally what it is sent and never
         * re-derives, the next line write silently replaces it, the replaced
         * figures are kept nowhere, and `convert` copies all four onto the
         * invoice verbatim. The detail page knows the count and offers the
         * inputs where they are genuinely safe.
         *
         * A CREATE is a definite `false`: a quote that does not exist yet has
         * no lines, and those four figures are the only way to record a quote
         * that will never have any. The form re-locks them the instant the
         * record comes back, because its line-items table is live from then on.
         */
        hasLineItems={formState?.quote ? 'unknown' : false}
        onSaved={handleSaved}
        // "Create and preview" (owner 2026-10-25): the form shows it on create only.
        onCreatedPreview={(created) => navigate(quotePreviewHref(created.id))}
        /*
         * ⚠️ A SECOND, NARROWER SIGNAL — deliberately not `onSaved`.
         *
         * `onSaved` means "there is a record, reload and CLOSE", and several of
         * this modal's other mounts NAVIGATE on it. This one means only "the
         * line items moved"; the form stays open because the person is still
         * in it.
         */
        onLinesSaved={handleLinesSaved}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={
          pendingDelete
            ? `Delete ${documentDisplayTitle(pendingDelete.subject, pendingDelete.xeroQuoteNumber)}?`
            : 'Delete quote?'
        }
        description={
          <DeleteWarning lineCount={pendingLineCount} invoiceCount={pendingInvoiceCount} />
        }
        confirmLabel="Delete quote"
        loading={deleting}
        error={deleteError}
      />

      {/*
        One sheet for the whole table, not one per row — a row only says WHICH
        quote it is for. `open` is derived from that, so closing keeps the quote
        for the duration of the exit and the heading never blanks.

        `subject` is trimmed before it is trusted: it is a free-text column and a
        whitespace-only value would render an invisible heading, so it falls back
        to the id.
      */}
      <ActivityDrawer
        open={activityFor !== null}
        onClose={() => setActivityFor(null)}
        target={{ type: 'quote', id: activityFor?.id ?? 0 }}
        recordLabel={activityFor?.subject.trim() || `Quote #${activityFor?.id ?? ''}`}
        viewerId={user?.id ?? null}
        viewerTimeZone={viewerZone(user?.timezone)}
      />

      {/*
        ⚠️ **THE CONFIRMATION DIALOG THAT STOOD HERE IS GONE, AND `ConvertWarning`
        WITH IT.** Convert no longer raises the invoice on a confirm — it opens
        this prefilled form, and nothing is created until Save. Most of what that
        dialog had to STATE, this form SHOWS: the four totals, the dates, the
        counterparty, and the line items themselves with their projects, cities
        and campaign inclusions.

        ⚠️ The one thing the dialog said that a form cannot show is the ALREADY
        BILLED refusal, which this page could never pre-check without a
        `GET /invoices?quoteId=` per row. It is unchanged in substance: the button
        still ships enabled, the server still refuses, and the modal renders that
        409 in the server's own words — which carry the reference somebody needs
        in order to go and look.

        ⚠️ **Do not put a ConfirmDialog back in front of this**, and do not
        pre-check the refusal with a request per row: a per-row lookup on a
        paginated list is the wrong shape, and the limiter is per IP for the
        whole office — 12,000 per 60s (⚠️ not the 120 this once said).
      */}
      <InvoiceFormModal
        open={pendingConvert !== null}
        onClose={() => setPendingConvert(null)}
        /*
         * ⚠️ `?? undefined`, because `convertFrom` is what SELECTS convert mode.
         * `open` already gates the render, and the modal remounts on the quote id
         * — so a stale quote can never seed a form for a different one.
         */
        convertFrom={pendingConvert ?? undefined}
        /*
         * The conversion resolves to an INVOICE, never a quote, and this list
         * renders fields the two do not share — so it is navigated to rather than
         * folded into `result`. Called once the conversion has landed, and also
         * when somebody closes the modal after a conversion whose follow-up edit
         * was refused: the invoice exists either way, and walking away from it in
         * silence is what produces a second one.
         */
        onSaved={afterConvert}
      />

      {/*
        SEND FOR SIGNATURE — the SAME dialog `/quotes/:id` mounts.

        Mounted conditionally rather than held at `open={false}`: the form takes
        no `open` prop, so a fresh mount is the reset and no effect can carry a
        signer's email from the last row into the next one.

        ⚠️ **EVERY GUARD IS INSIDE IT, and mounting it here loses none of them.**
        The sandbox warning in particular is the modal's own first element: it
        reads `GET /docusign/connection` itself and resolves through
        `signaturesNotBinding()`, which WARNS on a null connection — and null is
        the ordinary case for a list-page sender, because that read is admin-only
        and 403s for the write roles who send. The `ConfirmDialog` it opens
        restates it at the moment it becomes irreversible. There is exactly one
        copy of that test and this page adds no second one.

        ⚠️ The modal closes ITSELF after a successful send, so `onSent` must not
        also close it — what it has to do is make this list stop claiming the row
        has nothing outstanding. See `afterSignatureSent`.

        ⚠️ **The STAGE is not touched here.** A send moving a quote to its "sent"
        stage is being added SERVER-SIDE, gated on an `is_sent` flag on the stage
        master. There is no way to identify that stage from this app today except
        by matching its NAME, which is admin-editable free text and has misled
        this codebase three times. Do not add one.
      */}
      {/* The ONE PDF preview host for the rows, the phone cards and the board.
          Mounted before the send modal so a send opened from the viewer stacks
          above it. */}
      {pdfPreview.element}

      {sendingSignature !== null && (
        <SendForSignatureModal
          quote={sendingSignature.quote}
          /*
           * 🔴 **LOAD-BEARING, and only ever set by "Change signer and
           * resend".** Absent is not empty: `undefined` lets the dialog seed the
           * company's primary contact, which is what an ordinary Send wants,
           * while an empty value would suppress that seed. On the change-signer
           * path the primary contact is very often the person somebody has just
           * decided not to send to, so omitting this would silently undo the
           * whole operation and look like it worked.
           */
          {...(sendingSignature.initialSigner
            ? { initialSigner: sendingSignature.initialSigner }
            : {})}
          onClose={() => setSendingSignature(null)}
          onSent={afterSignatureSent}
        />
      )}

      {/*
        "CHANGE SIGNER AND RESEND" — the VOID half. The dialog owns it, because
        it is the screen that states what voiding costs: the original signer is
        emailed the reason, their link dies, and any partial signature is
        discarded. None of that is restated in the menu that opens this.

        ⚠️ **The SEND half belongs to `SendForSignatureModal`**, which is the
        only thing that knows the document payload — the anchor text, the Xero
        fetch or an uploaded PDF — so `onVoided` hands the chosen signer straight
        into it. See `afterSignerChanged`.

        `companyId` scopes the signer picker to this quote's own company; `null`
        when the quote names none, which the picker states rather than falling
        back to the unscoped directory of every contact in the CRM.
      */}
      {changingSignerFor !== null && (
        <ChangeSignerDialog
          open
          envelope={changingSignerFor.envelope}
          companyId={changingSignerFor.quote.company?.id ?? null}
          onClose={() => setChangingSignerFor(null)}
          onVoided={(voided, signer) =>
            afterSignerChanged(changingSignerFor.quote, voided, signer)
          }
        />
      )}

      {/*
        THE SIGNING LINK — a credential, not a page reference.

        Mounted only while there is an envelope for it, so closing UNMOUNTS the
        dialog and the minted URL dies with it. The link is single use and dies
        in about five minutes; it is never lifted into page state and never put
        in the URL.

        ⚠️ **CORRECTED 2026-08-31.** *SUPERSEDED: "There is deliberately NO
        resend or remind control beside it. That endpoint is being written
        concurrently and its path is not settled."* **It shipped**, and it is
        mounted — one item up in the row's signature menu, as
        `RemindEnvelopeControl`. There is still no remind control INSIDE this
        dialog, and now for a real reason rather than a missing endpoint: a
        reminder emails the signer, and this dialog exists for `embedded`
        delivery, where DocuSign emails nobody and there is nothing to re-send.

        ⚠️ **A sentence asserting the ABSENCE of a control rots the moment
        somebody builds it, and the builder is never the one reading the
        sentence.** Nothing failed while this was false.
      */}
      {signingLinkFor !== null && (
        <SigningLinkDialog
          open
          envelope={signingLinkFor}
          onClose={() => setSigningLinkFor(null)}
          /*
           * Minting is WRITE_ROLES, the same question the row's Send button
           * asked — and this dialog is only reachable through a send that has
           * already happened, so it is belt and braces rather than the only
           * check. `connection` is not passed: `GET /docusign/connection` is
           * admin-only, so fetching it per list page would 403 for most senders,
           * and omitting it makes the dialog's own sandbox warning resolve
           * through `signaturesNotBinding(null)` — which warns.
           */
          canMint={canSend}
          mintDenial={signatureManageDenial}
          /*
           * A refresh inside the dialog can observe a signature, which moves the
           * quote's stage server-side with nobody here having pressed anything.
           * Two things are stale from that moment: the envelope's own status
           * (the "Awaiting signature" column) and the quote row itself (its stage
           * may have moved), so both are re-read.
           */
          onEnvelopeRefreshed={(envelope) => {
            setSigningLinkFor(envelope);
            setSignatureRefresh((token) => token + 1);
            setListRefresh((token) => token + 1);
          }}
        />
      )}
    </>
  );
}

/**
 * The two halves of what deleting a quote actually does.
 *
 * The line items are the familiar half. The invoices are the half nobody
 * expects: they are NOT deleted and NOT protected — the FK is ON DELETE SET
 * NULL, so each one keeps its figures and loses the only record of where those
 * figures came from. Naming that count is the difference between a decision and
 * an accident, so it gets its own paragraph rather than a clause tacked onto
 * the end of the first one.
 */
function DeleteWarning({
  lineCount,
  invoiceCount,
}: {
  lineCount: number | null;
  invoiceCount: number | null;
}) {
  return (
    <>
      <p className="m-0">
        {lineCount === null
          ? 'Any line items on this quote are removed with it.'
          : lineCount === 0
            ? 'This quote has no line items on it.'
            : `This also removes the ${lineCount} ${
                lineCount === 1 ? 'line item' : 'line items'
              } on it.`}
      </p>
      {invoiceCount !== null && invoiceCount > 0 && (
        <p className="m-0 mt-2 font-medium text-danger">
          {invoiceCount === 1
            ? '1 invoice raised from this quote will keep its figures but lose the link back to it.'
            : `${invoiceCount} invoices raised from this quote will keep their figures but lose their link back to it.`}
        </p>
      )}
      {/* The delete never reaches Xero (backend `deleteQuote`): the Xero quote and its link survive. */}
      <p className="m-0 mt-2">
        If this quote is in Xero, it stays there. Only the SEED EMERGE record is deleted, and that
        cannot be undone.
      </p>
    </>
  );
}

function QuotesEmptyState({
  filtered,
  expiryFiltered,
  projectFiltered,
  columnsHidAll,
  onClearColumns,
  onClear,
  hasError,
  action,
}: {
  filtered: boolean;
  expiryFiltered: boolean;
  /** A project id went out on the list request — see the note below. */
  projectFiltered: boolean;
  /** Rows CAME BACK and a page-scoped column funnel hid all of them. */
  columnsHidAll: boolean;
  onClearColumns: () => void;
  onClear: () => void;
  hasError: boolean;
  action: ReactNode;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<QuoteIcon />}
        title="Nothing to show"
        description="The quote list couldn’t be loaded — see the message above."
      />
    );
  }

  /*
   * A picklist id is never validated against the data: an unknown or retired id
   * returns an empty page rather than an error, so "no matches" is not evidence
   * that the filter itself is wrong. When an expiry preset is on, though, there
   * is a specific reason the page may be emptier than expected, and it is worth
   * saying instead of the generic line.
   */
  /*
   * The project filter comes first because it is the one someone ARRIVES on —
   * the link off `/projects/:id` — rather than one they set here. An empty
   * result is the ordinary answer for it today: a quote is put on a project only
   * when someone raises or edits it onto one. That has to read as a fact about
   * the project rather than as a filter that failed.
   */
  /*
   * FIRST, because it is the only branch where the SERVER found matches. The
   * request returned rows and a column funnel hid every one of them, so every
   * sentence below — and the button below them — would be about the wrong
   * filter. A reader told to "clear filters" here would clear the URL, watch
   * nothing change, and reasonably conclude the page is broken.
   */
  if (columnsHidAll) {
    return (
      <EmptyState
        icon={<QuoteIcon />}
        title="Every loaded quote is hidden"
        description="A column filter is hiding all the quotes on this page."
        action={
          <Button variant="secondary" size="sm" onClick={onClearColumns}>
            Clear column filters
          </Button>
        }
      />
    );
  }

  const filteredDescription = projectFiltered
    ? 'No quote names this project yet.'
    : expiryFiltered
      ? 'Quotes with no expiry date match neither preset.'
      : 'Search matches a quote’s subject and number, not the company.';

  return (
    <EmptyState
      icon={<QuoteIcon />}
      title={filtered ? 'No quotes match those filters' : 'No quotes yet'}
      description={filtered ? filteredDescription : undefined}
      action={
        filtered ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        ) : (
          action
        )
      }
    />
  );
}
